import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { BACKEND_URL } from "../../config/backend";
import * as THREE from "three";
import {
  deleteDoc,
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  arrayUnion, // ✅ ADD THIS
} from "firebase/firestore";
import PricingPage from "./PricingPage/PricingPage";
import FeedbackPage from "./FeedbackPage/FeedbackPage";
import SettingsPage from "./SettingsPage/SettingsPage";
import LeftToolbar from "./LeftToolbar/LeftToolbar";
import LeftSidebar from "./LeftSidebar/LeftSidebar";
import TopToolbar from "./TopToolbar/TopToolbar";
import BottomToolbar from "./BottomToolbar/BottomToolbar";
import ModelTabs from "./ModelTabs/ModelTabs";
import ModelsSidebar from "./ModelsSidebar/ModelsSidebar";

import View from "./View/View";
import RightSidebar from "./RightSidebar/RightSidebar";
import styles from "./CAD.module.css";
import { useAuth } from "../../components/Auth/AuthProvider";
import { db } from "../../firebase/config";
import { doc, getDoc, updateDoc, addDoc } from "firebase/firestore";
import { debounce } from "lodash";
import ExpandableProjectSidebar, {
  CreditsModal,
} from "../../components/ProjectDashboard/ProjectLeftSidebar";
interface TreeItem {
  id: string;
  name: string;
  displayName?: string;
  type: "create" | "modify" | "delete";
  children?: TreeItem[];
  parentId?: string;
}

interface ChatMessage {
  id: number;
  type: "user" | "assistant";
  content: string;
  originalContent?: string;
  responseType?: "create" | "modify" | "delete" | "error";
  featureName?: string;
  backendMessageId?: number;
  thinkingDuration?: string; // ADD THIS LINE
}

interface CommandHistoryEntry {
  userInput: string;
  originalUserInput: string;
  selectionContext: any[];
  messageId: number;
  timestamp: number;
  browserSelectionData?: {
    edges?: Array<{
      index: number;
      coordinates: number[];
      length: number;
      object: string;
    }>;
    faces?: Array<{
      index: number;
      center: number[];
      normal: number[];
      area: number;
      object: string;
    }>;
  };
  // ADD THIS LINE:
  fullSelectionContext?: any;
}

interface TimelineSnapshot {
  featureTree: TreeItem[];
  chatMessages: ChatMessage[];
  bodyCounter: number;
  tipFeature: string | null;
  commandHistory: CommandHistoryEntry[];
}

interface OperationVersion {
  messageId: number;
  timelineId: string;
  snapshot: TimelineSnapshot;
}

interface VersionedOperation {
  baseMessageId: number;
  versions: OperationVersion[];
  currentVersion: number;
  versionPath?: { [versionIndex: number]: number }; // ADD THIS LINE
}

interface TimelineVersionControl {
  timelines: { [timelineId: string]: TimelineSnapshot };
  currentTimelineId: string;
  versionedOperations: { [messageId: number]: VersionedOperation };
  timelineCounter: number;
  isInVersionMode: boolean;
}

interface OperationVersionInfo {
  current: number;
  total: number;
  canGoBack: boolean;
  canGoForward: boolean;
}

interface CADProps {
  projectId: string | null;
  onBackToProjects: (targetView?: "models" | "new-model") => void;
  onOpenProject: (projectId: string) => void;
  viewMode?: "owner" | "viewer";
  isSharedView?: boolean;
  initialProjectName?: string;
  onNavigateToSettings?: () => void;
  onProjectCreated?: (projectId: string) => void;
  onProjectCleared?: () => void; // ← ADD THIS NEW LINE
  onSharedModelLoaded?: () => void; // ← ADD THIS LINE RIGHT HERE
  onToolbarExpandChange?: (expanded: boolean) => void; // ← ADD THIS LINE
}

interface RightSidebarRefMethods {
  updateChatMessages: (
    currentDisplayName: string,
    newDisplayName: string,
  ) => void;
  clearChat: () => void;
  setChatMessages: (messages: ChatMessage[]) => void;
  getChatMessages?: () => ChatMessage[];
  sendInitialPrompt: (prompt: string) => void;
  focusInput: () => void;
}

// Helper function to generate project names

// === TimelineVersionSystem (full, updated) ===
class TimelineVersionSystem {
  protected state: TimelineVersionControl;
  protected applySnapshotCallback?: (
    snapshot: TimelineSnapshot,
  ) => Promise<void>;

  constructor(
    applySnapshotCallback?: (snapshot: TimelineSnapshot) => Promise<void>,
  ) {
    this.applySnapshotCallback = applySnapshotCallback;

    this.state = {
      timelines: {
        main: this.createEmptySnapshot(),
      },
      currentTimelineId: "main",
      versionedOperations: {},
      timelineCounter: 1,
      isInVersionMode: false,
    };
  }

  private createEmptySnapshot(): TimelineSnapshot {
    return {
      featureTree: [],
      chatMessages: [],
      bodyCounter: 0,
      tipFeature: null,
      commandHistory: [],
    };
  }

  saveOperation(messageId: number, snapshot: TimelineSnapshot): void {
    this.state.timelines[this.state.currentTimelineId] = JSON.parse(
      JSON.stringify(snapshot),
    );
    // console.log(
    //   `Saved operation ${messageId} to timeline ${this.state.currentTimelineId}`
    // );
  }

  restoreToOperation(messageId: number): boolean {
    const sourceTimeline = this.findTimelineContainingMessage(messageId);
    if (!sourceTimeline) {
      // console.error(`Message ${messageId} not found in any timeline`);
      return false;
    }

    const snapshot = this.getSnapshotAtMessage(sourceTimeline, messageId);
    if (!snapshot) {
      // console.error(`Could not get snapshot for message ${messageId}`);
      return false;
    }

    if (!this.state.versionedOperations[messageId]) {
      this.state.versionedOperations[messageId] = {
        baseMessageId: messageId,
        versions: [
          {
            messageId: messageId,
            timelineId: sourceTimeline,
            snapshot: this.state.timelines[sourceTimeline],
          },
        ],
        currentVersion: 0,
        versionPath: {}, // NEW: initialize empty version path
      };
    }

    const newTimelineId = `timeline_${this.state.timelineCounter++}`;
    this.state.timelines[newTimelineId] = JSON.parse(JSON.stringify(snapshot));

    const versionedOp = this.state.versionedOperations[messageId];
    versionedOp.versions.push({
      messageId: messageId,
      timelineId: newTimelineId,
      snapshot: JSON.parse(JSON.stringify(snapshot)),
    });
    versionedOp.currentVersion = versionedOp.versions.length - 1;

    this.state.currentTimelineId = newTimelineId;
    this.state.isInVersionMode = true;

    // console.log(
    //   `Created version ${versionedOp.currentVersion + 1}/${
    //     versionedOp.versions.length
    //   } for operation ${messageId}`
    // );
    return true;
  }

  navigateOperationVersion(
    messageId: number,
    direction: "back" | "forward",
  ): boolean {
    const versionedOp = this.state.versionedOperations[messageId];
    if (!versionedOp) {
      // console.error(`No versions found for operation ${messageId}`);
      return false;
    }

    let newVersion = versionedOp.currentVersion;
    if (direction === "back") {
      newVersion = Math.max(0, versionedOp.currentVersion - 1);
    } else {
      newVersion = Math.min(
        versionedOp.versions.length - 1,
        versionedOp.currentVersion + 1,
      );
    }

    if (newVersion === versionedOp.currentVersion) {
      // console.log(
      //   `Already at ${direction === "back" ? "first" : "last"} version`
      // );
      return false;
    }

    const targetVersion = versionedOp.versions[newVersion];
    this.state.currentTimelineId = targetVersion.timelineId;
    versionedOp.currentVersion = newVersion;

    // NEW: Record which sub-version we're navigating to
    const currentTimelineId = this.state.currentTimelineId;

    // Find if there are any child versioned operations on this timeline
    for (const [childMessageId, childOp] of Object.entries(
      this.state.versionedOperations,
    )) {
      if (childMessageId === messageId.toString()) continue; // Skip self

      // Check if any of this child's versions belong to our current timeline
      const childVersionOnThisTimeline = childOp.versions.findIndex(
        (v) => v.timelineId === currentTimelineId,
      );

      if (childVersionOnThisTimeline !== -1) {
        // Store which sub-version of this child we're on for this parent version
        if (!versionedOp.versionPath) {
          versionedOp.versionPath = {};
        }
        versionedOp.versionPath[newVersion] = childVersionOnThisTimeline;
      }
    }

    // console.log(
    //   `Navigated to version ${newVersion + 1}/${
    //     versionedOp.versions.length
    //   } for operation ${messageId}`
    // );
    return true;
  }

  getOperationVersionInfo(messageId: number): OperationVersionInfo | null {
    const versionedOp = this.state.versionedOperations[messageId];
    if (!versionedOp) return null;

    return {
      current: versionedOp.currentVersion + 1,
      total: versionedOp.versions.length,
      canGoBack: versionedOp.currentVersion > 0,
      canGoForward:
        versionedOp.currentVersion < versionedOp.versions.length - 1,
    };
  }

  getCurrentSnapshot(): TimelineSnapshot {
    return this.state.timelines[this.state.currentTimelineId];
  }

  getCurrentTimelineId(): string {
    return this.state.currentTimelineId;
  }

  isVersionMode(): boolean {
    return this.state.isInVersionMode;
  }

  getVersionedOperations(): { [messageId: number]: VersionedOperation } {
    return this.state.versionedOperations;
  }

  reset(): void {
    this.state = {
      timelines: {
        main: this.createEmptySnapshot(),
      },
      currentTimelineId: "main",
      versionedOperations: {},
      timelineCounter: 1,
      isInVersionMode: false,
    };
  }

  restoreVersionedOperation(messageId: number, versionedOpData: any): void {
    this.state.versionedOperations[messageId] = {
      baseMessageId: versionedOpData.baseMessageId,
      versions: versionedOpData.versions,
      currentVersion: versionedOpData.currentVersion,
    };

    // console.log(
    //   `Restored versioned operation ${messageId} with ${versionedOpData.versions.length} versions`
    // );
  }

  private findTimelineContainingMessage(messageId: number): string | null {
    for (const [timelineId, snapshot] of Object.entries(this.state.timelines)) {
      if (snapshot.chatMessages.some((msg) => msg.id === messageId)) {
        return timelineId;
      }
    }
    return null;
  }

  private getSnapshotAtMessage(
    timelineId: string,
    messageId: number,
  ): TimelineSnapshot | null {
    const timeline = this.state.timelines[timelineId];
    if (!timeline) return null;

    const messageIndex = timeline.chatMessages.findIndex(
      (msg) => msg.id === messageId,
    );
    if (messageIndex === -1) return null;

    const messagesUpToPoint = timeline.chatMessages.slice(0, messageIndex + 1);
    const commandsUpToPoint = timeline.commandHistory.filter((cmd) =>
      messagesUpToPoint.some((msg) => msg.id === cmd.messageId),
    );

    return {
      ...timeline,
      chatMessages: messagesUpToPoint,
      commandHistory: commandsUpToPoint,
    };
  }

  printState(): void {
    // console.log("Timeline Version Control State:");
    // console.log(`Current timeline: ${this.state.currentTimelineId}`);
    // console.log(`Version mode: ${this.state.isInVersionMode}`);
    // console.log(`Timelines: ${Object.keys(this.state.timelines).join(", ")}`);
    // console.log(
    //   `Versioned operations: ${Object.keys(this.state.versionedOperations).join(
    //     ", "
    //   )}`
    // );

    for (const [messageId, versionedOp] of Object.entries(
      this.state.versionedOperations,
    )) {
      // console.log(
      //   `  Operation ${messageId}: ${versionedOp.currentVersion + 1}/${
      //     versionedOp.versions.length
      //   } versions`
      // );
    }
  }

  syncPointersToCurrentTimeline(): void {
    // 🔧 CRITICAL: Don't sync if we just loaded from a save file
    if ((this as any).__pointersRestoredFromSave) {
      // console.log(
      //   "⏸️ Skipping pointer sync - pointers were restored from save"
      // );
      (this as any).__pointersRestoredFromSave = false; // Clear flag
      return;
    }

    const active = this.state.currentTimelineId;
    let changed = false;

    for (const vo of Object.values(this.state.versionedOperations)) {
      const idx = vo.versions.findIndex((v) => v.timelineId === active);
      if (idx !== -1 && vo.currentVersion !== idx) {
        vo.currentVersion = idx;
        changed = true;
      }
    }

    if (changed) {
      // console.log(
      //   `Synchronized version pointers to active timeline "${active}".`
      // );
    }
  }
}

class CADTimelineVersionControl extends TimelineVersionSystem {
  constructor(
    applySnapshotCallback: (snapshot: TimelineSnapshot) => Promise<void>,
  ) {
    super(applySnapshotCallback);
  }

  getCurrentTimelineId(): string {
    return (this as any).state.currentTimelineId;
  }

  async restoreToOperationAndApply(messageId: number): Promise<boolean> {
    const success = this.restoreToOperation(messageId);
    if (!success) return false;

    const snapshot = this.getCurrentSnapshot();
    await this.applySnapshotCallback?.(snapshot);
    return true;
  }

  async navigateOperationVersionAndApply(
    messageId: number,
    direction: "back" | "forward",
  ): Promise<boolean> {
    const moved = this.navigateOperationVersion(messageId, direction);
    if (!moved) return false;

    const snapshot = this.getCurrentSnapshot();
    await this.applySnapshotCallback?.(snapshot);
    return true;
  }

  createSnapshotFromCurrentState(
    featureTree: TreeItem[],
    chatMessages: ChatMessage[],
    bodyCounter: number,
    tipFeature: string | null,
    commandHistory: CommandHistoryEntry[],
  ): TimelineSnapshot {
    return {
      featureTree: JSON.parse(JSON.stringify(featureTree)),
      chatMessages: JSON.parse(JSON.stringify(chatMessages)),
      bodyCounter,
      tipFeature,
      commandHistory: [...commandHistory],
    };
  }
}

const CAD: React.FC<CADProps> = ({
  projectId,
  onBackToProjects,
  onOpenProject,
  viewMode = "owner",
  isSharedView = false,
  initialProjectName,
  onNavigateToSettings,
  onProjectCreated,
  onProjectCleared, // ← ADD THIS NEW LINE
  onSharedModelLoaded, // ← ADD THIS LINE RIGHT HERE
  onToolbarExpandChange, // ← ADD THIS LINE
}) => {
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(true);
  const [userManuallyClosedToolbar, setUserManuallyClosedToolbar] =
    useState(false); // ADD THIS LINE
  const [projectToHunt, setProjectToHunt] = useState<string | null>(null);
  const [isWaitingForFirstPrompt, setIsWaitingForFirstPrompt] = useState(false);
  const [isCreatingNewModel, setIsCreatingNewModel] = useState(false);
  const [userHasTouchedSidebars, setUserHasTouchedSidebars] = useState(false);
  const [hasAutoSwitchedToProject, setHasAutoSwitchedToProject] =
    useState(false);
  const localHiddenExampleIds = useRef<Set<string>>(new Set());
  const [optimisticProject, setOptimisticProject] = useState<{
    id: string;
    name: string;
    updatedAt?: Date; // <--- ADD THIS LINE
  } | null>(null);
  const [showPricingPage, setShowPricingPage] = useState(false);
  const [showFeedbackPage, setShowFeedbackPage] = useState(false);
  const [showSettingsPage, setShowSettingsPage] = useState(false);
  const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
  const [projectsRefreshTrigger, setProjectsRefreshTrigger] = useState(0);
  const [sidebarProjects, setSidebarProjects] = useState<
    Array<{
      id: string;
      name: string;
      createdAt: Date;
      updatedAt: Date;
      userId: string;
      thumbnail?: string;
      description?: string;
      isPublic?: boolean;
      shareToken?: string;
      copiedFromExample?: string; // ← ADD THIS LINE
    }>
  >([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const versionControl = useMemo(() => {
    return new CADTimelineVersionControl(async (snapshot: TimelineSnapshot) => {
      await applySnapshotRef.current?.(snapshot);
    });
  }, []);
  // NEW: Track if we have a project yet
  // NEW: Track if we have a project yet
  const [hasProject, setHasProject] = useState(!!projectId);
  const [activeModelId, setActiveModelId] = useState<string | null>(projectId);

  // Sync activeModelId only when projectId actually changes value (not on every render)
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      if (projectId) {
        setHasProject(true);
        setActiveModelId(projectId);
      }
    }
  }, [projectId]);
  // CRITICAL: Detect if we're in a shared view from URL or sessionStorage
  const [isActuallySharedView, setIsActuallySharedView] = useState(() => {
    const path = window.location.pathname;
    const isSharePath = path.startsWith("/share/");
    const sessionFlag = sessionStorage.getItem("isSharedView") === "true";
    return isSharedView || isSharePath || sessionFlag;
  });

  // Detect current theme
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">(() => {
    const htmlElement = document.querySelector("html");
    const bodyElement = document.body;
    const isDarkMode =
      htmlElement?.classList.contains("dark-theme") ||
      bodyElement?.classList.contains("dark-theme") ||
      document.documentElement.classList.contains("dark-theme");

    return isDarkMode ? "dark" : "light";
  });

  // Track if we're using a default color (not user-customized)
  const isDefaultColorRef = useRef(false);

  // Define default colors for each theme
  const DEFAULT_COLORS = {
    dark: "6f6f6f",
    light: "8d8d8d",
  };

  // Initialize color state with theme-aware default
  // Initialize color state with theme-aware default
  // Initialize color state with theme-aware default
  const [colorValue, setColorValue] = useState(() => {
    const htmlElement = document.querySelector("html");
    const bodyElement = document.body;
    const isDarkMode =
      htmlElement?.classList.contains("dark-theme") ||
      bodyElement?.classList.contains("dark-theme") ||
      document.documentElement.classList.contains("dark-theme");

    const actualTheme = isDarkMode ? "dark" : "light";
    const stored = sessionStorage.getItem(`project_${projectId}_color`);

    if (stored) {
      const isDefault =
        stored === DEFAULT_COLORS.dark || stored === DEFAULT_COLORS.light;
      isDefaultColorRef.current = isDefault;

      if (isDefault) {
        return DEFAULT_COLORS[actualTheme];
      }
      return stored;
    }

    isDefaultColorRef.current = true;
    return DEFAULT_COLORS[actualTheme];
  });

  // Then use isActuallySharedView instead of isSharedView throughout the component
  const { user, userProfile } = useAuth();

  const operationTypeMapping: { [key: string]: string } = {
    Pad: "Extrude",
    Pocket: "Cut",
    Thickness: "Shell",
  };

  // Helper function to convert technical names to display names
  const getDisplayNameForFeature = useCallback(
    (technicalName: string): string => {
      // Check if this matches an operation type with number (e.g., "Pad001")
      for (const [techTerm, displayTerm] of Object.entries(
        operationTypeMapping,
      )) {
        const regex = new RegExp(`^${techTerm}(\\d{3})$`);
        const match = technicalName.match(regex);
        if (match) {
          return `${displayTerm}${match[1]}`; // e.g., "Extrude001"
        }
      }
      return technicalName; // Return unchanged if no mapping found
    },
    [operationTypeMapping],
  );

  const [bodyCounter, setBodyCounter] = useState(0);
  // Around line 200, replace the primitiveCountersRef definition:
  const primitiveCountersRef = useRef<{
    Box: number;
    Cylinder: number;
    Sphere: number;
    Cone: number;
    Torus: number;
    Ellipsoid: number;
    Prism: number;
    Wedge: number;
  }>({
    Box: 0,
    Cylinder: 0,
    Sphere: 0,
    Cone: 0,
    Torus: 0,
    Ellipsoid: 0,
    Prism: 0,
    Wedge: 0,
  });
  const commandHistoryRef = useRef<{ [tabId: string]: CommandHistoryEntry[] }>(
    {},
  );
  const featureTreeRef = useRef<TreeItem[]>([]);
  const [pendingFeatureName, setPendingFeatureName] = useState<string | null>(
    null,
  );
  const [projectIsPublic, setProjectIsPublic] = useState(false);
  const [projectShareToken, setProjectShareToken] = useState<
    string | undefined
  >(undefined);
  const [currentMeasurement, setCurrentMeasurement] = useState<number>(0);
  const [isRightSidebarLoading, setIsRightSidebarLoading] = useState(false);

  const [isSavingAfterCommand, setIsSavingAfterCommand] = useState(false); // ADD THIS LINE
  const isOperationInProgress = useRef(false);
  const [activeBranchId, setActiveBranchId] = useState<string>("");
  const [pendingCommandCount, setPendingCommandCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  const [noahGeometry, setNoahGeometry] = useState<any[]>([]);
  const [isAnyButtonLoading, setIsAnyButtonLoading] = useState(false);

  const [currentSelection, setCurrentSelection] = useState<any[]>([]);
  const [featureTree, setFeatureTree] = useState<TreeItem[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [fileName, setFileName] = useState(initialProjectName || "");

  const handleNewModelButtonClick = useCallback(() => {
    setShowPricingPage(false); // ADD THIS LINE
    setShowFeedbackPage(false);
    setShowSettingsPage(false); // next to setShowPricingPage(false) and setShowFeedbackPage(false)

    // ✅ CRITICAL: Close ALL tabs (this hides View.tsx)
    setOpenModels([]);
    setActiveModelId(null);

    // ✅ Clear all UI state
    setNoahGeometry([]);
    setFeatureTree([]);
    setExpandedItems([]);
    setFileName("");
    setCurrentSelection([]);

    // ✅ Close left sidebar
    setIsLeftSidebarOpen(false);
    setIsModelsSidebarOpen(false);

    // ✅ Open right sidebar
    setIsRightSidebarOpen(true);

    // ✅ Clear chat to empty state
    if (rightSidebarRef.current) {
      rightSidebarRef.current.setChatMessages([]);
    }

    // ✅ Reset timeline
    (versionControl as any).state.timelines = {
      main: {
        featureTree: [],
        chatMessages: [],
        bodyCounter: 0,
        tipFeature: null,
        commandHistory: [],
      },
    };
    (versionControl as any).state.currentTimelineId = "main";

    // ✅ Reset camera to defaults
    if ((window as any).__restoreCameraState) {
      (window as any).__restoreCameraState({
        rotation: { x: -30, y: -45 },
        distance: 50,
        isOrthographic: false,
      });
    }

    // ✅ Reset to default color
    const isDarkMode = document.documentElement.classList.contains("dark");
    const defaultColor = isDarkMode
      ? DEFAULT_COLORS.dark
      : DEFAULT_COLORS.light;
    setColorValue(defaultColor);
    isDefaultColorRef.current = true;

    // ✅ Reset transparency
    setTransparencyValue(100);

    // ✅ Mark that user touched sidebars
    setUserHasTouchedSidebars(true);

    // ✅ Clear has loaded flag
    hasLoadedRef.current = null;

    // ✅ Trigger project cleared callback
    if (onProjectCleared) {
      onProjectCleared();
    }
  }, [versionControl, onProjectCleared]);

  const handleFileNameChange = useCallback(
    async (newName: string) => {
      if (!newName || newName.trim() === "") return;

      setFileName(newName);

      // Update the active tab name
      setOpenModels((prev) =>
        prev.map((model) =>
          model.id === activeModelId ? { ...model, name: newName } : model,
        ),
      );

      // Save to Firebase
      if (projectId && user) {
        try {
          await updateDoc(doc(db, "projects", projectId as string), {
            name: newName,
            updatedAt: new Date().toISOString(), // ← CHANGED: Use ISO String
          });
          setProjectsRefreshTrigger((prev) => prev + 1);
          // console.log(`✅ Renamed project to: ${newName}`);
        } catch (error) {
          // console.error("Failed to save rename:", error);
        }
      }
    },
    [projectId, user, activeModelId],
  );

  // ========== INJECT NEW STATE HERE ==========
  // NEW: Multi-model tab management
  // Around line 140, update the openModels state type:
  const [openModels, setOpenModels] = useState<
    Array<{
      id: string;
      name: string;
      geometry: any[];
      featureTree: TreeItem[];
      chatMessages: ChatMessage[];
      commandHistory: CommandHistoryEntry[];
      versionControl: any;
      branchId: string;
      isSharedModel?: boolean;
      cameraState?: {
        rotation: { x: number; y: number };
        distance: number;
        isOrthographic: boolean;
      };
      primitiveCounters?: {
        // ← ADD THIS
        Box: number;
        Cylinder: number;
        Sphere: number;
        Cone: number;
        Torus: number;
        Ellipsoid: number;
        Prism: number;
        Wedge: number;
      };
    }>
  >([]);

  // ========== END INJECTION ==========

  const [currentBranch, setCurrentBranch] = useState<string>("main");

  // Save current model state before switching tabs
  // Around line 360, replace the saveCurrentModelState function:
  // In saveCurrentModelState function (around line 360):
  const saveCurrentModelState = useCallback(() => {
    if (!activeModelId) return;

    const currentChatMessages =
      rightSidebarRef.current?.getChatMessages?.() || [];

    const currentCameraState = (window as any).__getCurrentCameraState?.() || {
      rotation: { x: -30, y: -45 },
      distance: 50,
      isOrthographic: false,
    };

    // 🔧 FIX: Get command history from current snapshot
    const currentSnapshot = versionControl.getCurrentSnapshot();

    setOpenModels((prev) =>
      prev.map((model) =>
        model.id === activeModelId
          ? {
              ...model,
              geometry: [...noahGeometry],
              featureTree: [...featureTree],
              chatMessages: [...currentChatMessages],
              commandHistory: currentSnapshot.commandHistory || [], // ← ADD THIS LINE
              branchId: currentBranch,
              cameraState: currentCameraState,
              primitiveCounters: { ...primitiveCountersRef.current },
            }
          : model,
      ),
    );
  }, [activeModelId, noahGeometry, featureTree, versionControl, currentBranch]);

  const handleNewModel = useCallback(async () => {
    if (!user) return;

    try {
      if (sessionStorage.getItem("isSharedView") === "true") {
        sessionStorage.removeItem("isSharedView");
        sessionStorage.removeItem("shareToken");
      }

      setIsCreatingNewModel(true);
      saveCurrentModelState();

      const q = query(
        collection(db, "projects"),
        where("userId", "==", user.uid),
      );
      const allProjectsSnapshot = await getDocs(q);
      const nonBranchedCount = allProjectsSnapshot.docs.filter((doc) => {
        const data = doc.data();
        return !data.branchedFrom;
      }).length;
      const projectNumber = nonBranchedCount + 1;
      const paddedNumber = projectNumber.toString().padStart(3, "0");
      const newModelName = `Untitled${paddedNumber}`;

      const newProjectRef = await addDoc(collection(db, "projects"), {
        name: newModelName,
        createdAt: new Date(),
        updatedAt: new Date().toISOString(), // ← CHANGED: Use ISO String for consistency
        userId: user.uid,
        description: "",
      });

      setProjectsRefreshTrigger((prev) => prev + 1);

      // ✅ CRITICAL FIX: Refresh TopToolbar immediately after creating project in Firebase
      console.log(
        "🔄 Refreshing TopToolbar after creating project in Firebase",
      );
      if ((window as any).__refreshTopToolbarProjects) {
        await (window as any).__refreshTopToolbarProjects();
      }

      const newBranchId = `user_${user.uid}_${newProjectRef.id}_${Date.now()}`;

      const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "CLEAR_DOCUMENT",
          context: {},
          branch_id: newBranchId,
        }),
        signal: AbortSignal.timeout(60000),
      });

      if (!clearResponse.ok) {
        throw new Error("Failed to initialize backend branch");
      }

      (window as any).__forceFirstGeometryLoad = true;
      (window as any).__resetCameraForNewProject = true;

      const newModel = {
        id: newProjectRef.id,
        name: newModelName,
        geometry: [],
        featureTree: [],
        chatMessages: [],
        commandHistory: [],
        versionControl: null,
        branchId: newBranchId,
        isSharedModel: false,
        cameraState: {
          rotation: { x: -30, y: -45 },
          distance: 50,
          isOrthographic: false,
        },
        primitiveCounters: {
          Box: 0,
          Cylinder: 0,
          Sphere: 0,
          Cone: 0,
          Torus: 0,
          Ellipsoid: 0,
          Prism: 0,
          Wedge: 0,
        },
      };

      setOpenModels((prev) => [...prev, newModel]);
      setActiveModelId(newProjectRef.id);
      setIsCreatingNewModel(false);
      setCurrentBranch(newBranchId);
      setFileName(newModelName);
      setNoahGeometry([]);
      setFeatureTree([]);

      (versionControl as any).state.timelines = {
        main: {
          featureTree: [],
          chatMessages: [],
          bodyCounter: 0,
          tipFeature: null,
          commandHistory: [],
        },
      };
      (versionControl as any).state.currentTimelineId = "main";

      const htmlElement = document.querySelector("html");
      const bodyElement = document.body;
      const isDarkMode =
        htmlElement?.classList.contains("dark-theme") ||
        bodyElement?.classList.contains("dark-theme") ||
        document.documentElement.classList.contains("dark-theme");

      const defaultColor = isDarkMode
        ? DEFAULT_COLORS.dark
        : DEFAULT_COLORS.light;
      setColorValue(defaultColor);
      isDefaultColorRef.current = true;
      sessionStorage.setItem(`project_${newProjectRef.id}_color`, defaultColor);

      setTransparencyValue(100);
      sessionStorage.setItem(`project_${newProjectRef.id}_transparency`, "100");

      setProjectIsPublic(false);
      setProjectShareToken(undefined);

      setIsWaitingForFirstPrompt(true);
      setHasProject(true);

      setTimeout(() => {
        if (rightSidebarRef.current) {
          rightSidebarRef.current.setChatMessages([]);
        }
      }, 0);
    } catch (error) {
      console.error("Failed to create new model:", error);
    }
  }, [
    user,
    saveCurrentModelState,
    userHasTouchedSidebars,
    hasAutoSwitchedToProject,
    openModels,
    onToolbarExpandChange,
  ]);

  // Add this helper inside the CAD component
  const recalculatePrimitiveCounters = useCallback((tree: TreeItem[]) => {
    const newCounters = {
      Box: 0,
      Cylinder: 0,
      Sphere: 0,
      Cone: 0,
      Torus: 0,
      Ellipsoid: 0,
      Prism: 0,
      Wedge: 0,
    };

    const countRecursively = (items: TreeItem[]) => {
      items.forEach((item) => {
        // Check both technical name and display name to find the max number
        const namesToCheck = [item.name, item.displayName].filter(
          Boolean,
        ) as string[];

        namesToCheck.forEach((name) => {
          // Robust regex: Optional Sub prefix, Base Name, then Digits
          const match = name.match(
            /^(?:Sub|sub)?(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)(\d+)$/i,
          );

          if (match) {
            const rawType = match[1];
            // Normalize type to Key (e.g. "box" -> "Box")
            const typeKey = (rawType.charAt(0).toUpperCase() +
              rawType.slice(1).toLowerCase()) as keyof typeof newCounters;

            const num = parseInt(match[2], 10);

            if (newCounters[typeKey] !== undefined) {
              newCounters[typeKey] = Math.max(newCounters[typeKey], num);
            }
          }
        });

        if (item.children) {
          countRecursively(item.children);
        }
      });
    };

    countRecursively(tree);
    return newCounters;
  }, []);

  const handleTabClick = useCallback(
    (tabId: string) => {
      // console.log("=== TAB CLICK START ===");
      // console.log("Switching FROM:", activeModelId, "TO:", tabId);

      if (tabId === activeModelId) return;

      const currentCameraState = (window as any).__getCurrentCameraState?.();

      if (activeModelId) {
        const currentChatMessages =
          rightSidebarRef.current?.getChatMessages?.() || [];

        setOpenModels((prev) =>
          prev.map((model) =>
            model.id === activeModelId
              ? {
                  ...model,
                  geometry: [...noahGeometry],
                  featureTree: [...featureTree],
                  chatMessages: [...currentChatMessages],
                  commandHistory:
                    versionControl.getCurrentSnapshot().commandHistory || [],
                  branchId: currentBranch,
                  cameraState: currentCameraState || model.cameraState,
                  primitiveCounters: { ...primitiveCountersRef.current }, // Save current counters
                }
              : model,
          ),
        );
      }

      if (sessionStorage.getItem("isSharedView") === "true") {
        sessionStorage.removeItem("isSharedView");
        sessionStorage.removeItem("shareToken");
      }

      (window as any).__isTabSwitch = true;

      const targetModel = openModels.find((m) => m.id === tabId);
      if (!targetModel) return;

      setActiveModelId(tabId);
      setCurrentBranch(targetModel.branchId);

      // Restore primitive counters for target tab
      if (targetModel.primitiveCounters) {
        primitiveCountersRef.current = { ...targetModel.primitiveCounters };
      } else {
        // Reset to zero if no counters saved
        primitiveCountersRef.current = {
          Box: 0,
          Cylinder: 0,
          Sphere: 0,
          Cone: 0,
          Torus: 0,
          Ellipsoid: 0,
          Prism: 0,
          Wedge: 0,
        };
      }

      // Restore camera AND sync NaviCube
      if (targetModel.cameraState && (window as any).__restoreCameraState) {
        (window as any).__restoreCameraState(targetModel.cameraState);
      }

      const cleanGeometry = targetModel.geometry.map((g: any) => {
        const {
          _autoFit,
          _errorRecovery,
          _skipCameraReset,
          _forceUpdate,
          ...rest
        } = g;
        return rest;
      });

      setNoahGeometry(cleanGeometry);
      setFeatureTree([...targetModel.featureTree]);

      // 🔧 FIX: Restore this tab's command history to the timeline
      const currentTimelineId = versionControl.getCurrentTimelineId();
      const timeline = (versionControl as any).state.timelines[
        currentTimelineId
      ];
      if (timeline) {
        timeline.commandHistory = targetModel.commandHistory
          ? [...targetModel.commandHistory]
          : [];
        // console.log(
        //   `✅ TAB SWITCH: Restored ${timeline.commandHistory.length} commands for tab ${tabId}`
        // );
      }

      const bodyIds = targetModel.featureTree.map((body) => body.id);
      setExpandedItems(bodyIds);

      if (rightSidebarRef.current) {
        rightSidebarRef.current.setChatMessages([...targetModel.chatMessages]);
      }

      setFileName(targetModel.name);

      const cachedColor = sessionStorage.getItem(`project_${tabId}_color`);
      const cachedTransparency = sessionStorage.getItem(
        `project_${tabId}_transparency`,
      );

      if (cachedColor) {
        setColorValue(cachedColor);
      } else {
        const isDarkMode = document.documentElement.classList.contains("dark");
        const defaultColor = isDarkMode
          ? DEFAULT_COLORS.dark
          : DEFAULT_COLORS.light;
        setColorValue(defaultColor);
      }

      if (cachedTransparency) {
        setTransparencyValue(parseFloat(cachedTransparency));
      } else {
        setTransparencyValue(100);
      }

      const loadShareStatus = async () => {
        try {
          const projectDoc = await getDoc(doc(db, "projects", tabId));
          if (projectDoc.exists()) {
            const data = projectDoc.data();

            if (data.modelColor && data.modelColor !== cachedColor) {
              setColorValue(data.modelColor);
              sessionStorage.setItem(`project_${tabId}_color`, data.modelColor);
            }

            if (
              data.transparencyValue !== undefined &&
              data.transparencyValue.toString() !== cachedTransparency
            ) {
              setTransparencyValue(data.transparencyValue);
              sessionStorage.setItem(
                `project_${tabId}_transparency`,
                data.transparencyValue.toString(),
              );
            }

            setProjectIsPublic(data.isPublic || false);
            setProjectShareToken(data.shareToken);
          }
        } catch (error) {
          // console.error("Error loading share status:", error);
        }
      };
      loadShareStatus();

      // ✅ ADD THIS: Collapse toolbar when switching tabs
      // if (onToolbarExpandChange) {
      //   onToolbarExpandChange(false);
      // }

      // console.log("=== TAB CLICK END ===\n");
    },
    [
      activeModelId,
      openModels,
      noahGeometry,
      featureTree,
      versionControl,
      currentBranch,
    ],
  );

  const handleTabClose = useCallback(
    (tabId: string) => {
      // ✅ FIX: Find which tab we're closing BEFORE any state changes
      const closingIndex = openModels.findIndex((m) => m.id === tabId);
      const isClosingActiveTab = tabId === activeModelId;

      // ✅ FIX: If closing active tab, determine next tab FIRST
      let nextTabToActivate: string | null = null;
      if (isClosingActiveTab && openModels.length > 1) {
        // Switch to tab on the left, or rightmost tab if closing leftmost
        const nextIndex = closingIndex > 0 ? closingIndex - 1 : 1;
        nextTabToActivate = openModels[nextIndex]?.id || null;
      }

      setOpenModels((prev) => {
        const filtered = prev.filter((model) => model.id !== tabId);

        // 🔧 FIX: Clean up timeline when closing tabs
        if (filtered.length === 0) {
          // Closing last tab - reset timeline completely
          (versionControl as any).state.timelines = {
            main: {
              featureTree: [],
              chatMessages: [],
              bodyCounter: 0,
              tipFeature: null,
              commandHistory: [],
            },
          };
          (versionControl as any).state.currentTimelineId = "main";
          // console.log(`✅ CLOSE: Reset timeline - no tabs remaining`);
        }

        // If closing the last tab, clear all UI state
        if (filtered.length === 0) {
          setNoahGeometry([]);
          setFeatureTree([]);
          setExpandedItems([]);
          setFileName("");
          setCurrentSelection([]);
          setProjectIsPublic(false);
          setProjectShareToken(undefined);

          if (rightSidebarRef.current) {
            rightSidebarRef.current.setChatMessages([]);
          }

          hasLoadedRef.current = null;

          // ✅ CRITICAL: Reset camera to defaults via View.tsx
          if ((window as any).__restoreCameraState) {
            (window as any).__restoreCameraState({
              rotation: { x: -30, y: -45 },
              distance: 50,
              isOrthographic: false,
            });
          }

          // ✅ Reset to default color
          const isDarkMode =
            document.documentElement.classList.contains("dark");
          const defaultColor = isDarkMode
            ? DEFAULT_COLORS.dark
            : DEFAULT_COLORS.light;
          setColorValue(defaultColor);
          isDefaultColorRef.current = true;

          // ✅ Reset transparency
          setTransparencyValue(100);

          // ✅ NEW: Auto-open Models sidebar and close Feature Tree when reaching empty state
          setIsLeftSidebarOpen(false);
          // setIsModelsSidebarOpen(true);

          // console.log(
          //   "✅ Closed last tab, cleared UI state, opened Models sidebar"
          // );
        }

        return filtered;
      });

      // If closing active tab, switch to the determined next tab
      if (isClosingActiveTab && nextTabToActivate) {
        const nextModel = openModels.find((m) => m.id === nextTabToActivate);
        if (!nextModel) return;

        setActiveModelId(nextTabToActivate);
        setCurrentBranch(nextModel.branchId);

        // ✅ CRITICAL: Don't save current state - just load the next tab's saved state
        // (This prevents the closing tab from overwriting the next tab)

        // Restore primitive counters for target tab
        if (nextModel.primitiveCounters) {
          primitiveCountersRef.current = { ...nextModel.primitiveCounters };
        } else {
          primitiveCountersRef.current = {
            Box: 0,
            Cylinder: 0,
            Sphere: 0,
            Cone: 0,
            Torus: 0,
            Ellipsoid: 0,
            Prism: 0,
            Wedge: 0,
          };
        }

        // ✅ Restore camera from SAVED state (not current camera)
        if (nextModel.cameraState && (window as any).__restoreCameraState) {
          (window as any).__restoreCameraState(nextModel.cameraState);
        }

        // 🔧 FIX: Restore next tab's command history to timeline
        const currentTimelineId = versionControl.getCurrentTimelineId();
        const timeline = (versionControl as any).state.timelines[
          currentTimelineId
        ];
        if (timeline) {
          timeline.commandHistory = nextModel.commandHistory
            ? [...nextModel.commandHistory]
            : [];
          // console.log(
          //   `✅ CLOSE: Restored ${timeline.commandHistory.length} commands for next tab ${nextTabToActivate}`
          // );
        }

        const cleanGeometry = nextModel.geometry.map((g: any) => {
          const {
            _autoFit,
            _errorRecovery,
            _skipCameraReset,
            _forceUpdate,
            ...rest
          } = g;
          return rest;
        });

        setNoahGeometry(cleanGeometry);
        setFeatureTree([...nextModel.featureTree]);

        const bodyIds = nextModel.featureTree.map((body) => body.id);
        setExpandedItems(bodyIds);

        if (rightSidebarRef.current) {
          rightSidebarRef.current.setChatMessages([...nextModel.chatMessages]);
        }

        setFileName(nextModel.name);

        // ✅ Restore color from session storage for this specific tab
        const cachedColor = sessionStorage.getItem(
          `project_${nextTabToActivate}_color`,
        );
        const cachedTransparency = sessionStorage.getItem(
          `project_${nextTabToActivate}_transparency`,
        );

        if (cachedColor) {
          setColorValue(cachedColor);
          isDefaultColorRef.current = false;
        } else {
          const isDarkMode =
            document.documentElement.classList.contains("dark");
          const defaultColor = isDarkMode
            ? DEFAULT_COLORS.dark
            : DEFAULT_COLORS.light;
          setColorValue(defaultColor);
          isDefaultColorRef.current = true;
        }

        if (cachedTransparency) {
          setTransparencyValue(parseFloat(cachedTransparency));
        } else {
          setTransparencyValue(100);
        }

        const loadShareStatus = async () => {
          try {
            const projectDoc = await getDoc(
              doc(db, "projects", nextTabToActivate!),
            );
            if (projectDoc.exists()) {
              const data = projectDoc.data();
              setProjectIsPublic(data.isPublic || false);
              setProjectShareToken(data.shareToken);
            }
          } catch (error) {
            // console.error("Error loading share status:", error);
          }
        };
        loadShareStatus();

        // console.log(`✅ Switched to tab on the left: ${nextModel.name}`);
      } else if (isClosingActiveTab) {
        // No remaining tabs
        setActiveModelId(null);
        setProjectIsPublic(false);
        setProjectShareToken(undefined);

        // ✅ Reset camera to defaults via View.tsx
        if ((window as any).__restoreCameraState) {
          (window as any).__restoreCameraState({
            rotation: { x: -30, y: -45 },
            distance: 50,
            isOrthographic: false,
          });
        }

        const isDarkMode = document.documentElement.classList.contains("dark");
        const defaultColor = isDarkMode
          ? DEFAULT_COLORS.dark
          : DEFAULT_COLORS.light;
        setColorValue(defaultColor);
        isDefaultColorRef.current = true;

        setTransparencyValue(100);

        hasLoadedRef.current = null;

        if (onProjectCleared) {
          onProjectCleared();
        }
      }
    },
    [activeModelId, openModels, onProjectCleared, versionControl],
  );

  //
  const refreshSidebarProjects = useCallback(async () => {
    if (!user) return;

    try {
      // ✅ STEP 1: Fetch the PERMANENT list of hidden examples from the User Profile
      // This ensures that even if you delete the copy, the original stays hidden
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const persistentHiddenIds = new Set<string>(
        userDocSnap.exists() ? userDocSnap.data()?.hiddenExampleIds || [] : [],
      );

      // Add local (session) hidden IDs to the persistent set
      localHiddenExampleIds.current.forEach((id) =>
        persistentHiddenIds.add(id),
      );

      // 2. Load User Projects
      const q = query(
        collection(db, "projects"),
        where("userId", "==", user.uid),
      );

      const querySnapshot = await getDocs(q);
      const loadedProjects: typeof sidebarProjects = [];
      const copiedExampleIds = new Set<string>();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.copiedFromExample) {
          copiedExampleIds.add(data.copiedFromExample);
        }

        const safeToDate = (value: any): Date => {
          if (!value) return new Date(0);
          if (value.toDate) return value.toDate();
          if (value instanceof Date) return value;
          if (typeof value === "string") return new Date(value);
          return new Date(0);
        };

        loadedProjects.push({
          id: doc.id,
          name: data.name || "Untitled",
          createdAt: safeToDate(data.createdAt),
          updatedAt: safeToDate(data.updatedAt),
          userId: data.userId,
          thumbnail: data.thumbnail,
          description: data.description,
          isPublic: data.isPublic || false,
          shareToken: data.shareToken,
          copiedFromExample: data.copiedFromExample,
        });
      });

      // 3. Process Examples
      const EXAMPLE_PROJECT_IDS = [
        "S79Nx7xikgyBxC78ksSf",
        "VkQgP0JkSWZmM1jIjeJN",
        "LHIiWYRVduGrYUe6hAyb",
      ];

      for (const exampleId of EXAMPLE_PROJECT_IDS) {
        // ✅ CRITICAL CHECK:
        // Hide if:
        // 1. We have a live copy (copiedExampleIds)
        // OR
        // 2. It was EVER hidden in the past (persistentHiddenIds)
        if (
          copiedExampleIds.has(exampleId) ||
          persistentHiddenIds.has(exampleId)
        ) {
          continue;
        }

        try {
          const exampleDoc = await getDoc(doc(db, "projects", exampleId));
          if (exampleDoc.exists()) {
            const data = exampleDoc.data();
            const safeToDate = (value: any): Date => {
              if (!value) return new Date(0);
              if (value.toDate) return value.toDate();
              if (value instanceof Date) return value;
              if (typeof value === "string") return new Date(value);
              return new Date(0);
            };

            loadedProjects.push({
              id: exampleDoc.id,
              name: data.name || "Example",
              createdAt: safeToDate(data.createdAt),
              updatedAt: safeToDate(data.updatedAt),
              userId: data.userId,
              thumbnail: data.thumbnail,
              description: data.description,
              isPublic: data.isPublic || false,
              shareToken: data.shareToken,
              copiedFromExample: data.copiedFromExample,
            });
          }
        } catch (error) {
          console.warn(
            `Failed to load example ${exampleId} (keeping cached version):`,
            error,
          );
        }
      }

      // 4. NON-DESTRUCTIVE MERGE (Cache Rescue)
      setSidebarProjects((currentProjects) => {
        const freshMap = new Map(loadedProjects.map((p) => [p.id, p]));
        const combinedList: typeof sidebarProjects = [];
        const processedIds = new Set<string>();

        // Step A: Update existing items or RESCUE failed fetches
        currentProjects.forEach((existingProject) => {
          const freshVersion = freshMap.get(existingProject.id);

          // SPECIAL SAFETY CHECK: Is this an Example ID?
          if (EXAMPLE_PROJECT_IDS.includes(existingProject.id)) {
            const shouldHide =
              copiedExampleIds.has(existingProject.id) ||
              persistentHiddenIds.has(existingProject.id); // ✅ Check persistent IDs here too

            // If it should be hidden, remove it
            if (shouldHide) return;

            // If it should NOT be hidden, but the fetch failed (freshVersion is undefined),
            // WE KEEP THE EXISTING ONE.
            if (!freshVersion) {
              combinedList.push(existingProject);
              processedIds.add(existingProject.id);
              return;
            }
          }

          combinedList.push(freshVersion || existingProject);
          processedIds.add(existingProject.id);
        });

        // Step B: Add any TRULY NEW items
        loadedProjects.forEach((newProject) => {
          if (!processedIds.has(newProject.id)) {
            combinedList.push(newProject);
            processedIds.add(newProject.id);
          }
        });

        // Step C: Add Optimistic Project
        if (optimisticProject && !processedIds.has(optimisticProject.id)) {
          combinedList.push({
            id: optimisticProject.id,
            name: optimisticProject.name,
            createdAt: new Date(),
            updatedAt: optimisticProject.updatedAt || new Date(),
            userId: user.uid,
            thumbnail: undefined,
            description: undefined,
            isPublic: false,
            copiedFromExample: undefined,
          });
        }

        combinedList.sort(
          (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
        );
        return combinedList;
      });
    } catch (error) {
      console.error("Error refreshing sidebar projects:", error);
    }
  }, [user, optimisticProject]);
  // Around line 650, update handleTabRename:
  const handleTabRename = useCallback(
    async (tabId: string, newName: string) => {
      if (!newName || newName.trim() === "") return;

      // Update local state (Open Tabs)
      setOpenModels((prev) =>
        prev.map((model) =>
          model.id === tabId ? { ...model, name: newName } : model,
        ),
      );

      // If this is the active tab, update fileName too
      if (tabId === activeModelId) {
        setFileName(newName);
      }

      // Optimistically update the sidebar list IMMEDIATELY
      // This removes the "second long" lag while waiting for Firebase
      setSidebarProjects((prev) =>
        prev.map((p) => (p.id === tabId ? { ...p, name: newName } : p)),
      );

      // Optimistically update the TopToolbar state for ALL projects
      setOptimisticProject({ id: tabId, name: newName });

      // Save to Firebase
      if (user) {
        try {
          // ✅ FIX: Only update 'name', DO NOT update 'updatedAt'
          // This ensures the project stays in its current sorted position
          await updateDoc(doc(db, "projects", tabId), {
            name: newName,
          });

          // We don't need to trigger a full refresh here since we updated locally
          // setProjectsRefreshTrigger((prev) => prev + 1);
        } catch (error) {
          // console.error("Failed to save tab rename:", error);
        }
      }
    },
    [activeModelId, user], // Removed refreshSidebarProjects dependency
  );

  const handleCloseAllTabs = useCallback(() => {
    setOpenModels([]);
    setActiveModelId(null);

    // 🔧 FIX: Reset timeline when closing all tabs
    (versionControl as any).state.timelines = {
      main: {
        featureTree: [],
        chatMessages: [],
        bodyCounter: 0,
        tipFeature: null,
        commandHistory: [],
      },
    };
    (versionControl as any).state.currentTimelineId = "main";
    // console.log(`✅ CLOSE ALL: Reset timeline to empty state`);

    // Clear all UI state
    setNoahGeometry([]);
    setFeatureTree([]);
    setExpandedItems([]);
    setFileName("");
    setCurrentSelection([]);

    if (rightSidebarRef.current) {
      rightSidebarRef.current.setChatMessages([]);
    }

    // ✅ CRITICAL: Reset camera to defaults via View.tsx
    if ((window as any).__restoreCameraState) {
      (window as any).__restoreCameraState({
        rotation: { x: -30, y: -45 },
        distance: 50,
        isOrthographic: false,
      });
    }

    // ✅ Reset color to default
    const isDarkMode = document.documentElement.classList.contains("dark");
    const defaultColor = isDarkMode
      ? DEFAULT_COLORS.dark
      : DEFAULT_COLORS.light;
    setColorValue(defaultColor);
    isDefaultColorRef.current = true;

    // ✅ Reset transparency
    setTransparencyValue(100);

    // ✅ NEW: Auto-open Models sidebar and close Feature Tree
    setIsLeftSidebarOpen(false);
    // setIsModelsSidebarOpen(true);

    hasLoadedRef.current = null;

    if (onProjectCleared) {
      onProjectCleared();
    }

    // console.log(
    //   "✅ Closed all tabs, reset camera/color/transparency, opened Models sidebar"
    // );
  }, [onProjectCleared, versionControl]);

  const loadSidebarProjects = async () => {
    if (!user) {
      return;
    }

    try {
      // ✅ STEP 1: Fetch Persistent Hidden IDs (This was missing before!)
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const persistentHiddenIds = new Set<string>(
        userDocSnap.exists() ? userDocSnap.data()?.hiddenExampleIds || [] : [],
      );

      // Load user's own projects
      const q = query(
        collection(db, "projects"),
        where("userId", "==", user.uid),
      );

      const querySnapshot = await getDocs(q);
      const loadedProjects: typeof sidebarProjects = [];
      const copiedExampleIds = new Set<string>();

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        if (data.copiedFromExample) {
          copiedExampleIds.add(data.copiedFromExample);
        }

        const safeToDate = (value: any): Date => {
          if (!value) return new Date(0);
          if (value.toDate) return value.toDate();
          if (value instanceof Date) return value;
          if (typeof value === "string") return new Date(value);
          return new Date(0);
        };

        loadedProjects.push({
          id: doc.id,
          name: data.name || "Untitled",
          createdAt: safeToDate(data.createdAt),
          updatedAt: safeToDate(data.updatedAt),
          userId: data.userId,
          thumbnail: data.thumbnail,
          description: data.description,
          isPublic: data.isPublic || false,
          shareToken: data.shareToken,
          copiedFromExample: data.copiedFromExample,
        });
      });

      // ✅ NEW: Also load the example projects (Respecting Persistent Hidden IDs)
      const EXAMPLE_PROJECT_IDS = [
        "S79Nx7xikgyBxC78ksSf",
        "VkQgP0JkSWZmM1jIjeJN",
        "LHIiWYRVduGrYUe6hAyb",
      ];

      for (const exampleId of EXAMPLE_PROJECT_IDS) {
        // ✅ CRITICAL CHECK: Skip if copied OR persisted as hidden
        if (
          copiedExampleIds.has(exampleId) ||
          persistentHiddenIds.has(exampleId)
        )
          continue;

        try {
          const exampleDoc = await getDoc(doc(db, "projects", exampleId));
          if (exampleDoc.exists()) {
            const data = exampleDoc.data();

            const safeToDate = (value: any): Date => {
              if (!value) return new Date(0);
              if (value.toDate) return value.toDate();
              if (value instanceof Date) return value;
              if (typeof value === "string") return new Date(value);
              return new Date(0);
            };

            loadedProjects.push({
              id: exampleDoc.id,
              name: data.name || "Example",
              createdAt: safeToDate(data.createdAt),
              updatedAt: safeToDate(data.updatedAt),
              userId: data.userId,
              thumbnail: data.thumbnail,
              description: data.description,
              isPublic: data.isPublic || false,
              shareToken: data.shareToken,
              copiedFromExample: data.copiedFromExample,
            });
          }
        } catch (error) {
          console.warn(`Failed to load example ${exampleId}:`, error);
        }
      }

      setSidebarProjects((currentProjects) => {
        const freshMap = new Map(loadedProjects.map((p) => [p.id, p]));

        const preservedList = currentProjects
          .filter((p) => freshMap.has(p.id))
          .map((p) => freshMap.get(p.id)!);

        const currentIds = new Set(currentProjects.map((p) => p.id));
        const newItems = loadedProjects.filter((p) => !currentIds.has(p.id));

        newItems.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

        return [...newItems, ...preservedList];
      });
    } catch (error) {
      // console.error("Error loading sidebar projects:", error);
    }
  };

  // Add handler for project deletion from sidebar
  const handleSidebarProjectDelete = async (projectId: string) => {
    try {
      await deleteDoc(doc(db, "projects", projectId));

      // Remove from sidebar list
      setSidebarProjects((prev) => prev.filter((p) => p.id !== projectId));

      // If this was the active project, close it
      if (activeModelId === projectId) {
        // Close the tab
        setOpenModels((prev) => prev.filter((m) => m.id !== projectId));

        // Switch to another tab if available
        const remainingModels = openModels.filter((m) => m.id !== projectId);
        if (remainingModels.length > 0) {
          setActiveModelId(remainingModels[0].id);
        } else {
          setActiveModelId(null);
        }
      }

      setProjectsRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      // console.error("Error deleting project:", error);
    }
  };

  const handleSidebarProjectRename = async (
    projectId: string,
    newName: string,
  ) => {
    try {
      // ✅ FIX: Only update 'name', DO NOT update 'updatedAt'
      await updateDoc(doc(db, "projects", projectId), {
        name: newName,
      });

      // Update in sidebar list in-place
      setSidebarProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, name: newName } : p)),
      );

      // Update in open tabs if present
      setOpenModels((prev) =>
        prev.map((m) => (m.id === projectId ? { ...m, name: newName } : m)),
      );

      // Update fileName if this is the active project
      if (activeModelId === projectId) {
        setFileName(newName);
      }

      // If this is the optimistic project, update that too
      if (optimisticProject && optimisticProject.id === projectId) {
        setOptimisticProject({ id: projectId, name: newName });
      }
    } catch (error) {
      // console.error("Error renaming project:", error);
    }
  };

  // Load projects in background when component mounts
  useEffect(() => {
    if (user) {
      loadSidebarProjects();
    }
  }, [user]);

  // Add handler for project selection from sidebar:
  // Add handler for project selection from sidebar:
  // REPLACE the entire function with this version that sets loading FIRST:
  const handleSidebarProjectSelect = async (projectId: string) => {
    // ✅ Set loading FIRST before changing ANY state
    setLoadingProjectId(projectId);
    setIsLoading(true);
    setIsViewLoading(true);

    saveCurrentModelState();

    const existingTab = openModels.find((m) => m.id === projectId);

    if (existingTab) {
      // ✅ For existing tabs, just switch state
      setActiveModelId(projectId);
      setCurrentBranch(existingTab.branchId);
      setNoahGeometry([...existingTab.geometry]);
      setFeatureTree([...existingTab.featureTree]);

      if (rightSidebarRef.current) {
        rightSidebarRef.current.setChatMessages([...existingTab.chatMessages]);
      }

      setFileName(existingTab.name);

      const cachedColor = sessionStorage.getItem(`project_${projectId}_color`);
      const cachedTransparency = sessionStorage.getItem(
        `project_${projectId}_transparency`,
      );

      if (cachedColor) {
        setColorValue(cachedColor);
      }

      if (cachedTransparency) {
        setTransparencyValue(parseFloat(cachedTransparency));
      }

      // ✅ Clear loading states at the END
      setLoadingProjectId(null);
      setIsLoading(false);
      setIsViewLoading(false);

      return;
    }

    // For new projects, load from Firebase
    try {
      await loadProject(projectId);
    } catch (error) {
      console.error("Failed to load project:", error);
      setIsViewLoading(false);
    } finally {
      setLoadingProjectId(null);
      setIsLoading(false);
    }
  };

  const handleFeatureTreeClick = () => {
    if (openModels.length === 0) {
      return;
    }

    setUserHasTouchedSidebars(true);

    // If already open, do nothing
    if (isLeftSidebarOpen) {
      return;
    }

    // Close other sidebars
    setIsRightSidebarOpen(false);
    setIsModelsSidebarOpen(false);

    // Open this sidebar
    setIsLeftSidebarOpen(true);
  };

  const handleChatClick = () => {
    handleNewModelButtonClick();
  };

  const handleModelsClick = () => {
    console.log("🔍 handleModelsClick called in CAD.tsx");
    setUserHasTouchedSidebars(true);

    // Trigger TopToolbar's search functionality
    if ((window as any).__toggleTopToolbarSearch) {
      console.log("✅ Calling __toggleTopToolbarSearch");
      (window as any).__toggleTopToolbarSearch();
    } else {
      console.log("❌ __toggleTopToolbarSearch not found!");
    }
  };

  // Around line 1075, restore the conditional initial states:
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false); // ← Keep closed
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);

  const [isProjectSidebarExpanded, setIsProjectSidebarExpanded] =
    useState(false);
  const [isModelsSidebarOpen, setIsModelsSidebarOpen] = useState(false); // ✅ CHANGED: Always closed initially

  // ADD THIS NEW STATE RIGHT AFTER:
  const [forceShowProjectSidebar, setForceShowProjectSidebar] = useState(false);
  // REPLACE the useEffect with this corrected version:
  // Around line 1075, REPLACE the useEffect with:

  // Toolbar resize handler - separate from sidebar resize
  // Toolbar resize handler - separate from sidebar resize
  useEffect(() => {
    const handleToolbarResize = () => {
      const availableWidth = window.innerWidth;

      if (isToolbarExpanded && availableWidth < 920) {
        setIsToolbarExpanded(false);
        setUserManuallyClosedToolbar(false); // Reset when auto-closing
      }

      // Don't auto-open if user manually closed it
      if (
        !isToolbarExpanded &&
        availableWidth >= 920 &&
        !userManuallyClosedToolbar
      ) {
        setIsToolbarExpanded(true);
      }
    };

    // REMOVED: handleToolbarResize(); - Don't run on mount, only on resize events

    window.addEventListener("resize", handleToolbarResize);

    return () => {
      window.removeEventListener("resize", handleToolbarResize);
    };
  }, [isToolbarExpanded, userManuallyClosedToolbar]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const prevWidth = (window as any).__prevWindowWidth || windowWidth;
      (window as any).__prevWindowWidth = windowWidth;

      setIsMobile(windowWidth < 1280);

      // ✅ ALWAYS enforce one-sidebar rule on ANY screen size change
      const openCount = [
        isLeftSidebarOpen,
        isRightSidebarOpen,
        isModelsSidebarOpen,
      ].filter(Boolean).length;

      if (openCount > 1) {
        // Keep the rightmost open sidebar
        if (isRightSidebarOpen) {
          setIsLeftSidebarOpen(false);
          setIsModelsSidebarOpen(false);
        } else if (isLeftSidebarOpen) {
          setIsRightSidebarOpen(false);
          setIsModelsSidebarOpen(false);
        } else if (isModelsSidebarOpen) {
          setIsLeftSidebarOpen(false);
          setIsRightSidebarOpen(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    // Initial check on mount
    const windowWidth = window.innerWidth;
    (window as any).__prevWindowWidth = windowWidth;
    setIsMobile(windowWidth < 1280);

    return () => window.removeEventListener("resize", handleResize);
  }, [isLeftSidebarOpen, isRightSidebarOpen, isModelsSidebarOpen]);

  const [isSwitchingBranch, setIsSwitchingBranch] = useState(false);
  const [availableBranches, setAvailableBranches] = useState<string[]>([
    "main",
  ]);
  const [transparencyValue, setTransparencyValue] = useState(() => {
    const stored = sessionStorage.getItem(`project_${projectId}_transparency`);
    return stored ? parseFloat(stored) : 100;
  });

  // Watch for theme changes and update color if using default
  // Watch for theme changes and update color if using default
  // Watch for theme changes and update color if using default
  // Watch for theme changes and update color if using default
  // In CAD.tsx, around line 660-720, update the theme observer to this simpler version:
  // Watch for theme changes and update color if using default
  // Watch for theme changes and update color if using default
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const htmlElement = document.querySelector("html");
          const bodyElement = document.body;
          const isDarkMode =
            htmlElement?.classList.contains("dark-theme") ||
            bodyElement?.classList.contains("dark-theme") ||
            document.documentElement.classList.contains("dark-theme");

          const newTheme = isDarkMode ? "dark" : "light";

          if (newTheme !== currentTheme) {
            setCurrentTheme(newTheme);

            if (isDefaultColorRef.current) {
              const newDefaultColor = DEFAULT_COLORS[newTheme];
              setColorValue(newDefaultColor);

              if (projectId) {
                sessionStorage.setItem(
                  `project_${projectId}_color`,
                  newDefaultColor,
                );
              }
            }
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, [currentTheme, projectId]);
  // Debug: Log initial state
  // useEffect(() => {
  //   console.log(`🎨 INITIAL STATE CHECK:`);
  //   console.log(`   - Project ID: ${projectId}`);
  //   console.log(`   - Current theme: ${currentTheme}`);
  //   console.log(`   - Color value: ${colorValue}`);
  //   console.log(`   - isDefaultColorRef: ${isDefaultColorRef.current}`);
  //   console.log(
  //     `   - Session storage color: ${sessionStorage.getItem(
  //       `project_${projectId}_color`
  //     )}`
  //   );
  // }, [projectId, currentTheme, colorValue]);

  const [displayUnit, setDisplayUnit] = useState(() => {
    const stored = sessionStorage.getItem(`project_${projectId}_displayUnit`);
    return stored || "mm";
  });
  const [selectedExportFormat, setSelectedExportFormat] = useState(".STL");
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false); // ADD THIS LINE
  const [showTopToolbarFeedbackModal, setShowTopToolbarFeedbackModal] =
    useState(false);
  const [timelineBranchMap, setTimelineBranchMap] = useState<
    Record<string, string>
  >({
    main: "main",
  });
  const [connectionStatus, setConnectionStatus] = useState<
    "unknown" | "connected" | "error"
  >("unknown");
  const [projectName, setProjectName] = useState("Loading...");
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [shareStatusChanged, setShareStatusChanged] = useState(0);
  const [isPendingSave, setIsPendingSave] = useState(false);
  const [pendingFeatureId, setPendingFeatureId] = useState<string | null>(null);
  const [sharedProjectData, setSharedProjectData] = useState<any>(null);

  // Expose project ID globally so ProjectLeftSidebar can access it
  // Expose project ID globally so ProjectLeftSidebar can access it
  useEffect(() => {
    if (projectId) {
      sessionStorage.setItem("currentProjectId", projectId);

      const currentPath = window.location.pathname;
      const isShareUrl = currentPath.startsWith("/share/");

      if (
        !isSharedView &&
        !isShareUrl &&
        viewMode !== "viewer" &&
        !sharedProjectData
      ) {
        window.history.pushState({}, "", `/models/${projectId}`);
      }
    } else {
      sessionStorage.removeItem("currentProjectId");
    }
  }, [projectId, isSharedView, viewMode, sharedProjectData]);
  // ADD THIS NEW useEffect RIGHT AFTER:
  useEffect(() => {
    // Cleanup function runs when component unmounts
    return () => {
      // Clean up shared view markers
      sessionStorage.removeItem("isSharedView");
      sessionStorage.removeItem("shareToken");
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  // Add this inside your CAD component, before the generateThumbnail function
  const stlLoader = useRef({
    loadSTLFromBase64: function (base64Data: string): THREE.BufferGeometry {
      try {
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const header = new TextDecoder().decode(bytes.slice(0, 80));
        if (header.trim().startsWith("solid")) {
          return this.parseASCIISTL(binaryString);
        } else {
          return this.parseBinarySTL(bytes);
        }
      } catch (error) {
        // console.error("STL parsing error:", error);
        return new THREE.BoxGeometry(20, 15, 10);
      }
    },

    parseASCIISTL: function (stlText: string): THREE.BufferGeometry {
      const vertices: number[] = [];
      const normals: number[] = [];
      const lines = stlText.split("\n");
      let currentNormal = [0, 0, 0];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith("facet normal")) {
          const parts = line.split(/\s+/);
          currentNormal = [
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parseFloat(parts[4]),
          ];
        } else if (line.startsWith("vertex")) {
          const parts = line.split(/\s+/);
          vertices.push(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
          );
          normals.push(currentNormal[0], currentNormal[1], currentNormal[2]);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();
      geometry.computeTangents();

      return geometry;
    },

    parseBinarySTL: function (bytes: Uint8Array): THREE.BufferGeometry {
      const dataView = new DataView(bytes.buffer);
      const triangleCount = dataView.getUint32(80, true);
      const vertices: number[] = [];
      const normals: number[] = [];

      for (let i = 0; i < triangleCount; i++) {
        const offset = 84 + i * 50;
        const nx = dataView.getFloat32(offset, true);
        const ny = dataView.getFloat32(offset + 4, true);
        const nz = dataView.getFloat32(offset + 8, true);

        for (let j = 0; j < 3; j++) {
          const vertexOffset = offset + 12 + j * 12;
          const x = dataView.getFloat32(vertexOffset + 0, true);
          const y = dataView.getFloat32(vertexOffset + 4, true);
          const z = dataView.getFloat32(vertexOffset + 8, true);
          vertices.push(x, y, z);
          normals.push(nx, ny, nz);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();
      return geometry;
    },
  }).current;

  const generateThumbnail = useCallback(
    async (geometry: any[], currentColor: string): Promise<string | null> => {
      if (!geometry || geometry.length === 0) return null;

      const canvas = document.createElement("canvas");
      const size = 1024; // Increased from 512 to 1024 for higher resolution
      canvas.width = size;
      canvas.height = size;

      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: true, // ← CHANGED: Enable alpha channel
        powerPreference: "high-performance",
      });
      renderer.setSize(size, size);
      renderer.setPixelRatio(2);
      renderer.setClearColor(0x000000, 0); // ← CHANGED: Transparent background
      // CRITICAL: Copy the EXACT renderer settings from View.tsx
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const scene = new THREE.Scene();
      scene.background = null; // ← CHANGED: No background color (transparent)
      const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000); // Changed from 45 to 50 to match View

      // EXACT LIGHTING SETUP from View.tsx - this is what makes it look crisp and shiny

      // 1. ENVIRONMENT LIGHTING
      const ambientLight = new THREE.AmbientLight(0xf8f9fa, 0.3);
      scene.add(ambientLight);

      // 2. KEY LIGHT
      const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
      keyLight.position.set(100, 120, 80);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.width = 4096;
      keyLight.shadow.mapSize.height = 4096;
      keyLight.shadow.camera.near = 0.1;
      keyLight.shadow.camera.far = 1000;
      keyLight.shadow.camera.left = -200;
      keyLight.shadow.camera.right = 200;
      keyLight.shadow.camera.top = 200;
      keyLight.shadow.camera.bottom = -200;
      keyLight.shadow.bias = -0.0005;
      keyLight.shadow.normalBias = 0.02;
      keyLight.shadow.radius = 4;
      scene.add(keyLight);

      // 3. FILL LIGHT
      const fillLight = new THREE.DirectionalLight(0xe8f4f8, 0.6);
      fillLight.position.set(-80, 60, 40);
      fillLight.castShadow = true;
      fillLight.shadow.mapSize.width = 2048;
      fillLight.shadow.mapSize.height = 2048;
      fillLight.shadow.camera.near = 0.1;
      fillLight.shadow.camera.far = 800;
      fillLight.shadow.camera.left = -150;
      fillLight.shadow.camera.right = 150;
      fillLight.shadow.camera.top = 150;
      fillLight.shadow.camera.bottom = -150;
      fillLight.shadow.bias = -0.0003;
      fillLight.shadow.radius = 6;
      scene.add(fillLight);

      // 4. RIM LIGHT
      const rimLight = new THREE.DirectionalLight(0xfff8e1, 0.8);
      rimLight.position.set(-60, 40, -100);
      scene.add(rimLight);

      // 5. TOP LIGHT
      const topLight = new THREE.DirectionalLight(0xf0f8ff, 0.5);
      topLight.position.set(0, 150, 0);
      scene.add(topLight);

      // 6. HEMISPHERE LIGHT
      const hemisphereLight = new THREE.HemisphereLight(
        0xb8d4f0,
        0x8b7355,
        0.4,
      );
      hemisphereLight.position.set(0, 100, 0);
      scene.add(hemisphereLight);

      const meshes: THREE.Mesh[] = [];

      try {
        geometry.forEach((geomData) => {
          if (geomData.stl_data) {
            const geom = stlLoader.loadSTLFromBase64(geomData.stl_data);

            const cleanColor = currentColor
              .replace("#", "")
              .padEnd(6, "0")
              .slice(0, 6);
            const hexColor = `#${cleanColor}`;
            const color = new THREE.Color(hexColor);

            // EXACT MATERIAL from View.tsx
            const material = new THREE.MeshStandardMaterial({
              color: color,
              metalness: 0.1,
              roughness: 0.2,
              transparent: transparencyValue < 100,
              opacity: transparencyValue / 100,
              side:
                transparencyValue < 100 ? THREE.DoubleSide : THREE.FrontSide,
              depthWrite: transparencyValue >= 100,
            });

            const mesh = new THREE.Mesh(geom, material);
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            // Center the mesh
            geom.computeBoundingBox();
            const center = new THREE.Vector3();
            geom.boundingBox!.getCenter(center);
            mesh.position.sub(center);

            meshes.push(mesh);
            scene.add(mesh);
          }
        });

        if (meshes.length === 0) return null;

        // Auto-fitting - fit to visible screen bounds
        const box = new THREE.Box3();
        meshes.forEach((mesh) => box.expandByObject(mesh));

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Start with an initial distance estimate
        const fov = 50;
        const fovRadians = (fov * Math.PI) / 180;
        const maxDim = Math.max(size.x, size.y, size.z);
        let distance = maxDim * 2;

        // Position camera
        const camDirX = 0.7;
        const camDirY = 0.5;
        const camDirZ = 0.7;

        camera.position.set(
          center.x + distance * camDirX,
          center.y + distance * camDirY,
          center.z + distance * camDirZ,
        );
        camera.lookAt(center);
        camera.updateProjectionMatrix();

        // Project all 8 corners of the bounding box to screen space
        const corners = [
          new THREE.Vector3(box.min.x, box.min.y, box.min.z),
          new THREE.Vector3(box.min.x, box.min.y, box.max.z),
          new THREE.Vector3(box.min.x, box.max.y, box.min.z),
          new THREE.Vector3(box.min.x, box.max.y, box.max.z),
          new THREE.Vector3(box.max.x, box.min.y, box.min.z),
          new THREE.Vector3(box.max.x, box.min.y, box.max.z),
          new THREE.Vector3(box.max.x, box.max.y, box.min.z),
          new THREE.Vector3(box.max.x, box.max.y, box.max.z),
        ];

        // Project corners and find screen bounds
        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;

        corners.forEach((corner) => {
          const projected = corner.clone().project(camera);
          minX = Math.min(minX, projected.x);
          maxX = Math.max(maxX, projected.x);
          minY = Math.min(minY, projected.y);
          maxY = Math.max(maxY, projected.y);
        });

        // Calculate how much to scale distance to fit within padding
        // Screen space is -1 to 1, we want to fit within -0.906 to 0.906 (leaving ~4.7% padding per side)
        const targetBounds = 0.906;
        const currentWidth = maxX - minX;
        const currentHeight = maxY - minY;
        const scaleNeeded = Math.max(
          currentWidth / (targetBounds * 2),
          currentHeight / (targetBounds * 2),
        );

        // Adjust distance
        distance = distance * scaleNeeded;

        // Reposition camera with corrected distance
        camera.position.set(
          center.x + distance * camDirX,
          center.y + distance * camDirY,
          center.z + distance * camDirZ,
        );
        camera.lookAt(center);

        camera.near = distance * 0.1;
        camera.far = distance * 10;
        camera.updateProjectionMatrix();
        // Render
        renderer.render(scene, camera);

        // High quality PNG export
        const dataURL = canvas.toDataURL("image/png", 1.0); // Maximum quality

        // Cleanup
        renderer.dispose();
        scene.clear();

        return dataURL;
      } catch (error) {
        // console.error("Failed to generate thumbnail:", error);
        renderer.dispose();
        return null;
      }
    },
    [transparencyValue, stlLoader],
  );

  const handleFileDelete = useCallback(async () => {
    if (!projectId || !user) return;

    try {
      // Delete the project from Firebase
      // Delete the project from Firebase
      if (projectId) {
        await deleteDoc(doc(db, "projects", projectId as string));
      }
      // console.log(`Project ${projectId} deleted successfully`);

      // Go back to projects dashboard (which will show the "new-model" view)
      onBackToProjects();
    } catch (error) {
      // console.error("Error deleting project:", error);
    }
  }, [projectId, user, onBackToProjects]);

  const handleInitialCreationError = useCallback(
    async (failedProjectId: string) => {
      console.log(
        `⚠️ Initial creation error on ${failedProjectId} - cleaning up...`,
      );

      // 1. Clear Optimistic State (This fixes the TopToolbar issue)
      setOptimisticProject((prev) =>
        prev?.id === failedProjectId ? null : prev,
      );

      // 2. Immediately reset UI to "Empty State"
      setOpenModels((prev) => prev.filter((m) => m.id !== failedProjectId));

      if (activeModelId === failedProjectId) {
        setActiveModelId(null);
        setFileName("");
        setNoahGeometry([]);
        setFeatureTree([]);
        setExpandedItems([]);

        if (versionControl) {
          (versionControl as any).state.timelines = {
            main: {
              featureTree: [],
              chatMessages: [],
              bodyCounter: 0,
              tipFeature: null,
              commandHistory: [],
            },
          };
          (versionControl as any).state.currentTimelineId = "main";
        }
      }

      // 3. Delete from Sidebar list immediately
      setSidebarProjects((prev) =>
        prev.filter((p) => p.id !== failedProjectId),
      );

      // 4. Delete from Firebase (Background operation)
      if (failedProjectId) {
        try {
          await deleteDoc(doc(db, "projects", failedProjectId));
          console.log("✅ Cleanup: Deleted failed project doc");
        } catch (e) {
          console.warn("Cleanup failed:", e);
        }
      }
    },
    [activeModelId, versionControl],
  );
  // 👆👆👆 END NEW FUNCTION 👆👆👆

  const rightSidebarRef = useRef<RightSidebarRefMethods>(null);

  // In CAD.tsx, remove timestamps from branch IDs
  const getUserBranchId = (userId: string, projectId: string) => {
    return `user_${userId}_${projectId}`; // Remove timestamp!
  };

  const flattenForFirebase = (obj: any): any => {
    // Handle null/undefined
    if (obj === null || obj === undefined) return obj;

    // Handle primitive types
    if (typeof obj !== "object") return obj;

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        // If item is an array, flatten it completely
        if (Array.isArray(item)) {
          const flattened = item.flat(Infinity);
          // Return as object with metadata to preserve structure
          return {
            _type: "flattened_array",
            _data: flattened,
          };
        }
        return flattenForFirebase(item);
      });
    }

    // Handle objects
    const result: any = {};

    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) {
        result[key] = value;
        continue;
      }

      if (Array.isArray(value)) {
        // Check if array contains arrays (nested)
        const hasNestedArrays = value.some((v) => Array.isArray(v));

        if (hasNestedArrays) {
          // Convert each nested array to an object
          result[key] = value.map((v, idx) => {
            if (Array.isArray(v)) {
              return {
                _type: "flattened_array",
                _index: idx,
                _data: v.flat(Infinity),
              };
            }
            return flattenForFirebase(v);
          });
        } else {
          result[key] = value.map((v) => flattenForFirebase(v));
        }
      } else if (typeof value === "object") {
        result[key] = flattenForFirebase(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  };

  const unflattenFromFirebase = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      return obj.map((item) => {
        if (item && item._type === "flattened_array") {
          return item._data;
        }
        return unflattenFromFirebase(item);
      });
    }

    const result: any = {};

    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        result[key] = value.map((v) => {
          if (v && v._type === "flattened_array") {
            return v._data;
          }
          return unflattenFromFirebase(v);
        });
      } else if (value && typeof value === "object") {
        result[key] = unflattenFromFirebase(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  };

  const consistentBranchId = useMemo(() => {
    if (user?.uid && projectId) {
      return getUserBranchId(user.uid, projectId);
    }
    return "main";
  }, [user?.uid, projectId]);

  // Add this BEFORE the heartbeat useEffect
  const lastHealthCheckRef = useRef<{
    timestamp: number;
    bodyCount: number;
    success: boolean;
  }>({ timestamp: 0, bodyCount: 0, success: false });

  useEffect(() => {
    if (!projectId || !user || !consistentBranchId) return;

    const heartbeat = setInterval(async () => {
      // NEW: Check global operation lock first
      if ((window as any).__operationInProgress) {
        // console.log("Skipping heartbeat - operation in progress (global lock)");
        return;
      }

      if (pendingCommandCount > 0) {
        // console.log("Skipping heartbeat - operation in progress");
        return;
      }

      // Skip if a command executed recently (within 15 seconds)
      const timeSinceLastCommand =
        Date.now() - (window as any).__lastCommandTime || 0;
      if (timeSinceLastCommand < 15000) {
        // console.log("Skipping heartbeat - recent command activity");
        return;
      }

      // Skip if stabilization is in progress
      if ((window as any).__isStabilizing) {
        // console.log("Skipping heartbeat - stabilization in progress");
        return;
      }

      // Skip if navigation/restore is in progress
      if (isSwitchingBranch) {
        // console.log("Skipping heartbeat - branch navigation in progress");
        return;
      }

      try {
        // CHANGED: Use refresh_geometry instead of DEBUG to keep branch warm
        const response = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "refresh_geometry", // ← CHANGED from DEBUG_DOCUMENT_STATE
            context: {},
            branch_id: currentBranch || consistentBranchId, // ← Use current branch!
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (response.ok) {
          const result = await response.json();
          const bodyCount = result.geometry?.length || 0;

          // CACHE the health check result
          lastHealthCheckRef.current = {
            timestamp: Date.now(),
            bodyCount: bodyCount,
            success: true,
          };

          // console.log(`Heartbeat: ${bodyCount} body/bodies present`);

          // If bodies missing but we expect them, try recovery
          if (bodyCount === 0 && noahGeometry.length > 0) {
            // console.warn(
            //   "⚠️ Heartbeat detected missing bodies - attempting recovery"
            // );

            try {
              const refreshResponse = await fetch(
                `${BACKEND_URL}/api/command`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    user_input: "refresh_geometry",
                    context: {},
                    branch_id: currentBranch || consistentBranchId,
                  }),
                  signal: AbortSignal.timeout(20000),
                },
              );

              const refreshResult = await refreshResponse.json();
              if (refreshResult.success && refreshResult.geometry) {
                // console.log("✅ Document recovered");
                lastHealthCheckRef.current.bodyCount =
                  refreshResult.geometry?.length || 1;
              }
            } catch (recoveryError) {
              // console.error("❌ Recovery failed:", recoveryError);
            }
          }
        }
      } catch (error) {
        // console.warn("Heartbeat failed:", error);
        lastHealthCheckRef.current = {
          timestamp: Date.now(),
          bodyCount: 0,
          success: false,
        };
      }
    }, 45000);

    return () => clearInterval(heartbeat);
  }, [
    projectId,
    user,
    consistentBranchId,
    currentBranch, // ← ADD THIS
    pendingCommandCount,
    noahGeometry,
    isSwitchingBranch,
  ]);

  // Add this right after the heartbeat useEffect closes
  useEffect(() => {
    // Expose health check data to RightSidebar via window
    (window as any).__lastHealthCheck = lastHealthCheckRef.current;
  }, [lastHealthCheckRef.current]);

  const getFeatureTreeFromChatMessages = useCallback(
    (chatMessages: ChatMessage[]): TreeItem[] => {
      let reconstructedTree: TreeItem[] = [];

      chatMessages.forEach((message, index) => {
        if (message.type === "assistant" && message.responseType !== "error") {
          // 1. Get the raw name string (which might be comma-separated)
          const rawTechnicalName =
            message.originalContent ||
            message.featureName ||
            message.content ||
            "";

          // 2. Split by comma to handle multiple features
          const technicalNames = rawTechnicalName
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

          // 🔧 Fix: Cast responseType to string to prevent TypeScript narrowing errors
          // (This resolves the "types have no overlap" error)
          const opType = message.responseType as string;

          // 3. Process each name individually
          technicalNames.forEach((technicalName) => {
            const displayName = technicalName; // Use technical name as base for display

            if (opType === "delete") {
              // Remove the deleted feature AND all its dependents from the tree
              const removeFeatureAndDependents = (
                items: TreeItem[],
                targetName: string,
              ): TreeItem[] => {
                return items
                  .map((item) => {
                    // Check if this body is being deleted
                    if (
                      item.name === targetName ||
                      item.displayName === targetName
                    ) {
                      return null; // Mark for removal
                    }

                    // Check children
                    if (item.children && item.children.length > 0) {
                      const featureIndex = item.children.findIndex(
                        (child) =>
                          child.name === targetName ||
                          child.displayName === targetName,
                      );

                      if (featureIndex !== -1) {
                        // Remove this child AND everything after it
                        return {
                          ...item,
                          children: item.children.slice(0, featureIndex),
                        };
                      }
                    }

                    return item;
                  })
                  .filter(Boolean) as TreeItem[];
              };

              reconstructedTree = removeFeatureAndDependents(
                reconstructedTree,
                technicalName,
              );
              return;
            }

            if (opType === "modify") {
              const updateFeatureInTree = (
                items: TreeItem[],
                targetName: string,
              ): boolean => {
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  if (
                    item.name === targetName ||
                    (item.displayName && item.displayName === targetName)
                  ) {
                    return true;
                  }

                  if (
                    item.children &&
                    updateFeatureInTree(item.children, targetName)
                  ) {
                    return true;
                  }
                }
                return false;
              };

              const found = updateFeatureInTree(
                reconstructedTree,
                technicalName,
              );
              if (found) {
                return;
              }
            }

            const primitiveTypes = [
              "Box",
              "Cylinder",
              "Sphere",
              "Cone",
              "Torus",
              "Ellipsoid",
              "Prism",
              "Wedge",
              "SubBox",
              "SubCylinder",
              "SubSphere",
              "SubCone",
              "SubTorus",
              "SubEllipsoid",
              "SubPrism",
              "SubWedge",
            ];

            const isPrimitive = primitiveTypes.some((type) =>
              technicalName.startsWith(type),
            );

            // 🔧 Fix: Ensure strict typing for the new TreeItem
            let treeItemType: "create" | "modify" | "delete" = "create";
            if (opType === "modify" || opType === "delete") {
              treeItemType = opType as "modify" | "delete";
            }

            if (isPrimitive || reconstructedTree.length === 0) {
              const featureId = `feature_${index}_${message.id}_${technicalName}`;
              const item: TreeItem = {
                id: featureId,
                name: technicalName,
                type: treeItemType,
                children: [],
              };

              if (displayName !== technicalName) {
                item.displayName = displayName;
              }

              reconstructedTree.push(item);
            } else {
              if (reconstructedTree.length > 0) {
                const lastItem =
                  reconstructedTree[reconstructedTree.length - 1];
                const featureId = `feature_${index}_${message.id}_${technicalName}`;

                if (!lastItem.children) {
                  lastItem.children = [];
                }

                const mappedDisplayName =
                  getDisplayNameForFeature(technicalName);
                const finalDisplayName =
                  displayName !== technicalName
                    ? displayName
                    : mappedDisplayName;

                const childItem: TreeItem = {
                  id: featureId,
                  name: technicalName,
                  type: treeItemType,
                  parentId: lastItem.id,
                };

                if (finalDisplayName !== technicalName) {
                  childItem.displayName = finalDisplayName;
                }

                lastItem.children.push(childItem);
              }
            }
          });
        }
      });

      return reconstructedTree;
    },
    [getDisplayNameForFeature],
  );

  const handleChatMessagesChange = useCallback(
    (chatMessages: ChatMessage[]) => {
      // 🔒 CRITICAL: Don't rebuild tree if the last operation was a delete
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (
        lastMessage?.type === "assistant" &&
        lastMessage?.responseType === "delete"
      ) {
        return;
      }

      // 🔒 CRITICAL: Don't rebuild tree if we just loaded AND already have the correct tree
      const timeSinceLoad =
        Date.now() - ((window as any).__projectLoadTime || 0);
      const justLoaded = timeSinceLoad < 2000;

      // If we just loaded and already have a feature tree, trust it
      if (justLoaded && featureTree.length > 0) {
        return;
      }

      // Only rebuild tree if we don't already have the right structure
      const expectedFeatureCount = chatMessages.filter(
        (msg) => msg.type === "assistant" && msg.responseType !== "error",
      ).length;

      // Skip rebuild if tree already has correct number of features
      if (
        featureTree.length > 0 &&
        featureTree.reduce(
          (count, item) => count + 1 + (item.children?.length || 0),
          0,
        ) === expectedFeatureCount
      ) {
        return;
      }

      const newFeatureTree = getFeatureTreeFromChatMessages(chatMessages);
      setFeatureTree([...newFeatureTree]);
      featureTreeRef.current = newFeatureTree; // Keep ref in sync

      const bodyCount = newFeatureTree.length;
      setBodyCounter(bodyCount);

      const bodyIds = newFeatureTree.map((body) => body.id);
      setExpandedItems(bodyIds);

      // ✅ CRITICAL FIX: Recalculate primitive counters whenever the tree is rebuilt from chat.
      // This ensures that subsequent operations start numbering correctly (e.g. Box002)
      // instead of resetting to 001.
      const newCounters = recalculatePrimitiveCounters(newFeatureTree);
      primitiveCountersRef.current = newCounters;

      // Also sync to openModels state for persistence
      if (activeModelId) {
        setOpenModels((prev) =>
          prev.map((model) =>
            model.id === activeModelId
              ? { ...model, primitiveCounters: newCounters }
              : model,
          ),
        );
      }
    },
    [
      getFeatureTreeFromChatMessages,
      featureTree,
      recalculatePrimitiveCounters,
      activeModelId, // Added dependency
    ],
  );

  const saveProjectAsNoahFile = useCallback(
    async (
      geometryData?: any[],
      forceBranchId?: string,
    ): Promise<{ success: boolean; error?: string }> => {
      // console.log(`💾 SAVE: Starting save with branch: ${forceBranchId}`);

      const branchToUse = forceBranchId || currentBranch;

      // 🔒 CRITICAL FIX: ONLY use activeModelId, NEVER use projectId
      const effectiveProjectId = activeModelId;

      if (!effectiveProjectId || !user) {
        // console.log("❌ SAVE: Missing activeModelId or user");
        return { success: false, error: "Missing project or user" };
      }

      // 🔒 NEW FIX: Check if model exists, but don't fail if it doesn't
      const currentModel = openModels.find((m) => m.id === effectiveProjectId);
      if (!currentModel) {
        // console.log(`⚠️ SAVE: Model ${effectiveProjectId} not in openModels yet`);
      }

      if (isSavingRef.current) {
        // console.log("⏭️ SAVE: Already saving, skipping...");
        return { success: false, error: "Save already in progress" };
      }

      const actualGeometryData = geometryData || noahGeometry;
      isSavingRef.current = true;

      try {
        // console.log("💾 SAVE: Generating thumbnail...");
        const thumbnailDataUrl = await generateThumbnail(
          actualGeometryData,
          colorValue,
        );

        let fcstdBase64: string | undefined;
        let fileSize = 0;
        let hasObjects = false;
        let objectCount = 0;

        if (actualGeometryData && actualGeometryData.length > 0) {
          const exportData = (window as any).__lastExportData;

          if (
            exportData &&
            exportData.fcstdBase64 &&
            // Keep the 5-minute freshness check
            Date.now() - exportData.timestamp < 300000
            // REMOVED: exportData.branchId === branchToUse check
          ) {
            console.log(
              "✅ SAVE: Found fresh export data (" +
                Math.round(exportData.fileSize / 1024) +
                "KB), attaching to project.",
            );

            fcstdBase64 = exportData.fcstdBase64;
            fileSize = exportData.fileSize;
            objectCount = exportData.objectCount;
            hasObjects = objectCount > 0;

            // Clear it so we don't reuse it by accident
            delete (window as any).__lastExportData;
          } else {
            console.warn(
              "⚠️ SAVE: No fresh export data available, file will not be saved",
            );
            hasObjects = false;
            objectCount = 0;
            fcstdBase64 = undefined;
          }
        }

        // Get current chat messages
        let currentChatMessages: ChatMessage[] = [];
        if (
          rightSidebarRef.current &&
          "getChatMessages" in rightSidebarRef.current &&
          rightSidebarRef.current.getChatMessages
        ) {
          currentChatMessages = rightSidebarRef.current.getChatMessages();
        }

        // Get current command history
        const commandHistory =
          commandHistoryRef.current[effectiveProjectId] || [];

        // Get versioned operations
        const versionedOperations = versionControl.getVersionedOperations();
        const currentTimelineId = versionControl.getCurrentTimelineId();

        // Helper to remove undefined
        const removeUndefined = (obj: any): any => {
          if (obj === null || obj === undefined) {
            return null;
          }
          if (Array.isArray(obj)) {
            return obj
              .map((item) => removeUndefined(item))
              .filter((item) => item !== undefined);
          }
          if (typeof obj === "object") {
            const cleaned: any = {};
            for (const [key, value] of Object.entries(obj)) {
              if (value !== undefined) {
                cleaned[key] = removeUndefined(value);
              }
            }
            return cleaned;
          }
          return obj;
        };

        const cleanedCommandHistory = removeUndefined(commandHistory);

        console.log("🛠️ SAVE DEBUG: Constructing project payload...");

        let safeChatHistory = [];
        let safeFeatureTree = [];
        let safeCommandHistory = [];
        let safeVersionedOps = [];

        // --- SAFE DATA PREPARATION (Try/Catch Wrappers) ---
        try {
          safeChatHistory = flattenForFirebase(currentChatMessages) || [];
        } catch (e) {
          console.warn("⚠️ Failed to flatten chat history:", e);
        }

        try {
          safeFeatureTree = flattenForFirebase(featureTree) || [];
        } catch (e) {
          console.warn("⚠️ Failed to flatten feature tree:", e);
        }

        try {
          // Aggressive sanitization for command history
          try {
            safeCommandHistory =
              flattenForFirebase(cleanedCommandHistory) || [];
          } catch (innerE) {
            console.warn(
              "⚠️ Complex command history failed, simplifying...",
              innerE,
            );
            // Fallback: Save only inputs and timestamps
            safeCommandHistory = cleanedCommandHistory.map((cmd: any) => ({
              userInput: cmd.userInput,
              messageId: cmd.messageId,
              timestamp: cmd.timestamp,
            }));
          }
        } catch (e) {
          console.warn("⚠️ Failed to process command history:", e);
        }

        try {
          safeVersionedOps = flattenForFirebase(versionedOperations) || {};
        } catch (e) {
          console.warn("⚠️ Failed to flatten versions:", e);
        }

        const rawProjectData = {
          hasChatHistory: safeChatHistory.length > 0,
          hasFeatureTree: safeFeatureTree.length > 0,
          ...(fcstdBase64 && { fcstdFile: fcstdBase64 }),
          fileSize,
          objectCount,
          hasObjects,
          thumbnail: thumbnailDataUrl,
          updatedAt: new Date().toISOString(),
          chatHistory: safeChatHistory,
          currentFeatureTree: safeFeatureTree,
          commandHistory: safeCommandHistory,
          versionedOperations: safeVersionedOps,
          currentTimelineId: currentTimelineId, // ⚠️ Likely culprit
          timelineBranchMap: timelineBranchMap, // ⚠️ Likely culprit
          modelColor: colorValue,
          transparencyValue: transparencyValue,
        };

        // 2. 👇 CRITICAL FIX: Sanitize the ENTIRE payload
        const projectData = removeUndefined(rawProjectData);

        console.log(
          "🛠️ SAVE DEBUG: Payload constructed successfully. FCStd present: " +
            !!projectData.fcstdFile,
        );

        // --- FIRESTORE WRITE WITH DEBUGGING ---
        try {
          console.log(
            `💾 FIRESTORE WRITE: Attempting to save to ${effectiveProjectId}...`,
          );

          // Debug the payload
          const payloadKeys = Object.keys(projectData);
          const hasFile = Object.prototype.hasOwnProperty.call(
            projectData,
            "fcstdFile",
          );
          console.log(`   - Payload Keys: ${payloadKeys.join(", ")}`);
          console.log(`   - Has fcstdFile in payload? ${hasFile}`);
          if (hasFile) {
            const fileLen = (projectData as any).fcstdFile?.length || 0;
            console.log(`   - fcstdFile Length: ${fileLen}`);
          }

          // Perform the write
          await updateDoc(doc(db, "projects", effectiveProjectId), projectData);

          if (fcstdBase64) {
            console.log(
              `🎉 FIRESTORE WRITE COMPLETE: Saved ${Math.round(fileSize / 1024)}KB to project ${effectiveProjectId}`,
            );
          } else {
            console.log(`⚠️ FIRESTORE WRITE COMPLETE: Metadata only (no file)`);
          }
        } catch (writeError) {
          console.error("🔥 FIRESTORE WRITE FAILED:", writeError);
          throw writeError; // Re-throw to handle in outer catch
        }

        return { success: !!fcstdBase64 };
      } catch (err) {
        // console.error("❌ SAVE: Exception during save:", err);
        return { success: false, error: (err as Error).message };
      } finally {
        isSavingRef.current = false;
      }
    },
    [
      currentBranch,
      noahGeometry,
      activeModelId,
      openModels,
      user,
      colorValue,
      featureTree,
      versionControl,
      timelineBranchMap,
      transparencyValue,
      generateThumbnail,
      flattenForFirebase,
      refreshSidebarProjects,
    ],
  );

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/health`, {
          method: "GET",
          signal: AbortSignal.timeout(64000),
        });
        if (response.ok) {
          setConnectionStatus("connected");
        } else {
          setConnectionStatus("error");
        }
      } catch (error) {
        if (error instanceof Error && !error.message.includes("timeout")) {
          setConnectionStatus("error");
        }
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(true);
  const loadingRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);

  // ← ADD THIS DEBUG CODE HERE
  useEffect(() => {
    // console.log(`🔍 isViewLoading changed to: ${isViewLoading}`);
    // console.log(`🔍 isSavingAfterCommand: ${isSavingAfterCommand}`);
    // console.log(
    //   `🔍 Combined isModelLoading: ${isViewLoading || isSavingAfterCommand}`
    // );
  }, [isViewLoading, isSavingAfterCommand]);

  // In CAD.tsx, around line 1500 where handleColorSave is defined, add this helper:

  // Add this helper function before the loadProject function
  // In CAD.tsx - add branch corruption detection
  // Around line 1500, REPLACE handleColorSave with this:
  // Replace the existing handleColorSave function (around line 1520):
  const handleColorSave = useCallback(
    async (currentColor: string) => {
      // Don't save if we're in the middle of an operation
      if (pendingCommandCount > 0) {
        // console.log("⏸️ Skipping color save - operation in progress");
        return;
      }

      if (isLoading) {
        // console.log("⏸️ Skipping color save - project is loading");
        return;
      }

      const timeSinceLoad =
        Date.now() - ((window as any).__projectLoadTime || 0);
      if (timeSinceLoad < 5000) {
        // console.log("⏸️ Skipping color save - project just loaded");
        return;
      }

      // console.log(
      //   `🎨 Saving color change: ${currentColor}, transparency: ${transparencyValue}%`
      // );

      try {
        let thumbnailDataUrl = null;
        if (noahGeometry && noahGeometry.length > 0) {
          // console.log("🎨 Regenerating thumbnail with new color...");
          thumbnailDataUrl = await generateThumbnail(
            noahGeometry,
            currentColor,
          );

          if (thumbnailDataUrl) {
            // console.log("✅ Thumbnail regenerated successfully");
          }
        }

        if (activeModelId && noahGeometry.length > 0) {
          // console.log(
          //   "💾 Saving project with new color baked into geometry..."
          // );

          const saveResult = await saveProjectAsNoahFile(
            noahGeometry,
            currentBranch,
          );

          if (saveResult.success) {
            if (thumbnailDataUrl && projectId) {
              await updateDoc(doc(db, "projects", projectId as string), {
                thumbnail: thumbnailDataUrl,
                updatedAt: new Date().toISOString(),
              });

              // DELETE or COMMENT OUT this line:
              // await refreshSidebarProjects();

              // Instead, update the local state directly if you want the thumbnail to update
              setSidebarProjects((prev) =>
                prev.map((p) =>
                  p.id === projectId
                    ? { ...p, thumbnail: thumbnailDataUrl! }
                    : p,
                ),
              );
            }
          } else {
            // console.error("❌ Failed to save project with new color");
          }
        } else {
          if (projectId) {
            await updateDoc(doc(db, "projects", projectId as string), {
              modelColor: currentColor,
              transparencyValue: transparencyValue,
              ...(thumbnailDataUrl && { thumbnail: thumbnailDataUrl }),
              updatedAt: new Date().toISOString(),
            });
            // console.log("✅ Color metadata saved to Firebase");

            // ← ADD THIS: Refresh sidebar even for metadata-only saves
            // await refreshSidebarProjects();
          }
        }
      } catch (error) {
        // console.error("❌ Failed to save color:", error);
      }
    },
    [
      projectId,
      transparencyValue,
      pendingCommandCount,
      isLoading,
      noahGeometry,
      generateThumbnail,
      saveProjectAsNoahFile,
      currentBranch,
      activeModelId,
      refreshSidebarProjects, // ← ADD THIS DEPENDENCY
    ],
  );

  // Around line 1520, EXPOSE the save handler globally:
  // Around line 460 in View.tsx, update the color save trigger effect:
  useEffect(() => {
    // Expose color save handler to View component
    (window as any).__triggerColorSave = async (color: string) => {
      await handleColorSave(color);

      // ← ADD THIS: Refresh sidebar after color save completes
      if ((window as any).__refreshSidebarProjects) {
        await (window as any).__refreshSidebarProjects();
      }
    };

    return () => {
      delete (window as any).__triggerColorSave;
    };
  }, [handleColorSave]);

  // Around line 1530, ADD tracking for when project loads:
  useEffect(() => {
    if (projectId && noahGeometry.length > 0) {
      // Mark when project finished loading
      (window as any).__projectLoadTime = Date.now();
    }
  }, [projectId, noahGeometry]);

  // Improve the isBranchCorrupted function with better error differentiation
  const isBranchCorrupted = async (branchId: string): Promise<boolean> => {
    try {
      const testResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "DEBUG_DOCUMENT_STATE",
          context: {},
          branch_id: branchId,
        }),
        signal: AbortSignal.timeout(10000), // Increase to 10s
      });

      if (!testResponse.ok) {
        // Only 404/410 suggest missing branch
        if (testResponse.status === 404 || testResponse.status === 410) {
          return true;
        }
        return false; // Other errors = backend issue, not corruption
      }

      const result = await testResponse.json();
      return !result.success;
    } catch (error) {
      if (error instanceof Error && error.name === "TimeoutError") {
        return false; // Timeout = slow backend, not corruption
      }
      return true; // Other errors might be corruption
    }
  };

  const createFreshBranch = async (
    userId: string,
    projectId: string,
    timestamp?: number, // ADDED: optional parameter
  ): Promise<string> => {
    const ts = timestamp || Date.now(); // CHANGED: use provided timestamp or generate new one
    const newBranchId = `user_${userId}_${projectId}_${ts}`; // CHANGED: use ts instead of timestamp

    // console.log(`Creating fresh branch: ${newBranchId}`);

    // Initialize with clear document
    const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_input: "CLEAR_DOCUMENT",
        context: {},
        branch_id: newBranchId,
      }),
      signal: AbortSignal.timeout(60000),
    });

    if (!clearResponse.ok) {
      throw new Error(
        `Failed to initialize fresh branch: HTTP ${clearResponse.status}`,
      );
    }

    const clearResult = await clearResponse.json();
    if (!clearResult.success) {
      throw new Error(
        `Fresh branch init failed: ${clearResult.message || "Unknown error"}`,
      );
    }

    // console.log(`Fresh branch ${newBranchId} initialized successfully`);
    return newBranchId;
  };

  // const warmupBackend = async (branchId: string): Promise<boolean> => {
  //   console.log(`🔥 WARMUP: Pinging backend with real API call...`);

  //   for (let i = 0; i < 2; i++) {
  //     // Reduced to 2 attempts since we use a real call
  //     try {
  //       const response = await fetch(`${BACKEND_URL}/api/command`, {
  //         method: "POST",
  //         credentials: "include",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           user_input: "DEBUG_DOCUMENT_STATE",
  //           context: {},
  //           branch_id: branchId,
  //         }),
  //         signal: AbortSignal.timeout(15000),
  //       });

  //       if (response.ok) {
  //         const result = await response.json();
  //         console.log(
  //           `✅ WARMUP: Backend is ready (${
  //             result.debug_data?.total_objects || 0
  //           } objects)`
  //         );
  //         return true;
  //       }

  //       console.log(
  //         `⚠️ WARMUP: Backend not ready (attempt ${i + 1}/2), status ${
  //           response.status
  //         }`
  //       );
  //       await new Promise((resolve) => setTimeout(resolve, 3000));
  //     } catch (error) {
  //       console.log(`⚠️ WARMUP: API call failed (attempt ${i + 1}/2):`, error);
  //       if (i < 1) {
  //         await new Promise((resolve) => setTimeout(resolve, 3000));
  //       }
  //     }
  //   }

  //   console.log(`⚠️ WARMUP: Backend warmup incomplete, but proceeding...`);
  //   return false; // Return false but don't fail - the actual operations have their own retries
  // };

  // Replace loadProject with this version
  // UPDATED: Added backend health check and better timeout handling

  const loadProject = useCallback(
    async (id: string) => {
      // ✅ ADD THIS DEBUG CODE AT THE VERY START:
      console.log("🔍 loadProject called with:", {
        id,
        hasOnToolbarExpandChange: !!onToolbarExpandChange,
        onToolbarExpandChangeType: typeof onToolbarExpandChange,
      });
      // console.log("🔍 LOAD PROJECT CALLED:", {
      //   id,
      //   isSharedView,
      //   viewMode,
      //   hasCallback: !!onSharedModelLoaded,
      // });
      // ADD THIS: Skip all backend calls in dev mode
      // if (import.meta.env.DEV) {
      //   console.log("🚀 DEV MODE: Bypassing backend, showing UI only");
      //   setFileName(initialProjectName || "Test Project");
      //   setIsLoading(false);
      //   setIsViewLoading(false);
      //   loadingRef.current = null;
      //   hasLoadedRef.current = id;
      //   return;
      // }
      // END BYPASS - existing code continues below...

      if (isLoading || loadingRef.current === id) {
        // console.log(`Skipping duplicate load for project: ${id}`);
        return;
      }

      setIsLoading(true);
      setIsViewLoading(true);
      loadingRef.current = id;

      // console.log(`📦 LOAD: Starting load for project: ${id}`);

      try {
        // Check for new project flow
        const storedBranch = sessionStorage.getItem(`project_${id}_branch`);
        const pendingPrompt = sessionStorage.getItem(
          `project_${id}_pendingPrompt`,
        );

        if (storedBranch && pendingPrompt) {
          // console.log(
          //   `📦 LOAD: New project flow detected with branch: ${storedBranch}`
          // );

          const projectDoc = await getDoc(doc(db, "projects", id));
          if (projectDoc.exists()) {
            const projectData = projectDoc.data();
            if (projectData.name) {
              setFileName(projectData.name);
              // console.log(
              //   `✅ Loaded project name from Firebase: ${projectData.name}`
              // );
            }
          }

          setCurrentBranch(storedBranch);
          setNoahGeometry([]);
          setFeatureTree([]);
          setExpandedItems([]);

          sessionStorage.removeItem(`project_${id}_branch`);
          sessionStorage.removeItem(`project_${id}_pendingPrompt`);

          setTimeout(() => {
            if (rightSidebarRef.current?.sendInitialPrompt) {
              rightSidebarRef.current.sendInitialPrompt(pendingPrompt);
            }
          }, 1000);

          setIsLoading(false);
          loadingRef.current = null;
          setIsViewLoading(false);
          // console.log(`✅ LOAD: New project flow complete`);
          return;
        }

        // Load existing project
        // console.log(`📦 LOAD: Loading existing project from Firebase...`);
        const projectDoc = await getDoc(doc(db, "projects", id));
        if (!projectDoc.exists()) {
          throw new Error("Project not found");
        }

        const projectData = projectDoc.data();

        // 👇 ADD THESE DEBUG LOGS 👇
        console.log(`📦 LOAD DEBUG: Project ${id} loaded from Firestore`);
        console.log(`   - Has Name: ${projectData.name}`);
        console.log(`   - Has FCStd File: ${!!projectData.fcstdFile}`);
        if (projectData.fcstdFile) {
          console.log(
            `   - File Size: ${Math.round(projectData.fcstdFile.length / 1024)}KB (Base64)`,
          );
        }
        // 👆 END DEBUG LOGS 👆

        // CRITICAL: Restore share status IMMEDIATELY
        setProjectIsPublic(projectData.isPublic || false);
        setProjectShareToken(projectData.shareToken);

        if (projectData.name) {
          setFileName(projectData.name);
        }

        // ✅ NEW: Check if this is a freshly duplicated example
        const isDuplicatedExample =
          sessionStorage.getItem(`project_${id}_isDuplicatedExample`) ===
          "true";

        if (isDuplicatedExample) {
          // console.log(
          //   `📖 LOAD: This is a duplicated example - loading saved state directly`
          // );

          // ✅ ADD THIS DEBUG LOG
          // console.log(`🔍 PROJECT DATA CHECK:`, {
          //   hasChatHistory: !!projectData.chatHistory,
          //   chatHistoryLength: projectData.chatHistory?.length,
          //   chatHistoryFirstItem: projectData.chatHistory?.[0], // ✅ ADD THIS
          //   chatHistoryRaw: projectData.chatHistory, // ✅ ADD THIS to see structure
          //   hasFeatureTree: !!projectData.currentFeatureTree,
          //   featureTreeLength: projectData.currentFeatureTree?.length,
          //   featureTreeFirstItem: projectData.currentFeatureTree?.[0], // ✅ ADD THIS
          //   featureTreeRaw: projectData.currentFeatureTree, // ✅ ADD THIS
          //   hasVersionedOps: !!projectData.versionedOperations,
          //   versionedOpsType: typeof projectData.versionedOperations,
          // });

          // Clear the flag
          sessionStorage.removeItem(`project_${id}_isDuplicatedExample`);

          // Create a branch but don't import the file - we'll use saved state
          const userBranchId = user
            ? `user_${user.uid}_${id}_${Date.now()}`
            : `guest_${id}_${Date.now()}`;

          // console.log(`📦 LOAD: Using branch: ${userBranchId}`);

          // Initialize branch
          const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "CLEAR_DOCUMENT",
              context: {},
              branch_id: userBranchId,
            }),
            signal: AbortSignal.timeout(60000),
          });

          if (!clearResponse.ok) {
            throw new Error("Failed to initialize branch");
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Import the Noah file to backend
          if (projectData.fcstdFile) {
            // console.log("📦 LOAD: Importing Noah file to backend...");
            const binaryData = atob(projectData.fcstdFile);
            const bytes = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
              bytes[i] = binaryData.charCodeAt(i);
            }

            const formData = new FormData();
            formData.append(
              "file",
              new Blob([bytes], { type: "application/octet-stream" }),
              "project.FCStd",
            );
            formData.append("branch_id", userBranchId);

            const importResponse = await fetch(
              `${BACKEND_URL}/api/command/import-file`,
              {
                method: "POST",
                credentials: "include",
                body: formData,
                signal: AbortSignal.timeout(300000),
              },
            );

            if (!importResponse.ok) {
              throw new Error(`Import failed: HTTP ${importResponse.status}`);
            }

            const importResult = await importResponse.json();
            if (!importResult.success) {
              throw new Error(`Import failed: ${importResult.message}`);
            }

            // console.log("✅ LOAD: File imported to backend");
          }

          // Get geometry from backend
          const geometryResponse = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "refresh_geometry",
              context: {},
              branch_id: userBranchId,
            }),
            signal: AbortSignal.timeout(30000),
          });

          let geometryForRender: any[] = [];
          if (geometryResponse.ok) {
            const geometryResult = await geometryResponse.json();
            if (geometryResult?.geometry?.length > 0) {
              geometryForRender = geometryResult.geometry;
              // console.log(
              //   `✅ LOAD: Got ${geometryForRender.length} geometry items`
              // );
            }
          }

          // Load saved state from Firebase (chat, features, version control)
          if (
            projectData.versionedOperations &&
            Object.keys(projectData.versionedOperations).length > 0
          ) {
            // console.log(`📦 LOAD: Restoring version control data...`);
            const unflattened = unflattenFromFirebase(
              projectData.versionedOperations,
            );

            Object.entries(unflattened).forEach(
              ([msgId, versionedOp]: [string, any]) => {
                versionControl.restoreVersionedOperation(
                  parseInt(msgId),
                  versionedOp,
                );

                versionedOp.versions.forEach((version: any) => {
                  if (version.timelineId && version.snapshot) {
                    (versionControl as any).state.timelines[
                      version.timelineId
                    ] = {
                      featureTree: version.snapshot.featureTree || [],
                      chatMessages: version.snapshot.chatMessages || [],
                      bodyCounter: version.snapshot.bodyCounter || 0,
                      tipFeature: version.snapshot.tipFeature || null,
                      commandHistory: version.snapshot.commandHistory || [],
                    };
                  }
                });
              },
            );
          } else {
            // console.log(
            //   `📦 LOAD: No version control data, using saved state...`
            // );
            // ✅ ADD THESE DEBUG LOGS
            // console.log(`🔍 RAW chatHistory:`, projectData.chatHistory);
            // console.log(
            //   `🔍 RAW currentFeatureTree:`,
            //   projectData.currentFeatureTree
            // );

            const loadedChatMessages = projectData.chatHistory
              ? unflattenFromFirebase(projectData.chatHistory)
              : [];
            const loadedFeatureTree = projectData.currentFeatureTree
              ? unflattenFromFirebase(projectData.currentFeatureTree)
              : [];
            const loadedCommandHistory = projectData.commandHistory
              ? unflattenFromFirebase(projectData.commandHistory)
              : [];

            // ✅ ADD THESE DEBUG LOGS
            // console.log(
            //   `🔍 AFTER UNFLATTEN - chatMessages:`,
            //   loadedChatMessages
            // );
            // console.log(`🔍 AFTER UNFLATTEN - featureTree:`, loadedFeatureTree);

            (versionControl as any).state.timelines["main"] = {
              featureTree: loadedFeatureTree,
              chatMessages: loadedChatMessages,
              bodyCounter: 0,
              tipFeature: null,
              commandHistory: loadedCommandHistory,
            };
          }

          if (projectData.currentTimelineId) {
            (versionControl as any).state.currentTimelineId =
              projectData.currentTimelineId;
          }

          if (projectData.timelineBranchMap) {
            setTimelineBranchMap(projectData.timelineBranchMap);
          }

          // Restore version pointers
          if (
            projectData.versionedOperations &&
            Object.keys(projectData.versionedOperations).length > 0
          ) {
            const savedVersionedOps = unflattenFromFirebase(
              projectData.versionedOperations,
            );

            for (const [msgId, savedOp] of Object.entries(savedVersionedOps)) {
              const messageId = parseInt(msgId);
              const currentOp = (versionControl as any).state
                .versionedOperations[messageId];

              if (currentOp && (savedOp as any).currentVersion !== undefined) {
                const savedVersion = (savedOp as any).currentVersion;
                currentOp.currentVersion = savedVersion;
              }
            }

            (versionControl as any).__pointersRestoredFromSave = true;
          }

          // Get the snapshot to restore
          const currentTimelineId =
            (versionControl as any).state.currentTimelineId || "main";
          const currentTimeline = (versionControl as any).state.timelines[
            currentTimelineId
          ];

          if (!currentTimeline) {
            throw new Error(`Timeline ${currentTimelineId} not found`);
          }

          const snapshotToUse = currentTimeline;
          const finalChatMessages = snapshotToUse.chatMessages || [];
          const finalFeatureTree = snapshotToUse.featureTree || [];

          // console.log(
          //   `📦 LOAD: Restoring ${finalChatMessages.length} messages and ${finalFeatureTree.length} features`
          // );

          // Set feature tree FIRST
          if (finalFeatureTree.length > 0) {
            setFeatureTree([...finalFeatureTree]);
            const bodyIds = finalFeatureTree.map((body: TreeItem) => body.id);
            setExpandedItems(bodyIds);

            // Rebuild primitive counters
            const newCounters = {
              Box: 0,
              Cylinder: 0,
              Sphere: 0,
              Cone: 0,
              Torus: 0,
              Ellipsoid: 0,
              Prism: 0,
              Wedge: 0,
            };

            const countPrimitives = (items: TreeItem[]) => {
              items.forEach((item) => {
                const displayName = item.displayName || item.name;
                const match = displayName.match(
                  /^(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)(\d+)$/,
                );
                if (match) {
                  const primitiveType = match[1];
                  const number = parseInt(match[2], 10);
                  if (primitiveType in newCounters) {
                    newCounters[primitiveType as keyof typeof newCounters] =
                      Math.max(
                        newCounters[primitiveType as keyof typeof newCounters],
                        number,
                      );
                  }
                }
                if (item.children) {
                  countPrimitives(item.children);
                }
              });
            };

            countPrimitives(finalFeatureTree);
            primitiveCountersRef.current = newCounters;

            // console.log(
            //   `✅ LOAD: Set ${finalFeatureTree.length} features in left sidebar`
            // );
          } else {
            setFeatureTree([]);
            setExpandedItems([]);
          }

          // Mark project load time
          (window as any).__projectLoadTime = Date.now();

          // THEN set chat messages
          if (rightSidebarRef.current && finalChatMessages.length > 0) {
            rightSidebarRef.current.setChatMessages([]);
            await new Promise((resolve) => setTimeout(resolve, 100));
            rightSidebarRef.current.setChatMessages([...finalChatMessages]);
            // console.log(
            //   `✅ LOAD: Set ${finalChatMessages.length} chat messages`
            // );
          } else {
            if (rightSidebarRef.current) {
              rightSidebarRef.current.setChatMessages([]);
            }
          }

          setCurrentBranch(userBranchId);
          // console.log(
          //   `✅ LOAD: Duplicated example loaded with branch ${userBranchId}`
          // );

          // 🔧 CRITICAL FIX: Restore command history to ref for duplicated example
          const loadedCommandHistory = snapshotToUse.commandHistory || [];
          commandHistoryRef.current[id] = [...loadedCommandHistory];
          // console.log(
          //   `✅ LOAD: Restored ${loadedCommandHistory.length} commands to ref for duplicated example ${id}`
          // );

          // Create tab
          setOpenModels((prev) => {
            const exists = prev.some((m) => m.id === id);
            if (exists) {
              return prev.map((model) =>
                model.id === id
                  ? {
                      ...model,
                      name: projectData.name || "Untitled",
                      geometry: geometryForRender || [],
                      featureTree: finalFeatureTree,
                      chatMessages: finalChatMessages,
                      commandHistory: snapshotToUse.commandHistory || [],
                      branchId: userBranchId,
                      isSharedModel: false,
                    }
                  : model,
              );
            }

            return [
              ...prev,
              {
                id: id,
                name: projectData.name || "Untitled",
                geometry: geometryForRender || [],
                featureTree: finalFeatureTree,
                chatMessages: finalChatMessages,
                commandHistory: snapshotToUse.commandHistory || [],
                versionControl: null,
                branchId: userBranchId,
                isSharedModel: false,
                cameraState: {
                  rotation: { x: -30, y: -45 },
                  distance: 50,
                  isOrthographic: false,
                },
                primitiveCounters: { ...primitiveCountersRef.current },
              },
            ];
          });

          setActiveModelId(id);

          // Set colors
          const loadedColor = projectData.modelColor || DEFAULT_COLORS.dark;
          const loadedTransparency =
            projectData.transparencyValue !== undefined
              ? projectData.transparencyValue
              : 100;

          setColorValue(loadedColor);
          setTransparencyValue(loadedTransparency);
          sessionStorage.setItem(`project_${id}_color`, loadedColor);
          sessionStorage.setItem(
            `project_${id}_transparency`,
            loadedTransparency.toString(),
          );

          // Auto-switch sidebars if needed

          setIsViewLoading(false);

          // Set geometry
          if (geometryForRender.length > 0) {
            (window as any).__forceFirstGeometryLoad = true;
            (window as any).__resetCameraForNewProject = true;

            const newGeometry = geometryForRender.map((g: any) => ({
              ...g,
              _forceUpdate: `${Date.now()}_${Math.random()}`,
              _autoFit: true,
            }));

            handleShapeCreated(newGeometry, undefined, true);
            // console.log(`✅ LOAD: Set ${newGeometry.length} geometry items`);
          }

          // Before each onToolbarExpandChange(false) call, add:
          console.log("🔍 About to collapse toolbar:", {
            hasCallback: !!onToolbarExpandChange,
            callbackType: typeof onToolbarExpandChange,
          });

          // console.log(`✅ LOAD: Duplicated example fully loaded`);
          // ✅ ADD THIS: Collapse toolbar after duplicated example loads
          // if (onToolbarExpandChange) {
          //   onToolbarExpandChange(false);
          // }
          return; // Exit here for duplicated examples
        }

        // Rest of existing loadProject code for normal projects continues here...
        if (projectData.fcstdFile) {
          const userBranchId = user
            ? `user_${user.uid}_${id}_${Date.now()}`
            : `guest_${id}_${Date.now()}`;
          // console.log(`📦 LOAD: Using branch: ${userBranchId}`);

          // Initialize fresh branch
          // console.log("📦 LOAD: Initializing branch...");
          const clearStartTime = Date.now();
          const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "CLEAR_DOCUMENT",
              context: {},
              branch_id: userBranchId,
            }),
            signal: AbortSignal.timeout(64000),
          });
          // console.log(
          //   `📦 LOAD: Clear request took ${
          //     Date.now() - clearStartTime
          //   }ms, status: ${clearResponse.status}`
          // );

          if (!clearResponse.ok) {
            throw new Error(
              `Failed to initialize branch: HTTP ${clearResponse.status}`,
            );
          }

          const clearResult = await clearResponse.json();
          // console.log(`📦 LOAD: Clear result:`, clearResult);

          if (!clearResult.success) {
            throw new Error(
              `Branch init failed: ${clearResult.message || "Unknown error"}`,
            );
          }

          // console.log("✅ LOAD: Branch initialized");

          // Poll for readiness
          let isReady = false;
          let attempts = 0;
          const maxAttempts = 20;

          // console.log(
          //   `📦 LOAD: Polling for branch readiness (max ${maxAttempts} attempts)...`
          // );
          while (!isReady && attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            attempts++;

            try {
              const checkResponse = await fetch(`${BACKEND_URL}/api/command`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_input: "DEBUG_DOCUMENT_STATE",
                  context: {},
                  branch_id: userBranchId,
                }),
                signal: AbortSignal.timeout(15000),
              });

              if (checkResponse.ok) {
                const result = await checkResponse.json();
                if (result.success) {
                  // console.log(`✅ LOAD: Branch ready after ${attempts * 2}s`);
                  isReady = true;
                } else {
                  // console.log(
                  //   `⏳ LOAD: Branch not ready (attempt ${attempts}/${maxAttempts})...`
                  // );
                }
              } else {
                // console.log(
                //   `⏳ LOAD: Check failed with status ${checkResponse.status} (attempt ${attempts}/${maxAttempts})...`
                // );
              }
            } catch (error) {
              // console.log(
              //   `⏳ LOAD: Branch check error (attempt ${attempts}/${maxAttempts}):`,
              //   error instanceof Error ? error.message : error
              // );
            }
          }

          if (!isReady) {
            throw new Error("Branch initialization timed out after 40s");
          }

          // Import file
          // console.log("📦 LOAD: Decoding file from base64...");
          const binaryData = atob(projectData.fcstdFile);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            bytes[i] = binaryData.charCodeAt(i);
          }
          // console.log(`📦 LOAD: File decoded, size: ${bytes.length} bytes`);

          const formData = new FormData();
          formData.append(
            "file",
            new Blob([bytes], { type: "application/octet-stream" }),
            "project.FCStd",
          );
          formData.append("branch_id", userBranchId);

          // console.log("📦 LOAD: Uploading file to backend...");
          const importStartTime = Date.now();

          try {
            const importResponse = await fetch(
              `${BACKEND_URL}/api/command/import-file`,
              {
                method: "POST",
                credentials: "include",
                body: formData,
                signal: AbortSignal.timeout(300000), // 2 minutes
              },
            );

            const importDuration = Date.now() - importStartTime;
            // console.log(
            //   `📦 LOAD: Import request completed in ${importDuration}ms, status: ${importResponse.status}`
            // );

            if (!importResponse.ok) {
              throw new Error(`Import failed: HTTP ${importResponse.status}`);
            }

            const importResult = await importResponse.json();
            // console.log(`📦 LOAD: Import result:`, importResult);

            if (!importResult.success) {
              throw new Error(`Import failed: ${importResult.message}`);
            }

            // console.log("✅ LOAD: File imported successfully");
          } catch (importError) {
            // console.error("❌ LOAD: Import failed:", importError);
            if (
              importError instanceof Error &&
              importError.name === "TimeoutError"
            ) {
              throw new Error(
                "File import timed out - file may be too large or backend is overloaded",
              );
            }
            throw importError;
          }

          // Poll for backend to fully process import
          // console.log("📦 LOAD: Waiting for backend to process import...");

          let backendReady = false;
          let stabilizeAttempts = 0;
          const maxStabilizeAttempts = 60; // 2 minutes max

          while (!backendReady && stabilizeAttempts < maxStabilizeAttempts) {
            stabilizeAttempts++;
            await new Promise((resolve) => setTimeout(resolve, 2000));

            try {
              const checkResponse = await fetch(`${BACKEND_URL}/api/command`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_input: "DEBUG_DOCUMENT_STATE",
                  context: {},
                  branch_id: userBranchId,
                }),
                signal: AbortSignal.timeout(20000),
              });

              if (checkResponse.ok) {
                const result = await checkResponse.json();
                const bodyCount = result.debug_data?.bodies?.length || 0;
                const isRecomputing =
                  result.debug_data?.is_recomputing || false;

                // console.log(
                //   `📦 LOAD: Stabilize check ${stabilizeAttempts}/${maxStabilizeAttempts}: ${bodyCount} bodies, recomputing: ${isRecomputing}`
                // );

                if (bodyCount > 0 && !isRecomputing) {
                  // console.log(
                  //   `✅ LOAD: Backend ready with ${bodyCount} bodies after ${
                  //     stabilizeAttempts * 2
                  //   }s`
                  // );
                  backendReady = true;
                  break;
                }
              } else {
                // console.log(
                //   `📦 LOAD: Stabilize check ${stabilizeAttempts}/${maxStabilizeAttempts}: HTTP ${checkResponse.status}`
                // );
              }
            } catch (error) {
              // console.log(
              //   `📦 LOAD: Stabilize check ${stabilizeAttempts}/${maxStabilizeAttempts}:`,
              //   error instanceof Error ? error.message : error
              // );
            }
          }

          if (!backendReady) {
            // console.warn(
            //   `⚠️ LOAD: Backend didn't stabilize after ${
            //     maxStabilizeAttempts * 2
            //   }s, proceeding anyway...`
            // );
          }

          // Get geometry with retry
          // console.log("📦 LOAD: Fetching geometry from backend...");
          let geometryResult = null;
          let retries = 0;
          const maxRetries = 20;

          while (retries < maxRetries) {
            // console.log(
            //   `📦 LOAD: Geometry attempt ${retries + 1}/${maxRetries}...`
            // );

            try {
              // First check document state
              const debugResponse = await fetch(`${BACKEND_URL}/api/command`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_input: "DEBUG_DOCUMENT_STATE",
                  context: {},
                  branch_id: userBranchId,
                }),
                signal: AbortSignal.timeout(20000),
              });

              if (debugResponse.ok) {
                const debugResult = await debugResponse.json();
                const bodyCount = debugResult.debug_data?.bodies?.length || 0;

                // console.log(
                //   `📦 LOAD: Document has ${bodyCount} bodies (debug check)`
                // );

                if (bodyCount === 0) {
                  // console.log(`⚠️ LOAD: No bodies in document yet, waiting...`);
                  retries++;
                  if (retries < maxRetries) {
                    await new Promise((resolve) => setTimeout(resolve, 4000));
                  }
                  continue;
                }
              } else {
                // console.log(
                //   `⚠️ LOAD: Debug check failed with status ${debugResponse.status}`
                // );
              }

              // Now get geometry
              const geometryStartTime = Date.now();
              const geometryResponse = await fetch(
                `${BACKEND_URL}/api/command`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    user_input: "refresh_geometry",
                    context: {},
                    branch_id: userBranchId,
                  }),
                  signal: AbortSignal.timeout(300000), // 2 minutes
                },
              );

              const geometryDuration = Date.now() - geometryStartTime;
              // console.log(
              //   `📦 LOAD: Geometry request took ${geometryDuration}ms, status: ${geometryResponse.status}`
              // );

              if (geometryResponse.ok) {
                const result = await geometryResponse.json();

                // console.log(
                //   `📦 LOAD: Geometry response - success: ${
                //     result?.success
                //   }, count: ${result?.geometry?.length || 0}`
                // );

                if (result?.geometry?.length > 0) {
                  geometryResult = result;
                  // console.log(
                  //   `✅ LOAD: Got ${result.geometry.length} geometry items`
                  // );
                  break;
                } else {
                  // console.log(`⚠️ LOAD: Empty geometry, retrying...`);
                }
              } else {
                // console.log(
                //   `⚠️ LOAD: Bad response (${geometryResponse.status}), retrying...`
                // );
              }
            } catch (error) {
              if (error instanceof Error && error.name === "TimeoutError") {
                // console.log(
                //   `⏰ LOAD: Timeout on attempt ${
                //     retries + 1
                //   }, backend is slow...`
                // );
              } else {
                // console.log(
                //   `⚠️ LOAD: Request failed:`,
                //   error instanceof Error ? error.message : error
                // );
              }
            }

            retries++;
            if (retries < maxRetries) {
              const delay = Math.min(3000 + retries * 1000, 10000);
              // console.log(`⏳ LOAD: Waiting ${delay}ms before retry...`);
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }

          // Apply geometry results
          // Apply geometry results
          // Around line 1650 - where we set geometry after loading project:
          if (geometryResult?.geometry?.length > 0) {
            const newGeometry = geometryResult.geometry.map((g: any) => ({
              ...g,
              _forceUpdate: `${Date.now()}_${Math.random()}`,
              _autoFit: true, // ← ADD THIS FLAG
            }));

            // Use handleShapeCreated with bypass flag to show geometry immediately
            handleShapeCreated(newGeometry, undefined, true);
            // console.log(
            //   `✅ LOAD: Set ${newGeometry.length} geometry items (will auto-fit)`
            // );
          } else {
            // console.warn(
            //   "⚠️ LOAD: No geometry after retries - will load from saved state"
            // );
            setNoahGeometry([]);
          }

          // 🔒 CRITICAL: DON'T build tree from backend objects - we have saved tree!
          // console.log(
          //   `📦 LOAD: Geometry loaded, tree will be set from saved snapshot`
          // );

          // Restore state from saved data
          // console.log("📦 LOAD: Restoring project state from saved data...");

          // Load version control structures
          if (
            projectData.versionedOperations &&
            Object.keys(projectData.versionedOperations).length > 0
          ) {
            // console.log(
            //   `📦 LOAD: Restoring ${
            //     Object.keys(projectData.versionedOperations).length
            //   } versioned operations`
            // );
            const unflattened = unflattenFromFirebase(
              projectData.versionedOperations,
            );

            Object.entries(unflattened).forEach(
              ([msgId, versionedOp]: [string, any]) => {
                versionControl.restoreVersionedOperation(
                  parseInt(msgId),
                  versionedOp,
                );

                versionedOp.versions.forEach((version: any) => {
                  if (version.timelineId && version.snapshot) {
                    (versionControl as any).state.timelines[
                      version.timelineId
                    ] = {
                      featureTree: version.snapshot.featureTree || [],
                      chatMessages: version.snapshot.chatMessages || [],
                      bodyCounter: version.snapshot.bodyCounter || 0,
                      tipFeature: version.snapshot.tipFeature || null,
                      commandHistory: version.snapshot.commandHistory || [],
                    };
                    // console.log(
                    //   `✅ LOAD: Restored timeline ${version.timelineId}`
                    // );
                  }
                });
              },
            );
          } else {
            // console.log(
            //   `📦 LOAD: No versioned operations, populating main timeline from saved data`
            // );

            const loadedChatMessages = projectData.chatHistory
              ? unflattenFromFirebase(projectData.chatHistory)
              : [];
            const loadedFeatureTree = projectData.currentFeatureTree
              ? unflattenFromFirebase(projectData.currentFeatureTree)
              : [];
            const loadedCommandHistory = projectData.commandHistory
              ? unflattenFromFirebase(projectData.commandHistory)
              : [];

            (versionControl as any).state.timelines["main"] = {
              featureTree: loadedFeatureTree,
              chatMessages: loadedChatMessages,
              bodyCounter: 0,
              tipFeature: null,
              commandHistory: loadedCommandHistory,
            };

            // console.log(
            //   `✅ LOAD: Populated main timeline with ${loadedChatMessages.length} messages and ${loadedFeatureTree.length} features`
            // );
          }

          if (projectData.currentTimelineId) {
            // console.log(
            //   `📦 LOAD: Setting active timeline: ${projectData.currentTimelineId}`
            // );
            (versionControl as any).state.currentTimelineId =
              projectData.currentTimelineId;
          }

          if (projectData.timelineBranchMap) {
            // console.log("📦 LOAD: Restoring timeline-branch map");
            setTimelineBranchMap(projectData.timelineBranchMap);
          }

          // console.log(`🔧 LOAD: Restoring version pointers from saved data...`);

          if (
            projectData.versionedOperations &&
            Object.keys(projectData.versionedOperations).length > 0
          ) {
            const savedVersionedOps = unflattenFromFirebase(
              projectData.versionedOperations,
            );

            for (const [msgId, savedOp] of Object.entries(savedVersionedOps)) {
              const messageId = parseInt(msgId);
              const currentOp = (versionControl as any).state
                .versionedOperations[messageId];

              if (currentOp && (savedOp as any).currentVersion !== undefined) {
                const savedVersion = (savedOp as any).currentVersion;
                currentOp.currentVersion = savedVersion;

                // console.log(
                //   `✅ LOAD: Restored operation ${messageId} to version ${
                //     savedVersion + 1
                //   }/${currentOp.versions.length}`
                // );
              }
            }

            (versionControl as any).__pointersRestoredFromSave = true;

            // console.log(
            //   `✅ LOAD: All version pointers restored from save data`
            // );
          }

          const currentTimelineId =
            (versionControl as any).state.currentTimelineId || "main";

          // console.log(
          //   `📦 LOAD: Getting snapshot from timeline: ${currentTimelineId}`
          // );

          const currentTimeline = (versionControl as any).state.timelines[
            currentTimelineId
          ];

          if (!currentTimeline) {
            // console.error(
            //   `❌ LOAD: No timeline found for ${currentTimelineId}`
            // );
            throw new Error(`Timeline ${currentTimelineId} not found`);
          }

          const snapshotToUse = currentTimeline;

          // console.log(
          //   `📦 LOAD: Using timeline snapshot with ${
          //     snapshotToUse.chatMessages?.length || 0
          //   } messages and ${snapshotToUse.featureTree?.length || 0} features`
          // );

          // 🔧 Set UI from snapshot - with proper sequencing
          const finalChatMessages = snapshotToUse.chatMessages || [];
          const finalFeatureTree = snapshotToUse.featureTree || [];

          // console.log(
          //   `📦 LOAD: Restoring ${finalChatMessages.length} chat messages and ${finalFeatureTree.length} features from snapshot`
          // );

          // CRITICAL: Set feature tree FIRST (before chat messages)
          if (finalFeatureTree.length > 0) {
            setFeatureTree([...finalFeatureTree]);
            const bodyIds = finalFeatureTree.map((body: TreeItem) => body.id);
            setExpandedItems(bodyIds);

            // Rebuild primitive counters from loaded tree
            const newCounters = {
              Box: 0,
              Cylinder: 0,
              Sphere: 0,
              Cone: 0,
              Torus: 0,
              Ellipsoid: 0,
              Prism: 0,
              Wedge: 0,
            };

            const countPrimitives = (items: TreeItem[]) => {
              items.forEach((item) => {
                const displayName = item.displayName || item.name;
                const match = displayName.match(
                  /^(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)(\d+)$/,
                );
                if (match) {
                  const primitiveType = match[1];
                  const number = parseInt(match[2], 10);
                  if (
                    primitiveType in newCounters &&
                    newCounters[primitiveType as keyof typeof newCounters] !==
                      undefined
                  ) {
                    newCounters[primitiveType as keyof typeof newCounters] =
                      Math.max(
                        newCounters[primitiveType as keyof typeof newCounters],
                        number,
                      );
                  }
                }
                if (item.children) {
                  countPrimitives(item.children);
                }
              });
            };

            countPrimitives(finalFeatureTree);
            primitiveCountersRef.current = newCounters;

            // console.log(
            //   `✅ LOAD: Set ${finalFeatureTree.length} features in left sidebar`
            // );
          } else {
            // console.log(
            //   `📦 LOAD: No features in snapshot, clearing left sidebar`
            // );
            setFeatureTree([]);
            setExpandedItems([]);
          }

          // Mark when project finished loading (for handleChatMessagesChange guard)
          (window as any).__projectLoadTime = Date.now();

          // THEN set chat messages (after feature tree is stable)
          // ✅ CRITICAL: Clear loading states BEFORE setting messages
          setIsViewLoading(false);
          setIsLoading(false);

          // THEN set chat messages (after loading is false)
          if (rightSidebarRef.current && finalChatMessages.length > 0) {
            // Clear first to ensure clean state
            rightSidebarRef.current.setChatMessages([]);

            // ✅ UPDATED: Wait for React to process the clear
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Now set the actual messages
            rightSidebarRef.current.setChatMessages([...finalChatMessages]);
          } else {
            if (rightSidebarRef.current) {
              rightSidebarRef.current.setChatMessages([]);
            }
          }

          setCurrentBranch(userBranchId);
          // console.log(
          //   `✅ LOAD: Successfully loaded project with branch ${userBranchId}`
          // );

          // 🔧 CRITICAL FIX: Restore command history to ref when loading project
          const loadedCommandHistory = snapshotToUse.commandHistory || [];
          commandHistoryRef.current[id] = [...loadedCommandHistory];
          // console.log(
          //   `✅ LOAD: Restored ${loadedCommandHistory.length} commands to ref for project ${id}`
          // );
          if (loadedCommandHistory.length > 0) {
            // console.log(
            //   `   First command: ${loadedCommandHistory[0].userInput}`
            // );
          }

          // ========== ADD tab FIRST ==========
          // After successfully loading the project, when creating the tab:
          setOpenModels((prev) => {
            const exists = prev.some((m) => m.id === id);
            if (exists) {
              return prev.map((model) =>
                model.id === id
                  ? {
                      ...model,
                      name: projectData.name || "Untitled",
                      geometry: geometryResult?.geometry || [],
                      featureTree: finalFeatureTree,
                      chatMessages: finalChatMessages,
                      commandHistory: snapshotToUse.commandHistory || [],
                      branchId: userBranchId,
                      isSharedModel: id === projectId && isActuallySharedView, // ← FIX: Same here
                    }
                  : model,
              );
            }

            return [
              ...prev,
              // Around line 3490 in loadProject, when creating a new tab:
              {
                id: id,
                name: projectData.name || "Untitled",
                geometry: geometryResult?.geometry || [],
                featureTree: finalFeatureTree,
                chatMessages: finalChatMessages,
                commandHistory: snapshotToUse.commandHistory || [],
                versionControl: null,
                branchId: userBranchId,
                isSharedModel: id === projectId && isActuallySharedView,
                cameraState: {
                  rotation: { x: -30, y: -45 },
                  distance: 50, // Changed from 100 to 50 for better initial fit
                  isOrthographic: false,
                },
              },
            ];
          });
          setActiveModelId(id);

          // ✅ NOW set color/transparency AFTER switching to this tab
          const loadedColor =
            projectData.modelColor ||
            (document.documentElement.classList.contains("dark")
              ? DEFAULT_COLORS.dark
              : DEFAULT_COLORS.light);
          const loadedTransparency =
            projectData.transparencyValue !== undefined
              ? projectData.transparencyValue
              : 100;

          setColorValue(loadedColor);
          setTransparencyValue(loadedTransparency);
          sessionStorage.setItem(`project_${id}_color`, loadedColor);
          sessionStorage.setItem(
            `project_${id}_transparency`,
            loadedTransparency.toString(),
          );

          // console.log(
          //   `🎨 LOAD: Set color ${loadedColor} for newly loaded project ${id}`
          // );

          // ✅ FIX: Do auto-switch BEFORE setting isViewLoading to false

          // ✅ CRITICAL: Set loading to false BEFORE setting geometry
          setIsViewLoading(false);
          // console.log(
          //   `✅ LOAD: Loading state cleared, now setting geometry...`
          // );

          // ✅ ADD THIS: Collapse toolbar after successful load
          // if (onToolbarExpandChange) {
          //   onToolbarExpandChange(false);
          // }

          // ✅ ADD THIS: Force View to treat this as first load for camera fitting
          // ✅ CRITICAL: Mark that this is a fresh project load (not a tab switch)
          (window as any).__forceFirstGeometryLoad = true;
          (window as any).__resetCameraForNewProject = true; // ✅ ADD THIS FLAG

          // NOW set geometry AFTER loading is false
          if (geometryResult?.geometry?.length > 0) {
            const newGeometry = geometryResult.geometry.map((g: any) => ({
              ...g,
              _forceUpdate: `${Date.now()}_${Math.random()}`,
              _autoFit: true, // Auto-fit on first load from sidebar
            }));

            // Use handleShapeCreated with bypass flag to show geometry immediately
            handleShapeCreated(newGeometry, undefined, true);
            // console.log(
            //   `✅ LOAD: Set ${newGeometry.length} geometry items (will auto-fit on first load)`
            // );
          } else {
            console.warn(
              "⚠️ LOAD: No geometry after retries - will load from saved state",
            );
            setNoahGeometry([]);
          }

          // Around line 3570, replace the onSharedModelLoaded call:
          // ✅ ONLY close toolbar if we successfully loaded geometry
          // ✅ ONLY close toolbar if we successfully loaded geometry
          // ✅ Don't close toolbar here at all - it's not the right place
          console.log("🎯 CHECKPOINT 1: Reached end of main file load");

          if (onSharedModelLoaded && isActuallySharedView && id === projectId) {
            console.log("🔥 CALLING onSharedModelLoaded callback");
            onSharedModelLoaded();
          }

          // ✅ REMOVED: Don't collapse toolbar here
          // The toolbar should only collapse when geometry is actually rendered in View
          console.log(
            "⏸️ SKIPPING toolbar collapse - let View handle it after render",
          );
        } else {
          // console.log(`📦 LOAD: No Noah file found, loading empty project`);

          // ← NEW: Create a branch even for empty projects
          const userBranchId = user
            ? `user_${user.uid}_${id}_${Date.now()}`
            : `guest_${id}_${Date.now()}`;

          // ← NEW: Initialize the branch
          const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "CLEAR_DOCUMENT",
              context: {},
              branch_id: userBranchId,
            }),
            signal: AbortSignal.timeout(60000),
          });

          if (!clearResponse.ok) {
            console.warn("Failed to initialize branch for empty project");
          }

          setNoahGeometry([]);
          setFeatureTree([]);
          setExpandedItems([]);
          setIsViewLoading(false);

          // ✅ CREATE TAB FOR EMPTY PROJECT TOO
          // For empty projects without Noah file:
          // Around line 1980 - when creating tab for empty project:
          setOpenModels([
            {
              id: id,
              name: projectData.name || "Untitled",
              geometry: [],
              featureTree: [],
              chatMessages: [],
              commandHistory: [],
              versionControl: null,
              branchId: userBranchId,
              isSharedModel: isActuallySharedView && id === projectId,
              primitiveCounters: {
                // ← ADD THIS
                Box: 0,
                Cylinder: 0,
                Sphere: 0,
                Cone: 0,
                Torus: 0,
                Ellipsoid: 0,
                Prism: 0,
                Wedge: 0,
              },
            },
          ]);

          setActiveModelId(id);
          setCurrentBranch(userBranchId); // ← SET AS ACTIVE
          // console.log(
          //   `✅ Created tab for empty project: ${projectData.name} with branch: ${userBranchId}`
          // );

          // if (onToolbarExpandChange) {
          //   onToolbarExpandChange(false);
          // }
        }
      } catch (error) {
        console.error("❌ LOAD: Load failed:", error);
        setNoahGeometry([]);
        setFeatureTree([]);
        setExpandedItems([]);

        let errorMsg = "Failed to load project";
        if (error instanceof Error) {
          if (error.name === "TimeoutError") {
            errorMsg =
              "Backend timed out - the server may be overloaded. Please try again in a moment.";
          } else {
            errorMsg = error.message;
          }
        }

        console.error(`❌ LOAD: Final error: ${errorMsg}`);
        onBackToProjects();

        // Set loading to false on error
        setIsViewLoading(false);

        // ✅ IMPORTANT: Don't close toolbar on error - let user try again
        console.log("⚠️ Load failed - keeping toolbar open");
      } finally {
        setIsLoading(false);
        loadingRef.current = null;
        // REMOVED: setIsViewLoading(false); ← Don't set this here!
        // console.log(`📦 LOAD: Load process finished (success or failure)`);
      }
    },
    [
      user,
      onBackToProjects,
      versionControl,
      getFeatureTreeFromChatMessages,
      userHasTouchedSidebars,
      hasAutoSwitchedToProject,
      onToolbarExpandChange, // ✅ ADD THIS DEPENDENCY
    ], // ← ADD THE TWO NEW DEPS
  );

  const handleExampleSelect = useCallback(
    async (exampleId: string) => {
      if (!user) {
        return;
      }

      try {
        setLoadingProjectId(exampleId);
        setIsLoading(true);
        setIsViewLoading(true);

        const exampleDoc = await getDoc(doc(db, "projects", exampleId));
        if (!exampleDoc.exists()) {
          throw new Error("Example not found");
        }

        const exampleData = exampleDoc.data();
        const newProjectName = exampleData.name;
        const now = new Date();

        // Calculate old date to preserve position
        const preservedDate = exampleData.updatedAt
          ? new Date(
              exampleData.updatedAt.toDate
                ? exampleData.updatedAt.toDate()
                : exampleData.updatedAt,
            )
          : now;

        const copiedProjectData: any = {
          name: newProjectName,
          createdAt: now,
          updatedAt: preservedDate.toISOString(),
          userId: user.uid,
          copiedFromExample: exampleId,
          isPublic: false,
          shareToken: null,
        };

        // ... deep copy fields logic (same as before) ...
        const fieldsToSkip = [
          "name",
          "createdAt",
          "updatedAt",
          "userId",
          "isPublic",
          "shareToken",
        ];
        Object.keys(exampleData).forEach((key) => {
          if (!fieldsToSkip.includes(key) && exampleData[key] !== undefined) {
            if (Array.isArray(exampleData[key])) {
              copiedProjectData[key] = JSON.parse(
                JSON.stringify(exampleData[key]),
              );
            } else if (
              typeof exampleData[key] === "object" &&
              exampleData[key] !== null
            ) {
              copiedProjectData[key] = JSON.parse(
                JSON.stringify(exampleData[key]),
              );
            } else {
              copiedProjectData[key] = exampleData[key];
            }
          }
        });

        const newProjectRef = await addDoc(
          collection(db, "projects"),
          copiedProjectData,
        );

        setLoadingProjectId(newProjectRef.id);

        // Update local ref for instant UI update
        localHiddenExampleIds.current.add(exampleId);

        // Persist to Firestore
        try {
          await updateDoc(doc(db, "users", user.uid), {
            hiddenExampleIds: arrayUnion(exampleId),
          });
        } catch (err) {
          console.warn("Failed to update hidden examples:", err);
        }

        setOptimisticProject({
          id: newProjectRef.id,
          name: newProjectName,
          updatedAt: preservedDate,
        });

        // ✅ Update sidebar locally
        setSidebarProjects((prev) => {
          const filtered = prev.filter((p) => p.id !== exampleId);

          const newProject = {
            id: newProjectRef.id,
            name: newProjectName,
            createdAt: now,
            updatedAt: preservedDate,
            userId: user.uid,
            thumbnail: exampleData.thumbnail,
            description: exampleData.description,
            isPublic: false,
            copiedFromExample: exampleId,
          };

          const newList = [...filtered, newProject];
          newList.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
          return newList;
        });

        // 🔴 REMOVED: refreshSidebarProjects();
        // Reason: The database is "stale" for a few milliseconds after write.
        // Calling this immediately overwrites our correct local state with
        // old data, causing items to flicker or disappear.

        sessionStorage.setItem(
          `project_${newProjectRef.id}_isDuplicatedExample`,
          "true",
        );

        await loadProject(newProjectRef.id);
      } catch (error) {
        console.error("Failed to create copy from example:", error);
      } finally {
        setLoadingProjectId(null);
        setIsLoading(false);
        setIsViewLoading(false);
      }
    },
    [user, loadProject, onToolbarExpandChange], // Removed refreshSidebarProjects from deps
  );

  useEffect(() => {
    if (user?.uid && projectId && consistentBranchId !== "main") {
      setCurrentBranch(consistentBranchId);
      // console.log(`Set consistent branch ID: ${consistentBranchId}`);
    }
  }, [user?.uid, projectId, consistentBranchId]);

  // 🔧 NEW: Keep ref in sync with feature tree
  useEffect(() => {
    featureTreeRef.current = featureTree;
  }, [featureTree]);

  // Load project on mount - with duplicate call protection
  const hasLoadedRef = useRef<string | null>(null);

  // Global Key Handler for Cmd+K (Focus Chat)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd+K: Focus Chat Input
      if (modKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        e.stopPropagation();

        // 1. Ensure Sidebar is Open
        if (!isRightSidebarOpen) {
          setIsRightSidebarOpen(true);

          // If on mobile, close others to respect single-column rule
          if (window.innerWidth < 1280) {
            setIsLeftSidebarOpen(false);
            setIsModelsSidebarOpen(false);
          }
        }

        // 2. Focus Input (Small delay to allow render if it was hidden)
        setTimeout(() => {
          if (rightSidebarRef.current) {
            rightSidebarRef.current.focusInput();
          }
        }, 10);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isRightSidebarOpen]);

  // Around line 1950 in CAD.tsx, update the useEffect that loads projects:
  // Around line 1950 in CAD.tsx, replace the useEffect with:
  useEffect(() => {
    if (!projectId) {
      if (openModels.length === 0 && !loadingProjectId) {
        setIsLoading(false);
        setIsViewLoading(false);
        setFileName("");
        setNoahGeometry([]);
        setFeatureTree([]);
        setHasProject(false);
      }
      return;
    }

    const loadKey = `${projectId}_${user?.uid}`;

    if (
      projectId &&
      user &&
      hasLoadedRef.current !== loadKey &&
      !loadingProjectId
    ) {
      loadProject(projectId)
        .then(() => {
          hasLoadedRef.current = loadKey;

          // ✅ REMOVED: Don't collapse here - let loadProject handle it
          // if (onToolbarExpandChange) {
          //   onToolbarExpandChange(false);
          // }
        })
        .catch((error) => {
          console.error(`❌ Load failed for key: ${loadKey}`, error);
        });
    }
  }, [projectId, user, loadProject, openModels.length, loadingProjectId]); // ✅ REMOVED onToolbarExpandChange from deps
  // Add back button handler
  const handleBackToProjects = useCallback(async () => {
    // If in shared view, navigate to home instead of projects
    if (isSharedView) {
      window.location.href = "/home";
      return;
    }

    try {
      await saveProjectAsNoahFile();
    } catch (error) {
      // console.warn("Save failed on exit:", error);
    } finally {
      // Always navigate back, even if save fails
      onBackToProjects();
    }
  }, [saveProjectAsNoahFile, onBackToProjects, isSharedView]);

  const toggleLeftSidebar = () => {
    const newState = !isLeftSidebarOpen;
    setIsLeftSidebarOpen(newState);
    setUserHasTouchedSidebars(true); // ← ADD THIS LINE

    // On mobile, show collapsed project sidebar when left sidebar opens
    if (isMobile) {
      setForceShowProjectSidebar(newState);
      // Reset expanded state when toggling
      if (!newState) {
        setIsProjectSidebarExpanded(false);
      }
    }
  };

  const toggleRightSidebar = () => {
    const newState = !isRightSidebarOpen;
    setIsRightSidebarOpen(newState);
    // Don't mark right sidebar as "touched" since it's the chat
    // We only care about left sidebar (feature tree) and models sidebar

    // On mobile, when opening right sidebar, close both left sidebars
    if (isMobile && newState) {
      setIsLeftSidebarOpen(false);
      setIsProjectSidebarExpanded(false);
    }
  };

  const handleShapeSelect = (selectionData: string) => {
    try {
      const parsed = JSON.parse(selectionData);
      setCurrentSelection(parsed);
    } catch (error) {
      // console.error("Failed to parse selection data:", error);
      setCurrentSelection([]);
    }
  };

  const handleSelectionClear = () => {
    setCurrentSelection([]);
  };

  const handleToggleExpanded = useCallback((itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((item) => item !== itemId)
        : [...prev, itemId],
    );
  }, []);

  const getDisplayName = useCallback((item: TreeItem): string => {
    return item.displayName || item.name;
  }, []);

  const handleShapeCreated = useCallback(
    (
      geometryData?: any[],
      onSaveComplete?: () => void,
      bypassDefer?: boolean,
    ) => {
      // ✅ ADD THIS CHECK
      const skipLoading = (window as any).__skipViewLoading;
      if (skipLoading) {
        // console.log("⏭️ Skipping view loading - non-shared tab command");
        delete (window as any).__skipViewLoading;
      }

      // console.log(`🎯 handleShapeCreated CALLED`);

      if (geometryData) {
        if (bypassDefer || skipLoading) {
          // console.log(`🎯 BYPASS MODE - Setting geometry IMMEDIATELY`);
          setNoahGeometry(geometryData);
          if (onSaveComplete) {
            onSaveComplete();
          }
          return;
        }

        // ✅ NEW: Check if this is the first geometry (either waiting flag OR strictly empty geometry)
        // This ensures that even if the UI switched to "loading", we still catch that this is the first shape
        const isFirstGeometry =
          (isWaitingForFirstPrompt || noahGeometry.length === 0) &&
          geometryData.length > 0;

        if (isFirstGeometry) {
          // console.log(`🎯 FIRST GEOMETRY detected - will auto-fit camera`);
          const geometryWithAutoFit = geometryData.map((g: any) => ({
            ...g,
            _autoFit: true, // Force auto-fit for first geometry
          }));

          setNoahGeometry(geometryWithAutoFit);
          setIsWaitingForFirstPrompt(false);

          // Continue with save after a delay
          setTimeout(async () => {
            const branchToSave =
              (window as any).__currentExecutionBranch || currentBranch;
            if (!isSavingRef.current && !(window as any).__isStabilizing) {
              await saveProjectAsNoahFile(geometryData, branchToSave);
            }
            if (onSaveComplete) {
              onSaveComplete();
            }
            setPendingFeatureName(null);
          }, 500);

          return;
        }
        // NEW: Check if we need to force immediate update after error
        if ((window as any).__forceNextGeometryUpdate) {
          // console.log(`🔥 FORCING IMMEDIATE UPDATE - recovering from error`);
          delete (window as any).__forceNextGeometryUpdate;
          setNoahGeometry(geometryData);

          setTimeout(async () => {
            const branchToSave =
              (window as any).__currentExecutionBranch || currentBranch;
            if (!isSavingRef.current && !(window as any).__isStabilizing) {
              await saveProjectAsNoahFile(geometryData, branchToSave);
            }
            if (onSaveComplete) {
              onSaveComplete();
            }
            setPendingFeatureName(null);
          }, 500);

          return;
        }

        // console.log(`🎯 DEFERRED MODE - Setting timeout for 500ms`);
        setTimeout(async () => {
          const branchToSave =
            (window as any).__currentExecutionBranch || currentBranch;

          // console.log(`🎯 TIMEOUT FIRED - Branch: ${branchToSave}`);

          // ✅ FIX: Check if activeModelId exists BEFORE saving
          if (!activeModelId) {
            // console.log(
            //   `❌ SAVE SKIPPED - No activeModelId yet! Geometry will be lost.`
            // );

            // ✅ ADD THIS: Mark geometry to skip camera reset (command execution)
            const geometryWithSkipFlag = geometryData.map((g: any) => ({
              ...g,
              _skipCameraReset: true, // ← DON'T auto-fit camera on command updates
            }));

            setNoahGeometry(geometryWithSkipFlag); // ← CHANGED: Use flagged geometry

            if (onSaveComplete) {
              onSaveComplete();
            }
            setPendingFeatureName(null);
            return;
          }

          if (!isSavingRef.current && !(window as any).__isStabilizing) {
            // console.log(
            //   `🎯 About to call saveProjectAsNoahFile for ${activeModelId}`
            // );
            await saveProjectAsNoahFile(geometryData, branchToSave);
            // console.log(`🎯 saveProjectAsNoahFile completed`);
          } else {
            // console.log(`🎯 SKIPPED save - already in progress`);
          }

          // ✅ ADD THIS: Mark geometry to skip camera reset (command execution)
          const geometryWithSkipFlag = geometryData.map((g: any) => ({
            ...g,
            _skipCameraReset: true, // ← DON'T auto-fit camera on command updates
          }));

          // Show geometry after save
          // console.log(
          //   `🎯 Setting noahGeometry state with ${geometryData.length} items`
          // );
          // Show geometry after save
          setNoahGeometry(geometryWithSkipFlag);

          // Execute deferred UI updates AFTER save
          if (onSaveComplete) {
            onSaveComplete();
          }

          // Clear pending feature AFTER callback completes
          setPendingFeatureName(null);

          // ✅ ADD THIS: Collapse toolbar after geometry successfully rendered
          // if (onToolbarExpandChange && geometryWithSkipFlag.length > 0) {
          //   console.log(
          //     "✅ Collapsing toolbar - geometry rendered successfully",
          //   );
          //   onToolbarExpandChange(false);
          // }
        }, 500);
      } else {
        // console.log(`🎯 handleShapeCreated called with NO geometryData`);
      }
    },
    [
      saveProjectAsNoahFile,
      currentBranch,
      activeModelId,
      openModels,
      isWaitingForFirstPrompt,
    ],
  );

  const escapeRegex = (s: string): string =>
    s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const applyTimelineSnapshot = useCallback(
    async (
      snapshot: TimelineSnapshot,
      opts?: { preferredBranchId?: string },
    ) => {
      // console.log(
      //   `📸 APPLY SNAPSHOT: Starting with tip: ${snapshot.tipFeature}`
      // );
      // console.log(
      //   `📸 APPLY SNAPSHOT: Chat messages:`,
      //   snapshot.chatMessages?.length
      // );
      // console.log(
      //   `📸 APPLY SNAPSHOT: Feature tree:`,
      //   snapshot.featureTree?.length
      // );

      if (isSwitchingBranch) {
        // console.log(
        //   "⏸️ APPLY SNAPSHOT: Skipping - branch switch already in progress"
        // );
        return;
      }

      setIsSwitchingBranch(true);
      try {
        // console.log("📸 APPLY SNAPSHOT: Using multi-process backend");

        const activeTimelineId =
          (versionControl as any).getCurrentTimelineId?.() ??
          (versionControl as any).state?.currentTimelineId ??
          "main";

        // console.log(`📸 APPLY SNAPSHOT: Active timeline: ${activeTimelineId}`);

        // 🔧 CRITICAL: Don't sync pointers if we just loaded from save
        if (!(versionControl as any).__pointersRestoredFromSave) {
          (versionControl as any).syncPointersToCurrentTimeline?.();
        } else {
          // console.log(
          //   "⏸️ APPLY SNAPSHOT: Skipping sync - pointers restored from save"
          // );
        }

        const branchForTimeline =
          opts?.preferredBranchId ||
          (timelineBranchMap as any)?.[activeTimelineId] ||
          currentBranch ||
          "main";

        // console.log(
        //   `📸 APPLY SNAPSHOT: Switching to branch: ${branchForTimeline}`
        // );

        // Step 1: Switch branch
        await fetch(`${BACKEND_URL}/api/branch/switch`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ branch_id: branchForTimeline }),
        });

        // console.log(`📸 APPLY SNAPSHOT: Branch switched, fetching geometry...`);

        // Step 2: Get geometry with error handling
        let geometryResult;
        try {
          const refreshResponse = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "refresh_geometry",
              context: {},
              branch_id: branchForTimeline,
            }),
            signal: AbortSignal.timeout(300000),
          });

          if (!refreshResponse.ok) {
            // console.error(
            //   `❌ APPLY SNAPSHOT: Backend returned ${refreshResponse.status}`
            // );

            // Try to recover by forcing a document state refresh
            // console.log(
            //   `🔄 APPLY SNAPSHOT: Attempting recovery with DEBUG_DOCUMENT_STATE...`
            // );
            const debugResponse = await fetch(`${BACKEND_URL}/api/command`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_input: "DEBUG_DOCUMENT_STATE",
                context: {},
                branch_id: branchForTimeline,
              }),
            });

            const debugResult = await debugResponse.json();
            // console.log(`📸 APPLY SNAPSHOT: Debug state:`, debugResult);

            if (
              debugResult.success &&
              debugResult.debug_data?.bodies?.length > 0
            ) {
              // console.log(
              //   `✅ APPLY SNAPSHOT: Found ${debugResult.debug_data.bodies.length} bodies in debug state`
              // );

              // Try refresh one more time
              const retryResponse = await fetch(`${BACKEND_URL}/api/command`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_input: "refresh_geometry",
                  context: {},
                  branch_id: branchForTimeline,
                }),
              });

              geometryResult = await retryResponse.json();
            } else {
              // console.error(
              //   `❌ APPLY SNAPSHOT: No bodies found in backend, clearing geometry`
              // );
              geometryResult = { success: false, geometry: [] };
            }
          } else {
            geometryResult = await refreshResponse.json();
          }
        } catch (error) {
          // console.error(`❌ APPLY SNAPSHOT: Fetch failed:`, error);
          geometryResult = { success: false, geometry: [] };
        }

        // console.log(`📸 APPLY SNAPSHOT: Geometry result:`, {
        //   success: geometryResult?.success,
        //   geometryCount: geometryResult?.geometry?.length,
        //   objectsCount: geometryResult?.objects?.length,
        // });

        if (geometryResult?.geometry && geometryResult.geometry.length > 0) {
          const newGeometry = geometryResult.geometry.map((g: any) => ({
            ...g,
            _forceUpdate: `${Date.now()}_${Math.random()}`,
          }));
          // console.log(
          //   `📸 APPLY SNAPSHOT: Calling handleShapeCreated with ${newGeometry.length} items`
          // );
          handleShapeCreated(newGeometry);
        } else {
          // console.log(`📸 APPLY SNAPSHOT: No geometry returned, clearing...`);
          handleShapeCreated([]);
        }

        // Step 3: Update chat
        // Step 3: Update chat - but don't force immediate re-render during navigation
        // Step 3: Update chat AND feature tree
        // console.log(
        //   `📸 APPLY SNAPSHOT: Updating chat messages and feature tree...`
        // );

        // Use setTimeout to defer updates until after current render cycle completes
        setTimeout(() => {
          rightSidebarRef.current?.setChatMessages?.([
            ...snapshot.chatMessages,
          ]);

          let treeToCount: TreeItem[] = [];

          // 🔧 CRITICAL: Update feature tree from snapshot
          if (snapshot.featureTree && snapshot.featureTree.length > 0) {
            setFeatureTree([...snapshot.featureTree]);
            treeToCount = snapshot.featureTree;
            const bodyIds = snapshot.featureTree.map((body) => body.id);
            setExpandedItems(bodyIds);
          } else {
            const rebuiltTree = getFeatureTreeFromChatMessages(
              snapshot.chatMessages,
            );
            setFeatureTree(rebuiltTree);
            treeToCount = rebuiltTree;
            const bodyIds = rebuiltTree.map((body) => body.id);
            setExpandedItems(bodyIds);
          }

          // ✅ FIX: Recalculate counters whenever we apply a snapshot
          const newCounters = recalculatePrimitiveCounters(treeToCount);
          primitiveCountersRef.current = newCounters;
        }, 100);
        await new Promise((r) => setTimeout(r, 250));
        // console.log(`✅ APPLY SNAPSHOT: Complete`);
      } catch (error) {
        // console.error("❌ APPLY SNAPSHOT: Failed:", error);
      } finally {
        setIsSwitchingBranch(false);
      }
    },
    [handleShapeCreated, currentBranch, timelineBranchMap, isSwitchingBranch],
  );

  const applySnapshotRef = useRef<(s: TimelineSnapshot) => Promise<void>>();
  useEffect(() => {
    applySnapshotRef.current = applyTimelineSnapshot;
  }, [applyTimelineSnapshot]);

  const processUserInput = useCallback(
    (input: string): string => {
      let processedInput = input;

      const replaceInTree = (items: TreeItem[]) => {
        items.forEach((item) => {
          if (item.displayName && item.displayName !== item.name) {
            const regex = new RegExp(
              `\\b${escapeRegex(item.displayName)}\\b`,
              "gi",
            );
            if (regex.test(processedInput)) {
              processedInput = processedInput.replace(regex, item.name);
            }
          }
          if (item.children) {
            replaceInTree(item.children);
          }
        });
      };

      replaceInTree(featureTree);
      return processedInput;
    },
    [featureTree],
  );

  const saveOperationSnapshot = useCallback(
    (
      messageId: number,
      tipFeature: string | null = null,
      commandEntry?: CommandHistoryEntry,
    ) => {
      let currentChatMessages: ChatMessage[] = [];
      if (
        rightSidebarRef.current &&
        "getChatMessages" in rightSidebarRef.current &&
        rightSidebarRef.current.getChatMessages
      ) {
        currentChatMessages = rightSidebarRef.current.getChatMessages();
      }

      // 🔧 FIX: Use ref for immediate access, no race condition
      if (!activeModelId) {
        // console.error("❌ SNAPSHOT: No activeModelId!");
        return;
      }

      // Get current command history from ref (or initialize)
      if (!commandHistoryRef.current[activeModelId]) {
        commandHistoryRef.current[activeModelId] = [];
      }

      let commandHistory = [...commandHistoryRef.current[activeModelId]];

      // console.log(
      //   `📸 SNAPSHOT: Current tab has ${commandHistory.length} existing commands`
      // );

      if (commandEntry) {
        // Ensure fullSelectionContext exists
        if (
          !commandEntry.fullSelectionContext &&
          commandEntry.selectionContext
        ) {
          commandEntry.fullSelectionContext = {
            selection: commandEntry.selectionContext,
            objects: [{ name: "Body", type: "PartDesign::Body" }],
            history: [],
            last_created: "Body",
          };
        }

        // Only add if not duplicate
        const exists = commandHistory.some(
          (cmd) => cmd.messageId === commandEntry.messageId,
        );
        if (!exists) {
          commandHistory.push(commandEntry);
          // console.log(
          //   `📸 SNAPSHOT: Added command "${commandEntry.userInput}" (now ${commandHistory.length} total)`
          // );
        } else {
          // console.log(`📸 SNAPSHOT: Command already exists, skipping`);
        }
      }

      // 🔧 CRITICAL: Update ref immediately (synchronous)
      commandHistoryRef.current[activeModelId] = commandHistory;
      // console.log(
      //   `✅ SNAPSHOT: Updated ref with ${commandHistory.length} commands for tab ${activeModelId}`
      // );

      const currentFeatureTree = featureTreeRef.current;

      const snapshot = versionControl.createSnapshotFromCurrentState(
        currentFeatureTree,
        currentChatMessages,
        0,
        tipFeature,
        commandHistory,
      );

      versionControl.saveOperation(messageId, snapshot);

      // Update the timeline
      const currentTimelineId = versionControl.getCurrentTimelineId();
      const timeline = (versionControl as any).state.timelines[
        currentTimelineId
      ];
      if (timeline) {
        timeline.featureTree = [...currentFeatureTree];
        timeline.commandHistory = [...commandHistory];
        // console.log(
        //   `✅ SNAPSHOT: Timeline now has ${commandHistory.length} commands`
        // );
      }

      // Update versioned operations if in version mode
      const versionedOps = versionControl.getVersionedOperations();
      for (const [baseMessageId, versionedOp] of Object.entries(versionedOps)) {
        const currentTimelineId = versionControl.getCurrentTimelineId();
        const currentVersionObj =
          versionedOp.versions[versionedOp.currentVersion];

        if (
          currentVersionObj &&
          currentVersionObj.timelineId === currentTimelineId
        ) {
          const freshChatMessages =
            rightSidebarRef.current?.getChatMessages?.() || currentChatMessages;

          currentVersionObj.snapshot =
            versionControl.createSnapshotFromCurrentState(
              currentFeatureTree,
              freshChatMessages,
              0,
              tipFeature,
              commandHistory,
            );
          break;
        }
      }

      // Also update openModels for persistence (async is fine here)
      setOpenModels((prev) =>
        prev.map((model) =>
          model.id === activeModelId
            ? { ...model, commandHistory: [...commandHistory] }
            : model,
        ),
      );
    },
    [versionControl, activeModelId],
  );

  const updateCurrentVersionSnapshot = useCallback(
    (messageId: number, commandEntry: CommandHistoryEntry) => {
      // Get the versioned operation for this message
      const versionedOps = versionControl.getVersionedOperations();
      const versionedOp = versionedOps[messageId];

      if (!versionedOp) {
        // No versioned operation yet, this is fine
        return;
      }

      // Get current chat messages
      const currentChatMessages =
        rightSidebarRef.current?.getChatMessages?.() || [];

      // Get the current snapshot
      const currentSnapshot = versionControl.getCurrentSnapshot();
      let currentCommandHistory = currentSnapshot.commandHistory || [];

      // Add the new command to history if not already there
      const commandExists = currentCommandHistory.some(
        (cmd) => cmd.messageId === commandEntry.messageId,
      );

      if (!commandExists) {
        currentCommandHistory = [...currentCommandHistory, commandEntry];
      }

      // Update the current version's snapshot with the new command history
      const currentVersionIndex = versionedOp.currentVersion;
      const currentVersionObj = versionedOp.versions[currentVersionIndex];

      if (currentVersionObj) {
        currentVersionObj.snapshot = {
          ...currentVersionObj.snapshot,
          chatMessages: [...currentChatMessages],
          commandHistory: currentCommandHistory,
          featureTree: [...featureTree],
        };

        // console.log(
        //   `📝 Updated version ${currentVersionIndex + 1}/${
        //     versionedOp.versions.length
        //   } snapshot - now has ${currentCommandHistory.length} commands`
        // );
      }
    },
    [versionControl, featureTree],
  );

  const createProjectFromRestore = useCallback(
    async (messageId: number): Promise<void> => {
      // console.log(`🔄 RESTORE: Creating new project from message ${messageId}`);
      // console.log(`🔄 RESTORE: Current projectId: ${projectId}`);
      // console.log(`🔄 RESTORE: Current activeModelId: ${activeModelId}`);
      // console.log(`🔄 RESTORE: User:`, user?.uid);

      try {
        // Get current chat messages to find the restore point
        const currentChatMessages =
          rightSidebarRef.current?.getChatMessages?.() || [];
        // console.log(
        //   `🔄 RESTORE: Total chat messages: ${currentChatMessages.length}`
        // );

        const messageIndex = currentChatMessages.findIndex(
          (msg) => msg.id === messageId,
        );

        if (messageIndex === -1) {
          // console.error(
          //   `❌ RESTORE: Message ${messageId} not found in chat history`
          // );
          return;
        }

        // console.log(
        //   `📸 RESTORE: Found message at index ${messageIndex} out of ${currentChatMessages.length} total messages`
        // );

        // Slice chat messages up to the restore point
        const messagesToKeep = currentChatMessages.slice(0, messageIndex + 1);
        // console.log(`📸 RESTORE: Keeping ${messagesToKeep.length} messages`);

        // Load command history from Firebase
        // console.log("📦 RESTORE: Loading command history from Firebase...");
        if (!activeModelId) {
          // console.error("❌ RESTORE: No activeModelId available!");
          return;
        }

        const projectDoc = await getDoc(doc(db, "projects", activeModelId));

        if (!projectDoc.exists()) {
          // console.error("❌ RESTORE: Project document doesn't exist!");
          return;
        }

        const projectData = projectDoc.data();
        // console.log("📦 RESTORE: Project data loaded:", {
        //   hasCommandHistory: !!projectData?.commandHistory,
        //   commandHistoryLength: projectData?.commandHistory?.length,
        // });

        let savedCommands: CommandHistoryEntry[] = [];
        if (projectData?.commandHistory) {
          savedCommands = unflattenFromFirebase(projectData.commandHistory);
          // console.log(
          //   `📦 RESTORE: Found ${savedCommands.length} commands in Firebase`
          // );
        } else {
          // console.error("❌ RESTORE: No command history in Firebase!");
          // alert("Cannot restore: No command history found");
          return;
        }

        // Count assistant messages up to restore point
        const assistantMessageCount = messagesToKeep.filter(
          (msg) => msg.type === "assistant" && msg.responseType !== "error",
        ).length;

        // console.log(
        //   `📸 RESTORE: ${assistantMessageCount} assistant messages to restore`
        // );

        // Take only the commands we need up to the restore point
        const commandsToReplay = savedCommands.slice(0, assistantMessageCount);

        // console.log(
        //   `📦 RESTORE: Will replay ${commandsToReplay.length} commands`
        // );
        // console.log(
        //   "📦 RESTORE: Commands to replay:",
        //   commandsToReplay.map((cmd, i) => `${i + 1}. ${cmd.userInput}`)
        // );

        if (commandsToReplay.length === 0) {
          // console.error(
          //   "❌ RESTORE: No commands found to replay - cannot restore"
          // );
          // alert("Cannot restore: No commands to replay");
          return;
        }

        // 🔧 CRITICAL FIX: Get feature tree from CURRENT state (with display names)
        const currentFeatureTree = featureTreeRef.current;

        // Match features to their creating messages by name comparison
        const restoredFeatureTree = currentFeatureTree
          .filter((item) => {
            // Check if this feature's name matches any message we're keeping
            const creatingMessage = messagesToKeep.find(
              (msg) =>
                msg.type === "assistant" &&
                msg.responseType !== "error" &&
                (msg.featureName === item.name ||
                  msg.featureName === item.displayName ||
                  msg.content === item.name ||
                  msg.content === item.displayName),
            );

            const shouldKeep = creatingMessage !== undefined;

            // console.log(
            //   `📸 RESTORE: Item ${item.displayName || item.name} -> ${
            //     shouldKeep ? "KEEP" : "FILTER"
            //   }`
            // );

            return shouldKeep;
          })
          .map((item) => {
            if (item.children && item.children.length > 0) {
              const filteredChildren = item.children.filter((child) => {
                const creatingMessage = messagesToKeep.find(
                  (msg) =>
                    msg.type === "assistant" &&
                    msg.responseType !== "error" &&
                    (msg.featureName === child.name ||
                      msg.featureName === child.displayName ||
                      msg.content === child.name ||
                      msg.content === child.displayName),
                );

                const shouldKeep = creatingMessage !== undefined;

                // console.log(
                //   `📸 RESTORE: Child ${child.displayName || child.name} -> ${
                //     shouldKeep ? "KEEP" : "FILTER"
                //   }`
                // );

                return shouldKeep;
              });

              return {
                ...item,
                children: filteredChildren,
              };
            }
            return item;
          });

        // console.log(
        //   `📸 RESTORE: Filtered tree has ${restoredFeatureTree.length} parent features (preserving display names)`
        // );
        restoredFeatureTree.forEach((item) => {
          // console.log(
          //   `  - ${item.displayName || item.name} (${
          //     item.children?.length || 0
          //   } children)`
          // );
        });

        // Create a fresh branch and replay commands
        const timestamp = Date.now();
        const newBranch = user
          ? `user_${user.uid}_${timestamp}_restore`
          : `guest_${timestamp}_restore`;

        // console.log(`🔄 RESTORE: Creating fresh branch: ${newBranch}`);

        // Clear document on new branch
        const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "CLEAR_DOCUMENT",
            context: {},
            branch_id: newBranch,
          }),
          signal: AbortSignal.timeout(60000),
        });

        if (!clearResponse.ok) {
          throw new Error("Failed to create fresh branch");
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Replay commands up to the restore point
        // console.log(
        //   `🔄 RESTORE: Replaying ${commandsToReplay.length} commands...`
        // );

        for (let i = 0; i < commandsToReplay.length; i++) {
          const cmd = commandsToReplay[i];
          // console.log(
          //   `🔄 RESTORE: Command ${i + 1}/${commandsToReplay.length}: ${
          //     cmd.userInput
          //   }`
          // );

          let context = {
            selection: [] as any[],
            objects: [{ name: "Body", type: "PartDesign::Body" }],
            history: [],
            last_created: "Body",
          };

          if (cmd.fullSelectionContext) {
            context = {
              selection: cmd.fullSelectionContext.selection || [],
              objects: cmd.fullSelectionContext.objects || context.objects,
              history: cmd.fullSelectionContext.history || [],
              last_created: cmd.fullSelectionContext.last_created || "Body",
            };
          } else if (cmd.selectionContext) {
            context.selection = Array.isArray(cmd.selectionContext)
              ? cmd.selectionContext
              : [];
          }

          // Restore browser coordinate data for edge/face selections
          if (cmd.browserSelectionData) {
            context.selection = context.selection.map((sel: any) => {
              if (sel.type === "edges" && cmd.browserSelectionData?.edges) {
                const matchingEdges = cmd.browserSelectionData.edges.filter(
                  (e) => sel.indices.includes(e.index),
                );
                if (matchingEdges.length > 0) {
                  return {
                    ...sel,
                    browser_coordinates: matchingEdges.map(
                      (e) => e.coordinates,
                    ),
                  };
                }
              }
              if (sel.type === "faces" && cmd.browserSelectionData?.faces) {
                const matchingFaces = cmd.browserSelectionData.faces.filter(
                  (f) => sel.indices.includes(f.index),
                );
                if (matchingFaces.length === 1) {
                  return {
                    ...sel,
                    browser_face_data: {
                      center: matchingFaces[0].center,
                      normal: matchingFaces[0].normal,
                      area: matchingFaces[0].area,
                    },
                  };
                } else if (matchingFaces.length > 1) {
                  return {
                    ...sel,
                    browser_face_data: matchingFaces.map((f) => ({
                      center: f.center,
                      normal: f.normal,
                      area: f.area,
                    })),
                  };
                }
              }
              return sel;
            });
          }

          const response = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: cmd.userInput,
              context: context,
              branch_id: newBranch,
            }),
            signal: AbortSignal.timeout(300000),
          });

          if (!response.ok) {
            throw new Error(`Command ${i + 1} failed: HTTP ${response.status}`);
          }

          const result = await response.json();
          if (!result.success) {
            throw new Error(`Command ${i + 1} failed: ${result.message}`);
          }

          // console.log(`✅ RESTORE: Command ${i + 1} succeeded`);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // console.log("✅ RESTORE: All commands replayed");

        // Export the state at restore point
        // console.log("📦 RESTORE: Exporting state at restore point...");
        const exportResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "EXPORT_NOAH_FILE",
            context: {},
            branch_id: newBranch,
          }),
          signal: AbortSignal.timeout(30000),
        });

        if (!exportResponse.ok) {
          throw new Error("Failed to export at restore point");
        }

        const exportResult = await exportResponse.json();
        if (!exportResult.success || !exportResult.fcstd_data) {
          throw new Error("No FCStd data in export");
        }

        // console.log(
        //   `✅ RESTORE: Exported ${exportResult.file_size} bytes from restore point`
        // );

        // Get geometry for thumbnail
        const geometryResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "refresh_geometry",
            context: {},
            branch_id: newBranch,
          }),
          signal: AbortSignal.timeout(30000),
        });

        let geometryForThumbnail: any[] = [];
        if (geometryResponse.ok) {
          const geometryResult = await geometryResponse.json();
          if (geometryResult?.geometry?.length > 0) {
            geometryForThumbnail = geometryResult.geometry;
            // console.log(
            //   `✅ RESTORE: Got ${geometryForThumbnail.length} geometry items for thumbnail`
            // );
          }
        }

        // Create brand new project with ONLY the commands we replayed
        const now = new Date();

        // Use current project's name and strip any "(Branch X)" suffix
        let baseName = fileName;
        const branchMatch = baseName.match(/^(.*?)\s+\(Branch \d+\)$/);
        if (branchMatch) {
          baseName = branchMatch[1].trim();
        }

        // console.log(`📝 RESTORE: Using base name: "${baseName}"`);

        // Find the root project ID for counting family tree size
        let rootProjectId = activeModelId;
        const currentProjectDoc = await getDoc(
          doc(db, "projects", activeModelId),
        );
        const currentProjectData = currentProjectDoc.data();

        if (currentProjectData?.branchedFrom?.projectId) {
          let ancestorId = currentProjectData.branchedFrom.projectId;
          let safetyCounter = 0;

          while (safetyCounter < 50) {
            const ancestorDoc = await getDoc(doc(db, "projects", ancestorId));
            if (!ancestorDoc.exists()) break;

            const ancestorData = ancestorDoc.data();
            rootProjectId = ancestorId;

            if (ancestorData?.branchedFrom?.projectId) {
              ancestorId = ancestorData.branchedFrom.projectId;
              safetyCounter++;
            } else {
              break;
            }
          }
        }

        // console.log(`📝 RESTORE: Root project ID: ${rootProjectId}`);

        // Now count ALL branches in this family tree (including the root and current project)
        const { collection, query, where, getDocs } =
          await import("firebase/firestore");
        const projectsRef = collection(db, "projects");
        if (!user) return;
        const q = query(projectsRef, where("userId", "==", user.uid));
        const allProjectsSnapshot = await getDocs(q);

        // Build a set of all project IDs in this family
        const familyIds = new Set<string>([rootProjectId]);

        // Find all descendants of the root
        allProjectsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data?.branchedFrom?.projectId) {
            // Walk up this project's chain to see if it connects to our root
            let checkId = data.branchedFrom.projectId;
            let depth = 0;

            while (depth < 50 && checkId) {
              if (checkId === rootProjectId || familyIds.has(checkId)) {
                // This project is part of our family!
                familyIds.add(doc.id);
                break;
              }

              // Check this ancestor's parent
              const checkDoc = allProjectsSnapshot.docs.find(
                (d) => d.id === checkId,
              );
              if (checkDoc?.data()?.branchedFrom?.projectId) {
                checkId = checkDoc.data().branchedFrom.projectId;
              } else {
                break;
              }
              depth++;
            }
          }
        });

        const branchNumber = familyIds.size; // Total family size = next branch number
        const newProjectName = `${baseName} (Branch ${branchNumber})`;

        // console.log(
        //   `📝 RESTORE: Creating "${newProjectName}" (${branchNumber} total in family tree)`
        // );
        const docRef = await addDoc(collection(db, "projects"), {
          name: newProjectName,
          createdAt: now,
          updatedAt: now.toISOString(), // ← CHANGED: Use ISO String for consistent sorting
          userId: user?.uid || "guest",
          description: `Restored from ${fileName}`,
          fcstdFile: exportResult.fcstd_data,
          fileSize: exportResult.file_size,
          chatHistory: flattenForFirebase(messagesToKeep),
          currentFeatureTree: flattenForFirebase(restoredFeatureTree),
          commandHistory: flattenForFirebase(commandsToReplay),
          branchedFrom: {
            projectId: activeModelId,
            branchPoint: messageId,
            timestamp: Date.now(),
          },
        });

        // console.log(
        //   `✅ RESTORE: Created new project ${docRef.id} with ${messagesToKeep.length} messages and ${commandsToReplay.length} commands`
        // );

        // Generate and save thumbnail
        if (geometryForThumbnail.length > 0) {
          const thumbnailData = await generateThumbnail(
            geometryForThumbnail,
            colorValue,
          );
          if (thumbnailData) {
            await updateDoc(doc(db, "projects", docRef.id), {
              thumbnail: thumbnailData,
              modelColor: colorValue,
              transparencyValue: transparencyValue,
            });
            // console.log("✅ RESTORE: Thumbnail saved");
          }
        }

        // 🔧 CRITICAL FIX: Initialize command history ref for branched project
        commandHistoryRef.current[docRef.id] = [...commandsToReplay];
        // console.log(
        //   `✅ RESTORE: Initialized ref with ${commandsToReplay.length} commands for branched project ${docRef.id}`
        // );
        if (commandsToReplay.length > 0) {
          // console.log(`   First command: ${commandsToReplay[0].userInput}`);
        }

        // console.log(
        //   "✅ RESTORE: Project created successfully, opening in new tab..."
        // );

        // ✅ FIX: Optimistically update UI state for the new project
        // This ensures TopToolbar and Sidebar show the new project instantly
        setOptimisticProject({ id: docRef.id, name: newProjectName });

        setSidebarProjects((prev) => [
          {
            id: docRef.id,
            name: newProjectName,
            createdAt: now,
            updatedAt: now,
            userId: user?.uid || "guest",
            description: `Restored from ${fileName}`,
            isPublic: false,
            shareToken: undefined,
          },
          ...prev,
        ]);

        // ✅ CRITICAL FIX: Save current tab's state BEFORE doing anything else
        const currentChatMessagesBeforeSwitch =
          rightSidebarRef.current?.getChatMessages?.() || [];

        setOpenModels((prev) =>
          prev.map((model) =>
            model.id === activeModelId
              ? {
                  ...model,
                  geometry: [...noahGeometry],
                  featureTree: [...featureTree],
                  chatMessages: [...currentChatMessagesBeforeSwitch],
                  commandHistory:
                    versionControl.getCurrentSnapshot().commandHistory || [],
                  branchId: currentBranch,
                }
              : model,
          ),
        );

        // Small delay to ensure state is saved
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Now create the new tab
        const restoredCounters =
          recalculatePrimitiveCounters(restoredFeatureTree);

        // Now create the new tab
        const newModel = {
          id: docRef.id,
          name: newProjectName,
          geometry: geometryForThumbnail,
          featureTree: restoredFeatureTree,
          chatMessages: messagesToKeep,
          commandHistory: commandsToReplay,
          versionControl: null,
          branchId: newBranch,
          isSharedModel: false,
          cameraState: {
            rotation: { x: -30, y: -45 },
            distance: 50,
            isOrthographic: false,
          },
          // ✅ USE CALCULATED COUNTERS
          primitiveCounters: restoredCounters,
        };

        // Add to existing tabs
        setOpenModels((prev) => [...prev, newModel]);
        // 🔧 CRITICAL: Set loading state to show spinner in View
        setIsViewLoading(true);

        // Switch to the new tab
        setActiveModelId(docRef.id);
        setCurrentBranch(newBranch);

        // ✅ ADD THESE LINES - Clear loading states
        setIsViewLoading(false);
        setIsLoading(false);

        // Load the branched project's state
        setNoahGeometry([...geometryForThumbnail]);
        setFeatureTree([...restoredFeatureTree]);

        // Update chat
        if (rightSidebarRef.current) {
          rightSidebarRef.current.setChatMessages([...messagesToKeep]);
        }

        // Update file name
        setFileName(newProjectName);

        // Auto-expand all body items
        const bodyIds = restoredFeatureTree.map((body: TreeItem) => body.id);
        setExpandedItems(bodyIds);

        // ✅ NEW: Refresh sidebar to show the new branched project
        // ✅ NEW: Refresh sidebar to show the new branched project
        try {
          await refreshSidebarProjects();
          // console.log(`✅ Sidebar refreshed - new project should appear`);
        } catch (error) {
          // console.error("Failed to refresh sidebar:", error);
        }

        // ✅ NEW: Clear the branch button loading state immediately
        if ((window as any).__clearBranchButtonLoading) {
          (window as any).__clearBranchButtonLoading();
        }

        // console.log(`✅ Branched project opened in new tab: ${newProjectName}`);
      } catch (error) {
        // console.error("❌ RESTORE: Failed:", error);
        // alert(
        //   `Restore failed: ${
        //     error instanceof Error ? error.message : String(error)
        //   }`
        // );
      }
    },
    [
      versionControl,
      user,
      fileName,
      colorValue,
      transparencyValue,
      generateThumbnail,
      flattenForFirebase,
      projectId,
      activeModelId,
      noahGeometry,
      featureTree,
      currentBranch,
    ],
  );

  const navigateOperationVersion = useCallback(
    async (messageId: number, direction: "back" | "forward"): Promise<void> => {
      // console.log(`🔄 NAV: Navigating ${direction} for operation ${messageId}`);

      if (isSwitchingBranch) {
        // console.log("Navigation already in progress");
        return;
      }

      setIsSwitchingBranch(true);

      try {
        const versionedOp = (versionControl as any).state
          ?.versionedOperations?.[messageId];
        if (!versionedOp) {
          // console.error(`No versions found for operation ${messageId}`);
          return;
        }

        // Calculate target version
        let targetVersion = versionedOp.currentVersion;
        if (direction === "back") {
          targetVersion = Math.max(0, versionedOp.currentVersion - 1);
        } else {
          targetVersion = Math.min(
            versionedOp.versions.length - 1,
            versionedOp.currentVersion + 1,
          );
        }

        if (targetVersion === versionedOp.currentVersion) {
          // console.log(
          //   `Already at ${direction === "back" ? "first" : "last"} version`
          // );
          return;
        }

        // 🔧 Get the target version's snapshot
        const targetVersionObj = versionedOp.versions[targetVersion];
        const targetTimelineId = targetVersionObj.timelineId;
        let targetSnapshot = targetVersionObj.snapshot;

        // console.log(`🔧 NAV: Target timeline: ${targetTimelineId}`);

        // 🔧 CRITICAL: When navigating to a parent version, reset ALL subsequent operations to version 1
        // console.log(
        //   `🔧 NAV: Resetting all child/subsequent operations to version 1...`
        // );

        // Get the target snapshot to determine which messages exist at this point
        const targetChatMessages = targetSnapshot.chatMessages || [];

        // Find the position of the message we're navigating to
        const navigatedMessageIndex = targetChatMessages.findIndex(
          (msg: ChatMessage) => msg.id === messageId,
        );

        // console.log(
        //   `🔧 NAV: Parent operation (message ${messageId}) is at position ${navigatedMessageIndex} in snapshot with ${targetChatMessages.length} messages`
        // );

        // Get all versioned operations
        const allVersionedOps = (versionControl as any).state
          .versionedOperations;

        // Reset ALL operations that come AFTER this parent operation
        for (const [childMessageId, childOp] of Object.entries(
          allVersionedOps,
        )) {
          const childMsgId = parseInt(childMessageId);

          // Skip the operation we're navigating to
          if (childMsgId === messageId) {
            // console.log(`🔧 NAV: Skipping self (operation ${childMessageId})`);
            continue;
          }

          // Find this child's position in the target snapshot
          const childMessageIndex = targetChatMessages.findIndex(
            (msg: ChatMessage) => msg.id === childMsgId,
          );

          if (childMessageIndex === -1) {
            // Operation doesn't exist in this snapshot - must be after this point
            // console.log(
            //   `🔧 NAV: Operation ${childMessageId} not in snapshot - resetting to 1/${
            //     (childOp as any).versions?.length || 0
            //   }`
            // );
            if ((childOp as any).versions?.length > 0) {
              (childOp as any).currentVersion = 0;
            }
          } else if (childMessageIndex > navigatedMessageIndex) {
            // Operation exists but comes AFTER - reset it
            // console.log(
            //   `🔧 NAV: Operation ${childMessageId} comes after parent (pos ${childMessageIndex}) - resetting to 1/${
            //     (childOp as any).versions?.length || 0
            //   }`
            // );
            if ((childOp as any).versions?.length > 0) {
              (childOp as any).currentVersion = 0;
            }
          } else {
            // Operation comes BEFORE or is the parent - leave it alone
            // console.log(
            //   `🔧 NAV: Operation ${childMessageId} comes before parent (pos ${childMessageIndex}) - keeping at version ${
            //     ((childOp as any).currentVersion || 0) + 1
            //   }/${(childOp as any).versions?.length || 0}`
            // );
          }
        }

        // If navigating forward to the CURRENT timeline, refresh its snapshot with current state
        const currentTimelineId = versionControl.getCurrentTimelineId();
        const isNavigatingToCurrentTimeline =
          targetTimelineId === currentTimelineId;

        if (direction === "forward" && isNavigatingToCurrentTimeline) {
          // console.log(
          //   `🔧 NAV: Navigating to current timeline - using fresh state...`
          // );

          const currentChatMessages =
            rightSidebarRef.current?.getChatMessages?.() || [];
          const freshSnapshot = versionControl.createSnapshotFromCurrentState(
            featureTree,
            currentChatMessages,
            bodyCounter,
            null,
            targetVersionObj.snapshot.commandHistory || [],
          );

          targetVersionObj.snapshot = freshSnapshot;
          targetSnapshot = freshSnapshot;
          // console.log(`✅ NAV: Updated snapshot with current state`);
        }

        const commandsToReplay = targetSnapshot.commandHistory || [];
        // console.log(
        //   `🔄 NAV: Will replay ${
        //     commandsToReplay.length
        //   } commands for version ${targetVersion + 1}/${
        //     versionedOp.versions.length
        //   }`
        // );

        // Step 1: Create fresh branch
        const timestamp = Date.now();
        const newBranch = user
          ? `user_${user.uid}_${projectId}_${timestamp}`
          : `guest_${projectId}_${timestamp}`;

        // console.log(`🔄 NAV: Creating branch: ${newBranch}`);

        const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "CLEAR_DOCUMENT",
            context: {},
            branch_id: newBranch,
          }),
          signal: AbortSignal.timeout(60000),
        });

        if (!clearResponse.ok) {
          throw new Error(`Failed to create branch: ${clearResponse.status}`);
        }

        // console.log(`✅ NAV: Branch created and cleared`);
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Step 2: Replay commands one by one
        for (let i = 0; i < commandsToReplay.length; i++) {
          const cmd = commandsToReplay[i];
          // console.log(
          //   `🔄 NAV: Replaying ${i + 1}/${commandsToReplay.length}: ${
          //     cmd.userInput
          //   }`
          // );

          let context = {
            selection: [] as any[], // ADD: as any[]
            objects: [{ name: "Body", type: "PartDesign::Body" }],
            history: [],
            last_created: "Body",
          };

          if (cmd.fullSelectionContext) {
            context = {
              selection: cmd.fullSelectionContext.selection || [],
              objects: cmd.fullSelectionContext.objects || context.objects,
              history: cmd.fullSelectionContext.history || [],
              last_created: cmd.fullSelectionContext.last_created || "Body",
            };
          } else if (cmd.selectionContext) {
            context.selection = Array.isArray(cmd.selectionContext)
              ? cmd.selectionContext
              : [];
          }

          const response = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: cmd.userInput,
              context: context,
              branch_id: newBranch,
            }),
            signal: AbortSignal.timeout(300000),
          });

          if (!response.ok) {
            throw new Error(`Command ${i + 1} failed: HTTP ${response.status}`);
          }

          const result = await response.json();

          if (!result.success) {
            throw new Error(`Command ${i + 1} failed: ${result.message}`);
          }

          // console.log(`✅ NAV: Command ${i + 1} completed`);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // console.log(`✅ NAV: All commands replayed`);

        // Step 3: CRITICAL - Update currentBranch BEFORE updating version control
        setCurrentBranch(newBranch);
        // console.log(`🔄 NAV: Updated currentBranch to ${newBranch}`);

        const snapshot = targetVersionObj.snapshot;

        // Store geometry but DON'T update UI yet
        let geometryToApply: any[] = [];

        // Step 4: Get geometry from the NEW branch and save
        const geometryResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "refresh_geometry",
            context: {},
            branch_id: newBranch,
          }),
          signal: AbortSignal.timeout(30000),
        });

        // Around line 2050 - where we apply geometry after replaying commands:
        if (geometryResponse.ok) {
          const geometryResult = await geometryResponse.json();
          if (geometryResult?.geometry?.length > 0) {
            geometryToApply = geometryResult.geometry.map((g: any) => ({
              ...g,
              _forceUpdate: `${Date.now()}_${Math.random()}`,
              _autoFit: true, // ← ADD THIS FLAG to trigger camera reset
            }));

            // Save the project with this geometry (but don't show it yet)
            await saveProjectAsNoahFile(geometryToApply, newBranch);
          }
        }

        // console.log(
        //   `✅ NAV: Geometry loaded - now stabilizing before showing UI...`
        // );

        // CRITICAL: Stabilize BEFORE updating UI
        (window as any).__isStabilizing = true;

        try {
          // console.log(`🔧 NAV: Stabilizing branch ${newBranch}...`);
          const stabilized = await stabilizeBranch(newBranch);

          if (!stabilized) {
            // console.error("❌ NAV: Branch stabilization failed!");
            // alert(
            //   "Warning: Backend may not have persisted the geometry correctly. You may encounter issues with the next operation."
            // );
          } else {
            // console.log(`✅ NAV: Branch fully stabilized`);
          }
        } finally {
          (window as any).__isStabilizing = false;
        }

        // 🔧 NOW update the parent version pointer
        versionedOp.currentVersion = targetVersion;
        (versionControl as any).state.currentTimelineId =
          targetVersionObj.timelineId;

        // **NEW: Ensure the timeline has a proper snapshot with full message history**
        (versionControl as any).state.timelines[targetVersionObj.timelineId] = {
          featureTree: targetSnapshot.featureTree || [],
          chatMessages: targetSnapshot.chatMessages || [],
          bodyCounter: targetSnapshot.bodyCounter || 0,
          tipFeature: targetSnapshot.tipFeature || null,
          commandHistory: targetSnapshot.commandHistory || [],
        };
        // console.log(
        //   `✅ NAV: Populated timeline ${
        //     targetVersionObj.timelineId
        //   } with snapshot containing ${
        //     targetSnapshot.chatMessages?.length || 0
        //   } messages`
        // );

        // Step 5: Update timeline-branch mapping
        const activeTimelineId = versionControl.getCurrentTimelineId();
        if (activeTimelineId) {
          setTimelineBranchMap((prev) => ({
            ...prev,
            [activeTimelineId]: newBranch,
          }));
        }

        // NOW it's safe to save
        // console.log(`💾 NAV: Saving project state for timeline persistence...`);
        await saveProjectAsNoahFile(geometryToApply, newBranch);
        // console.log(`✅ NAV: Project saved with current timeline state`);

        // 🎨 NOW UPDATE UI - AFTER SAVE COMPLETES
        // console.log(
        //   `🎨 NAV: Updating UI with new geometry, chat, and feature tree...`
        // );

        if (geometryToApply.length > 0) {
          setNoahGeometry(geometryToApply);
        }

        rightSidebarRef.current?.setChatMessages?.([...snapshot.chatMessages]);

        let activeTreeForCounters: TreeItem[] = [];

        if (snapshot.featureTree && snapshot.featureTree.length > 0) {
          setFeatureTree([...snapshot.featureTree]);
          activeTreeForCounters = snapshot.featureTree;

          // ✅ FIX: Explicitly type 'body' as TreeItem
          const bodyIds = snapshot.featureTree.map((body: TreeItem) => body.id);
          setExpandedItems(bodyIds);
        } else {
          const rebuiltTree = getFeatureTreeFromChatMessages(
            snapshot.chatMessages,
          );
          setFeatureTree(rebuiltTree);
          activeTreeForCounters = rebuiltTree;

          // ✅ FIX: Explicitly type 'body' here too
          const bodyIds = rebuiltTree.map((body: TreeItem) => body.id);
          setExpandedItems(bodyIds);
        }

        // Calculate counters based on the tree we just restored
        const newCounters = recalculatePrimitiveCounters(activeTreeForCounters);
        primitiveCountersRef.current = newCounters;

        setOpenModels((prev) =>
          prev.map((m) =>
            m.id === activeModelId
              ? { ...m, primitiveCounters: newCounters }
              : m,
          ),
        );

        // console.log(
        //   `✅ NAV: Navigation complete to version ${targetVersion + 1}/${
        //     versionedOp.versions.length
        //   }`
        // );
        // console.log(`✅ NAV: Active branch is now ${newBranch}`);
      } catch (error) {
        // console.error("❌ NAV: Failed:", error);
      } finally {
        setIsSwitchingBranch(false);
      }
    },
    [
      versionControl,
      user,
      projectId,
      isSwitchingBranch,
      saveProjectAsNoahFile,
      getFeatureTreeFromChatMessages,
      featureTree,
      bodyCounter,
    ],
  );

  const stabilizeBranch = async (branchId: string): Promise<boolean> => {
    // console.log(`🔧 STABILIZE: Forcing backend to persist branch ${branchId}`);

    try {
      // Step 1: Export the current document
      const exportResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "EXPORT_NOAH_FILE",
          context: {},
          branch_id: branchId,
        }),
        signal: AbortSignal.timeout(300000),
      });

      if (!exportResponse.ok) {
        // console.error("❌ STABILIZE: Export failed");
        return false;
      }

      const exportResult = await exportResponse.json();

      if (!exportResult.success || !exportResult.fcstd_data) {
        // console.error("❌ STABILIZE: No file data returned");
        return false;
      }

      // console.log(`✅ STABILIZE: Exported ${exportResult.file_size} bytes`);

      // Step 2: Wait for backend to flush completely
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Step 3: Clear and re-import to force persistence
      const clearResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "CLEAR_DOCUMENT",
          context: {},
          branch_id: branchId,
        }),
        signal: AbortSignal.timeout(60000),
      });

      if (!clearResponse.ok) {
        // console.error("❌ STABILIZE: Clear failed");
        return false;
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Step 4: Re-import the file
      const binaryData = atob(exportResult.fcstd_data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }

      const formData = new FormData();
      formData.append(
        "file",
        new Blob([bytes], { type: "application/octet-stream" }),
        "stabilize.FCStd",
      );
      formData.append("branch_id", branchId);

      const importResponse = await fetch(
        `${BACKEND_URL}/api/command/import-file`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
          signal: AbortSignal.timeout(300000),
        },
      );

      if (!importResponse.ok) {
        // console.error("❌ STABILIZE: Import failed");
        return false;
      }

      const importResult = await importResponse.json();

      if (!importResult.success) {
        // console.error("❌ STABILIZE: Import unsuccessful");
        return false;
      }

      // console.log(`✅ STABILIZE: Branch re-imported successfully`);

      // Step 5: Smart verification - check multiple times instead of blind wait
      // console.log(`🔧 STABILIZE: Polling for bodies...`);
      let attempts = 0;
      const maxAttempts = 20;
      let bodiesFound = false;

      while (attempts < maxAttempts && !bodiesFound) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        attempts++;

        const checkResponse = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: "DEBUG_DOCUMENT_STATE",
            context: {},
            branch_id: branchId,
          }),
          signal: AbortSignal.timeout(5000),
        });

        if (checkResponse.ok) {
          const checkResult = await checkResponse.json();
          const bodyCount = checkResult.debug_data?.bodies?.length || 0;

          if (bodyCount > 0) {
            // console.log(
            //   `✅ STABILIZE: Found ${bodyCount} bodies after ${attempts * 0.5}s`
            // );
            bodiesFound = true;
            break;
          }
        }

        // console.log(
        //   `🔍 STABILIZE: Attempt ${attempts}/${maxAttempts} - no bodies yet`
        // );
      }

      if (!bodiesFound) {
        // console.error(
        //   `❌ STABILIZE: No bodies found after ${maxAttempts * 0.5}s`
        // );
        return false;
      }

      const verifyResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "DEBUG_DOCUMENT_STATE",
          context: {},
          branch_id: branchId,
        }),
        signal: AbortSignal.timeout(10000),
      });

      if (verifyResponse.ok) {
        const verifyResult = await verifyResponse.json();
        const bodyCount = verifyResult.debug_data?.bodies?.length || 0;

        if (bodyCount > 0) {
          // console.log(`✅ STABILIZE: Verified ${bodyCount} bodies present`);
          return true;
        } else {
          // console.error(`❌ STABILIZE: Still no bodies after stabilization`);
          return false;
        }
      }

      return false;
    } catch (error) {
      // console.error("❌ STABILIZE: Failed:", error);
      return false;
    }
  };

  const getOperationVersionInfo = useCallback(
    (messageId: number): OperationVersionInfo | null => {
      return versionControl.getOperationVersionInfo(messageId);
    },
    [versionControl],
  );

  const isInVersionMode = useCallback((): boolean => {
    return versionControl.isVersionMode();
  }, [versionControl]);

  const getVersionedOperations = useCallback(() => {
    return versionControl.getVersionedOperations();
  }, [versionControl]);

  const processAIResponse = useCallback(
    (response: string): string => {
      let processedResponse = response;

      const itemsToReplace: Array<{ name: string; displayName: string }> = [];

      const collectItems = (items: TreeItem[]) => {
        items.forEach((item) => {
          if (item.displayName && item.displayName !== item.name) {
            itemsToReplace.push({
              name: item.name,
              displayName: item.displayName,
            });
          }
          if (item.children) {
            collectItems(item.children);
          }
        });
      };

      collectItems(featureTree);
      itemsToReplace.sort((a, b) => b.name.length - a.name.length);

      itemsToReplace.forEach(({ name, displayName }) => {
        const regex = new RegExp(`\\b${escapeRegex(name)}\\b`, "gi");
        if (regex.test(processedResponse)) {
          processedResponse = processedResponse.replace(regex, displayName);
        }
      });

      return processedResponse;
    },
    [featureTree],
  );

  const handleItemRename = useCallback(
    async (itemId: string, newDisplayName: string) => {
      const trimmedName = newDisplayName.trim();

      if (!trimmedName) {
        return;
      }

      // Prepare lower-case target for case-insensitive comparison
      const targetLower = trimmedName.toLowerCase();

      // 🔒 PREVENT DUPLICATES (Case-Insensitive)
      const isNameTaken = (items: TreeItem[]): boolean => {
        for (const item of items) {
          // Skip the item currently being renamed
          if (item.id !== itemId) {
            // Check against technical name
            if (item.name.toLowerCase() === targetLower) return true;

            // Check against display name (if it exists, otherwise fallback to name)
            const currentDisplay = item.displayName || item.name;
            if (currentDisplay.toLowerCase() === targetLower) return true;
          }

          // Check children recursively
          if (item.children && item.children.length > 0) {
            if (isNameTaken(item.children)) return true;
          }
        }
        return false;
      };

      if (isNameTaken(featureTree)) {
        // console.log(
        //   `Rename aborted: "${trimmedName}" matches an existing feature (case-insensitive).`
        // );
        return; // Silent fail - effectively reverts the UI
      }

      let oldDisplayName = "";

      const findItem = (items: TreeItem[]): TreeItem | null => {
        for (const item of items) {
          if (item.id === itemId) {
            return item;
          }
          if (item.children) {
            const found = findItem(item.children);
            if (found) return found;
          }
        }
        return null;
      };

      const foundItem = findItem(featureTree);
      if (foundItem) {
        oldDisplayName = foundItem.displayName || foundItem.name;
      }

      // Create the updated tree first
      const updateItem = (items: TreeItem[]): TreeItem[] => {
        return items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              displayName: trimmedName,
            };
          }

          if (item.children) {
            return {
              ...item,
              children: updateItem(item.children),
            };
          }

          return item;
        });
      };

      const updatedTree = updateItem(featureTree);

      // Update tree state FIRST
      setFeatureTree(updatedTree);
      featureTreeRef.current = updatedTree; // ← ADD THIS LINE to keep ref in sync

      // Update chat messages in right sidebar
      setTimeout(() => {
        if (rightSidebarRef.current && oldDisplayName) {
          rightSidebarRef.current.updateChatMessages(
            oldDisplayName,
            trimmedName,
          );
        }
      }, 100);

      // ✅ CRITICAL FIX: Save to Firebase IMMEDIATELY (not in setTimeout)
      if (activeModelId && user) {
        // ← CHANGED: Use activeModelId instead of projectId
        try {
          const currentChatMessages =
            rightSidebarRef.current?.getChatMessages?.() || [];

          // ✅ Get the current snapshot to preserve command history
          const currentSnapshot = versionControl.getCurrentSnapshot();

          await updateDoc(doc(db, "projects", activeModelId), {
            // ← CHANGED: Use activeModelId
            currentFeatureTree: flattenForFirebase(updatedTree),
            chatHistory: flattenForFirebase(currentChatMessages),
            commandHistory: flattenForFirebase(
              currentSnapshot.commandHistory || [],
            ), // ← ADD THIS
            updatedAt: new Date().toISOString(),
          });
          // console.log(`✅ Saved rename: ${oldDisplayName} → ${trimmedName}`);
        } catch (err) {
          // console.error("Failed to save tree rename:", err);
        }
      }
    },
    [featureTree, activeModelId, user, flattenForFirebase, versionControl], // ← UPDATED DEPS
  );

  const handleNewProject = useCallback(() => {
    setFeatureTree([]);
    setExpandedItems([]);
    setNoahGeometry([]);
    setCurrentSelection([]);
    setBodyCounter(0);
    primitiveCountersRef.current = {
      Box: 0,
      Cylinder: 0,
      Sphere: 0,
      Cone: 0,
      Torus: 0,
      Ellipsoid: 0,
      Prism: 0,
      Wedge: 0,
    };
    setCurrentBranch("main");
    setAvailableBranches(["main"]);
    setTimelineBranchMap({ main: "main" });

    versionControl.reset();

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    const newFileName = `Untitled_${timestamp}`;
    setFileName(newFileName);

    // Use theme-appropriate default color for new projects
    const isDarkMode = document.documentElement.classList.contains("dark");
    const defaultColor = isDarkMode
      ? DEFAULT_COLORS.dark
      : DEFAULT_COLORS.light;
    setColorValue(defaultColor);
    isDefaultColorRef.current = true;
    // Note: No sessionStorage here since this function doesn't have a project ID yet
    // console.log(
    //   `🎨 NEW PROJECT: Set default color ${defaultColor} for theme ${
    //     isDarkMode ? "dark" : "light"
    //   }`
    // );

    if (rightSidebarRef.current && "clearChat" in rightSidebarRef.current) {
      rightSidebarRef.current.clearChat();
    }

    fetch(`${BACKEND_URL}/api/document/clear`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((result) => {
        // console.log("Document cleared:", result);
        return fetch(`${BACKEND_URL}/api/branch/switch`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ branch_id: "main" }),
        });
      })
      .then((response) => response.json())
      .then((result) => {
        // console.log("Switched to main branch:", result);
      })
      .catch((error) => {
        // console.error("Failed to clear document or switch branch:", error);
      });

    // console.log(`Started new project: ${newFileName}`);
  }, [versionControl]);

  // 🔧 NEW HELPER FUNCTION to handle adding one feature at a time
  // Around line 2210, replace the addSingleFeatureToTree function:
  const addSingleFeatureToTree = (
    featureName: string,
    operationType: "create" | "modify" | "delete",
    currentTree: TreeItem[],
    counters: {
      Box: number;
      Cylinder: number;
      Sphere: number;
      Cone: number;
      Torus: number;
      Ellipsoid: number;
      Prism: number;
      Wedge: number;
    },
  ): {
    newTree: TreeItem[];
    finalDisplayName: string;
    updatedCounters: {
      Box: number;
      Cylinder: number;
      Sphere: number;
      Cone: number;
      Torus: number;
      Ellipsoid: number;
      Prism: number;
      Wedge: number;
    };
  } => {
    // Create a working copy of counters
    const workingCounters = { ...counters };

    // NEW: Handle multiple Pocket features (counterbore/countersink)
    if (featureName.includes("Pocket") && featureName.includes("+")) {
      const pocketNames = featureName.split("+").map((s) => s.trim());

      // Count existing Cuts to get the next number
      let cutCount = 0;
      const countCuts = (items: TreeItem[]) => {
        items.forEach((item) => {
          const displayName = item.displayName || item.name;
          if (displayName.startsWith("Cut")) {
            const match = displayName.match(/Cut(\d+)/);
            if (match) {
              const num = parseInt(match[1], 10);
              cutCount = Math.max(cutCount, num);
            }
          }
          if (item.children) {
            countCuts(item.children);
          }
        });
      };
      countCuts(currentTree);

      // Create separate Cut features for each pocket
      let updatedTree = [...currentTree];
      const displayNames: string[] = [];

      pocketNames.forEach((pocketName, idx) => {
        cutCount += 1;
        const paddedNumber = String(cutCount).padStart(3, "0");
        const cutDisplayName = `Cut${paddedNumber}`;
        displayNames.push(cutDisplayName);

        const featureId = `${pocketName
          .toLowerCase()
          .replace(/\s+/g, "_")}_${Date.now()}_${Math.random()}_${idx}`;

        const newFeature: TreeItem = {
          id: featureId,
          name: pocketName,
          displayName: cutDisplayName,
          type: operationType === "create" ? "create" : "modify",
        };

        if (updatedTree.length === 0) {
          newFeature.children = [];
          updatedTree.push(newFeature);
        } else {
          const lastItemIndex = updatedTree.length - 1;
          newFeature.parentId = updatedTree[lastItemIndex].id;

          if (!updatedTree[lastItemIndex].children) {
            updatedTree[lastItemIndex].children = [];
          }
          updatedTree[lastItemIndex].children!.push(newFeature);
        }

        // Expand parent if adding children
        if (updatedTree.length > 0) {
          setExpandedItems((prev) => {
            const parentId = updatedTree[updatedTree.length - 1].id;
            if (!prev.includes(parentId)) {
              return [...prev, parentId];
            }
            return prev;
          });
        }
      });

      return {
        newTree: updatedTree,
        finalDisplayName: displayNames.join(", "),
        updatedCounters: workingCounters,
      };
    }

    // Enhanced feature existence check
    const findExistingFeature = (
      tree: TreeItem[],
      targetName: string,
    ): { exists: boolean; displayName?: string } => {
      for (const item of tree) {
        if (item.name === targetName || item.displayName === targetName) {
          return { exists: true, displayName: item.displayName || item.name };
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.name === targetName || child.displayName === targetName) {
              return {
                exists: true,
                displayName: child.displayName || child.name,
              };
            }
          }
        }
      }
      return { exists: false };
    };

    const existingFeature = findExistingFeature(currentTree, featureName);

    if (existingFeature.exists && operationType !== "create") {
      return {
        newTree: currentTree,
        finalDisplayName: existingFeature.displayName || featureName,
        updatedCounters: workingCounters,
      };
    }

    if (operationType === "create" || operationType === "modify") {
      const featureId = `${featureName
        .toLowerCase()
        .replace(/\s+/g, "_")}_${Date.now()}_${Math.random()}`;

      const primitiveTypes = [
        "Box",
        "Cylinder",
        "Sphere",
        "Cone",
        "Torus",
        "Ellipsoid",
        "Prism",
        "Wedge",
        "SubBox",
        "SubCylinder",
        "SubSphere",
        "SubCone",
        "SubTorus",
        "SubEllipsoid",
        "SubPrism",
        "SubWedge",
      ];

      let displayName = featureName;
      let technicalName = featureName;

      // ✅ CRITICAL FIX: Match primitives with optional Sub prefix AND optional number
      const primitiveMatch = featureName.match(
        /^(?:Sub|sub)?(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)(\d{3})?$/i,
      );

      if (primitiveMatch && operationType === "create") {
        const primitiveType = primitiveMatch[1] as keyof typeof workingCounters;

        // ✅ CRITICAL FIX: Use the COUNTER, not the tree count
        // The counter already accounts for previous additions in this batch
        const currentCount = workingCounters[primitiveType] || 0;
        const newCount = currentCount + 1;
        const paddedNumber = String(newCount).padStart(3, "0");

        displayName = `${primitiveType}${paddedNumber}`;
        technicalName = featureName;

        // ✅ Update the counter immediately
        workingCounters[primitiveType] = newCount;
      }

      const isPrimitive = primitiveTypes.some(
        (type) => featureName === type || featureName.startsWith(type),
      );

      if (isPrimitive || currentTree.length === 0) {
        const newFeature: TreeItem = {
          id: featureId,
          name: technicalName,
          ...(displayName !== technicalName && { displayName }),
          type: operationType === "create" ? "create" : "modify",
          children: [],
        };

        const newTree = [...currentTree, newFeature];
        setExpandedItems((prev) => [...prev, featureId]);

        return {
          newTree,
          finalDisplayName: displayName,
          updatedCounters: workingCounters,
        };
      } else {
        const newTree = [...currentTree];
        const lastItemIndex = newTree.length - 1;

        if (newTree[lastItemIndex]) {
          const mappedDisplayName = getDisplayNameForFeature(technicalName);
          const finalDisplayName =
            displayName !== technicalName ? displayName : mappedDisplayName;

          const newFeature: TreeItem = {
            id: featureId,
            name: technicalName,
            ...(finalDisplayName !== technicalName && {
              displayName: finalDisplayName,
            }),
            type: operationType === "create" ? "create" : "modify",
            parentId: newTree[lastItemIndex].id,
          };

          if (!newTree[lastItemIndex].children) {
            newTree[lastItemIndex].children = [];
          }
          newTree[lastItemIndex].children!.push(newFeature);

          const parentId = newTree[lastItemIndex].id;
          setExpandedItems((current) => {
            if (!current.includes(parentId)) {
              return [...current, parentId];
            }
            return current;
          });

          return {
            newTree,
            finalDisplayName,
            updatedCounters: workingCounters,
          };
        }
      }
    }

    return {
      newTree: currentTree,
      finalDisplayName: featureName,
      updatedCounters: workingCounters,
    };
  };

  // Around line 2370, replace addFeatureToTree:
  // Around line 2370, replace addFeatureToTree:
  const addFeatureToTree = useCallback(
    (featureName: string, operationType: "create" | "modify" | "delete") => {
      // 1. Get Counters from Live Tree
      const currentCounters = recalculatePrimitiveCounters(
        featureTreeRef.current,
      );
      let workingCounters = { ...currentCounters };

      // Helper to generate a unique display name using counters + collision check
      const generateUniqueName = (
        baseName: string,
        currentTree: TreeItem[],
      ) => {
        // Extract primitive type (e.g. "Box" from "SubBox")
        const typeMatch = baseName.match(
          /^(?:Sub|sub)?(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)/i,
        );

        if (!typeMatch) return baseName; // Not a primitive, return as is

        const primitiveType = typeMatch[1];
        // Normalize key (e.g. "Box")
        const typeKey = (primitiveType.charAt(0).toUpperCase() +
          primitiveType.slice(1).toLowerCase()) as keyof typeof workingCounters;

        if (workingCounters[typeKey] === undefined) return baseName;

        // Start from counter + 1
        let nextNum = (workingCounters[typeKey] || 0) + 1;
        let candidateName = `${typeKey}${String(nextNum).padStart(3, "0")}`;

        // SAFETY NET: Check if this specific name ALREADY exists in the tree
        // This handles cases where counters might be slightly off
        const nameExists = (tree: TreeItem[], target: string): boolean => {
          return tree.some(
            (item) =>
              item.displayName === target ||
              item.name === target ||
              (item.children && nameExists(item.children, target)),
          );
        };

        // If "Box001" exists, keep incrementing until we find "Box002"
        while (nameExists(currentTree, candidateName)) {
          nextNum++;
          candidateName = `${typeKey}${String(nextNum).padStart(3, "0")}`;
        }

        // Update working counters to this new high water mark
        workingCounters[typeKey] = nextNum;

        return candidateName;
      };

      // NEW: Handle counterbore/multiple pocket operation
      if (featureName.includes("Pocket") && featureName.includes("+")) {
        const result = addSingleFeatureToTree(
          featureName,
          operationType,
          featureTreeRef.current,
          workingCounters, // Pass working counters
        );

        setFeatureTree(result.newTree);
        featureTreeRef.current = result.newTree;
        primitiveCountersRef.current = result.updatedCounters;

        if (activeModelId) {
          setOpenModels((prev) =>
            prev.map((model) =>
              model.id === activeModelId
                ? { ...model, primitiveCounters: result.updatedCounters }
                : model,
            ),
          );
        }

        const currentTimelineId = versionControl.getCurrentTimelineId();
        const timeline = (versionControl as any).state.timelines[
          currentTimelineId
        ];
        if (timeline) timeline.featureTree = result.newTree;
        const bodyIds = result.newTree.map((body) => body.id);
        setExpandedItems(bodyIds);
        return result.finalDisplayName;
      }

      const featureNames = featureName
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0);

      if (featureNames.length > 1 && operationType !== "delete") {
        let workingTree = [...featureTreeRef.current];
        const finalDisplayNames: string[] = [];

        featureNames.forEach((name) => {
          // Pre-calculate unique name to ensure safety (only on create)
          const uniqueDisplayName =
            operationType === "create"
              ? generateUniqueName(name, workingTree)
              : name;

          // Since generateUniqueName updated `workingCounters`, we pass that.
          const result = addSingleFeatureToTree(
            name,
            operationType,
            workingTree,
            workingCounters,
          );

          // Force display name override ONLY on create
          if (operationType === "create") {
            const lastItem = result.newTree[result.newTree.length - 1];
            // Check if it's nested (usual case for features)
            if (lastItem.children && lastItem.children.length > 0) {
              const addedChild =
                lastItem.children[lastItem.children.length - 1];
              // Only override if it looks like a primitive we tracked
              if (
                addedChild.displayName &&
                addedChild.displayName.match(
                  /^(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)\d+$/,
                )
              ) {
                addedChild.displayName = uniqueDisplayName;
              }
            } else {
              // Root item
              if (
                lastItem.displayName &&
                lastItem.displayName.match(
                  /^(Box|Cylinder|Sphere|Cone|Torus|Ellipsoid|Prism|Wedge)\d+$/,
                )
              ) {
                lastItem.displayName = uniqueDisplayName;
              }
            }
          }

          workingTree = result.newTree;
          finalDisplayNames.push(uniqueDisplayName);
        });

        setFeatureTree(workingTree);
        featureTreeRef.current = workingTree;
        primitiveCountersRef.current = workingCounters;

        if (activeModelId) {
          setOpenModels((prev) =>
            prev.map((model) =>
              model.id === activeModelId
                ? { ...model, primitiveCounters: workingCounters }
                : model,
            ),
          );
        }

        const currentTimelineId = versionControl.getCurrentTimelineId();
        const timeline = (versionControl as any).state.timelines[
          currentTimelineId
        ];
        if (timeline) timeline.featureTree = workingTree;
        const bodyIds = workingTree.map((body) => body.id);
        setExpandedItems(bodyIds);

        return finalDisplayNames.join(", ");
      }

      // Standard Delete Operation
      if (operationType === "delete") {
        setFeatureTree((prev) => {
          for (let i = 0; i < prev.length; i++) {
            const body = prev[i];
            if (body.name === featureName || body.displayName === featureName) {
              const newTree = [...prev];
              newTree.splice(i, 1);
              const currentTimelineId = versionControl.getCurrentTimelineId();
              const timeline = (versionControl as any).state.timelines[
                currentTimelineId
              ];
              if (timeline) timeline.featureTree = newTree;
              return newTree;
            }
            if (body.children) {
              const featureIndex = body.children.findIndex(
                (child) =>
                  child.name === featureName ||
                  child.displayName === featureName,
              );
              if (featureIndex !== -1) {
                const newTree = prev.map((item, idx) => {
                  if (idx === i) {
                    return {
                      ...item,
                      children: item.children
                        ? item.children.slice(0, featureIndex)
                        : [],
                    };
                  }
                  return item;
                });
                const currentTimelineId = versionControl.getCurrentTimelineId();
                const timeline = (versionControl as any).state.timelines[
                  currentTimelineId
                ];
                if (timeline) timeline.featureTree = newTree;
                return newTree;
              }
            }
          }
          return prev;
        });
        return featureName;
      }

      // Single Feature Add - Standard Path
      const uniqueDisplayName =
        operationType === "create"
          ? generateUniqueName(featureName, featureTreeRef.current)
          : featureName;

      const result = addSingleFeatureToTree(
        featureName,
        operationType,
        featureTreeRef.current,
        workingCounters,
      );

      // 2. Apply Safe Name (Modified to only run on create)
      if (operationType === "create") {
        // Find the item we just added and ensure its name is uniqueDisplayName
        const lastItem = result.newTree[result.newTree.length - 1];
        if (lastItem.children && lastItem.children.length > 0) {
          // Usually features are children of Body
          const addedChild = lastItem.children[lastItem.children.length - 1];
          // Verify it matches the type we are creating
          if (addedChild.name === featureName) {
            addedChild.displayName = uniqueDisplayName;
          }
        } else {
          // Root item (like the first Box)
          if (lastItem.name === featureName) {
            lastItem.displayName = uniqueDisplayName;
          }
        }
      }

      const currentTimelineId = versionControl.getCurrentTimelineId();
      const timeline = (versionControl as any).state.timelines[
        currentTimelineId
      ];
      if (timeline) timeline.featureTree = result.newTree;

      setFeatureTree(result.newTree);
      featureTreeRef.current = result.newTree;
      primitiveCountersRef.current = result.updatedCounters;

      if (activeModelId) {
        setOpenModels((prev) =>
          prev.map((model) =>
            model.id === activeModelId
              ? { ...model, primitiveCounters: result.updatedCounters }
              : model,
          ),
        );
      }

      return uniqueDisplayName;
    },
    [
      featureTree,
      versionControl,
      getDisplayNameForFeature,
      openModels,
      activeModelId,
      recalculatePrimitiveCounters,
    ],
  );
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add this in CAD.tsx, somewhere before the return statement (around line 2000-2100)
  // REPLACE the existing handleSuccessfulOperation function (around line 2064)
  const handleSuccessfulOperation = useCallback(async () => {
    // console.log("Operation successful - save only on exit");
    // Don't auto-save, only save on unmount/exit
  }, []);

  // ADD THIS NEW CALLBACK (right after handleSuccessfulOperation)
  const handleCommandStart = useCallback(() => {
    setPendingCommandCount((prev) => prev + 1);

    // ← ADD THIS: Clear waiting state when first command starts
    setIsWaitingForFirstPrompt(false);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, []);

  const isHandlingCommandEnd = useRef(false);

  // ADD THIS NEW CALLBACK (right after handleCommandStart)
  const handleCommandEnd = useCallback(() => {
    setPendingCommandCount((prev) => {
      const newCount = Math.max(0, prev - 1);
      // console.log(`Command ended - pending count: ${prev} -> ${newCount}`);
      return newCount;
    });
  }, []);

  const handleFeatureCreated = useCallback(
    (
      featureName: string,
      operationType: "create" | "modify" | "delete",
      messageId?: number,
      commandEntry?: CommandHistoryEntry,
    ): string => {
      // Check if feature already exists in tree
      const featureExists = (
        items: TreeItem[],
        targetName: string,
      ): boolean => {
        for (const item of items) {
          if (item.name === targetName || item.displayName === targetName) {
            return true;
          }
          if (item.children) {
            if (featureExists(item.children, targetName)) {
              return true;
            }
          }
        }
        return false;
      };

      const alreadyExists = featureExists(featureTree, featureName);

      // Only mark as pending if it's a NEW feature
      if (!alreadyExists) {
        setPendingFeatureName(featureName);
      }

      const result = addFeatureToTree(featureName, operationType);

      if (messageId) {
        saveOperationSnapshot(messageId, featureName, commandEntry);
      }

      return result;
    },
    [addFeatureToTree, saveOperationSnapshot, featureTree],
  );

  const handleTreeItemSelect = useCallback((itemId: string) => {
    // console.log("Tree item selected:", itemId);
  }, []);

  const handleTransparencyChange = useCallback((value: number) => {
    setTransparencyValue(value);
  }, []);

  const handleColorChange = useCallback((value: string) => {
    setColorValue(value);

    const isDefault =
      value === DEFAULT_COLORS.dark || value === DEFAULT_COLORS.light;
    isDefaultColorRef.current = isDefault;
  }, []);

  const handleDisplayUnitChange = useCallback(
    (unit: string) => {
      setDisplayUnit(unit);
      sessionStorage.setItem(`project_${projectId}_displayUnit`, unit);
    },
    [projectId],
  );

  const handleExport = useCallback(
    async (format: string) => {
      if (!noahGeometry || noahGeometry.length === 0) {
        // alert("No geometry to export");
        return;
      }

      try {
        if (format === ".STL") {
          const geometryData = noahGeometry[0];
          if (!geometryData.stl_data) {
            // alert("No STL data available");
            return;
          }

          const binaryString = atob(geometryData.stl_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName || "model"}.stl`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return;
        }

        let exportCommand = "";
        let dataKey = "";
        let fileExtension = "";

        if (format === ".STEP") {
          exportCommand = "EXPORT_STEP_FILE";
          dataKey = "step_data";
          fileExtension = ".step";
        }

        const response = await fetch(`${BACKEND_URL}/api/command`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_input: exportCommand,
            context: {},
            branch_id: currentBranch || "main",
          }),
          signal: AbortSignal.timeout(300000),
        });

        const result = await response.json();

        if (result.success && result[dataKey]) {
          const binaryString = atob(result[dataKey]);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName}${fileExtension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        // console.error("Export failed:", error);
      }
    },
    [noahGeometry, fileName, currentBranch],
  );

  const runDiagnostics = async () => {
    const results = {
      timestamp: new Date().toISOString(),
      projectId,
      branchId: currentBranch || consistentBranchId,
      fileSize: noahGeometry.length,
      featureCount: featureTree.length,
      tests: {} as any,
    };

    try {
      // Test 1: Backend health
      // console.log("TEST 1: Backend health check...");
      const healthStart = Date.now();
      const healthResponse = await fetch(`${BACKEND_URL}/health`, {
        signal: AbortSignal.timeout(64000),
      });
      results.tests.health = {
        status: healthResponse.ok ? "PASS" : "FAIL",
        statusCode: healthResponse.status,
        responseTime: Date.now() - healthStart,
        data: healthResponse.ok ? await healthResponse.json() : null,
      };

      // Test 2: Document state
      // console.log("TEST 2: Checking document state...");
      const stateStart = Date.now();
      const stateResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "DEBUG_DOCUMENT_STATE",
          context: {},
          branch_id: currentBranch || consistentBranchId,
        }),
        signal: AbortSignal.timeout(15000),
      });
      const stateData = stateResponse.ok ? await stateResponse.json() : null;
      results.tests.documentState = {
        status: stateResponse.ok ? "PASS" : "FAIL",
        statusCode: stateResponse.status,
        responseTime: Date.now() - stateStart,
        objectCount: stateData?.debug_data?.total_objects || 0,
        data: stateData,
      };

      // Test 3: Geometry refresh
      // console.log("TEST 3: Testing geometry refresh...");
      const geoStart = Date.now();
      const geoResponse = await fetch(`${BACKEND_URL}/api/command`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_input: "refresh_geometry",
          context: {},
          branch_id: currentBranch || consistentBranchId,
        }),
        signal: AbortSignal.timeout(30000),
      });
      const geoData = geoResponse.ok ? await geoResponse.json() : null;
      results.tests.geometryRefresh = {
        status: geoResponse.ok ? "PASS" : "FAIL",
        statusCode: geoResponse.status,
        responseTime: Date.now() - geoStart,
        geometryCount: geoData?.geometry?.length || 0,
        data: geoData,
      };
    } catch (error) {
      results.tests.criticalError = {
        status: "FAIL",
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      };
    }

    // console.log("DIAGNOSTIC RESULTS:", results);
    setDebugInfo(results);
    return results;
  };

  // Track color changes and save on TRUE unmount only
  useEffect(() => {
    // Track when color/transparency changes
    (window as any).__lastColorChangeTime = Date.now();

    // Cleanup function only runs on TRUE unmount (when component is destroyed)
    return () => {
      const timeSinceLastChange =
        Date.now() - ((window as any).__lastColorChangeTime || 0);

      // Only save if color changed recently and we're truly unmounting
      if (timeSinceLastChange < 10000 && projectId && user && !isLoading) {
        // console.log("🎨 Saving color on TRUE unmount (component destroyed)");

        // Quick metadata-only save (no thumbnail on unmount - too slow)
        updateDoc(doc(db, "projects", projectId), {
          modelColor: colorValue,
          transparencyValue: transparencyValue,
          updatedAt: new Date().toISOString(),
        }).catch((err) =>
          console.error("Failed to save color on unmount:", err),
        );
      }
    };
  }, []); // ← CRITICAL: Empty array = only runs on TRUE mount/unmount

  // console.log("🔍 CAD Debug:", {
  //   isMobile,
  //   forceShowProjectSidebar,
  //   isLeftSidebarOpen,
  //   computedIsMobileOpen: isMobile && forceShowProjectSidebar,
  // });

  const handleProjectCreated = useCallback(
    (newProjectId: string) => {
      console.log(`🆕 CAD: New project created: ${newProjectId}`);

      // Update local state
      setHasProject(true);
      setActiveModelId(newProjectId);

      // Get names from storage immediately
      const projectName =
        sessionStorage.getItem(`project_${newProjectId}_name`) || "Untitled";
      const branchId =
        sessionStorage.getItem(`project_${newProjectId}_branch`) || "main";

      // ✅ CRITICAL FIX: Explicitly set the current branch in CAD state
      // This ensures the NEXT command uses this branch, not 'main'
      setCurrentBranch(branchId);

      // Update TopToolbar UI state
      setOptimisticProject({
        id: newProjectId,
        name: projectName,
      });

      // Create the tab
      setOpenModels((prev) => [
        ...prev,
        {
          id: newProjectId,
          name: projectName,
          geometry: [],
          featureTree: [],
          chatMessages: [],
          commandHistory: [],
          versionControl: null,
          branchId: branchId,
          isSharedModel: false,
          cameraState: {
            rotation: { x: -30, y: -45 },
            distance: 50,
            isOrthographic: false,
          },
          primitiveCounters: {
            Box: 0,
            Cylinder: 0,
            Sphere: 0,
            Cone: 0,
            Torus: 0,
            Ellipsoid: 0,
            Prism: 0,
            Wedge: 0,
          },
        },
      ]);

      // Notify parent
      if (onProjectCreated) {
        onProjectCreated(newProjectId);
      }
    },
    [onProjectCreated],
  );

  useEffect(() => {
    // Expose models sidebar toggle to View component
    (window as any).__toggleModelsSidebar = () => {
      setIsModelsSidebarOpen((prev) => !prev);
      setUserHasTouchedSidebars(true);
    };

    return () => {
      delete (window as any).__toggleModelsSidebar;
    };
  }, []);

  // ✅ ADD THIS NEW USEEFFECT RIGHT HERE (AFTER the toggleModelsSidebar one)
  useEffect(() => {
    // Expose manual save trigger for first tab
    (window as any).__triggerManualSave = async () => {
      if (activeModelId && noahGeometry.length > 0) {
        // console.log(`💾 Manual save triggered for project ${activeModelId}`);
        await saveProjectAsNoahFile(noahGeometry, currentBranch);
      }
    };

    return () => {
      delete (window as any).__triggerManualSave;
    };
  }, [activeModelId, noahGeometry, currentBranch, saveProjectAsNoahFile]);

  // ✅ ADD THIS NEW USEEFFECT RIGHT HERE
  useEffect(() => {
    (window as any).__clearAllButtonLoading = () => {
      setIsAnyButtonLoading(false);
    };

    return () => {
      delete (window as any).__clearAllButtonLoading;
    };
  }, []);

  // Add this useEffect near the end of CAD.tsx (around line 3800):
  useEffect(() => {
    // Expose sidebar refresh to child components
    (window as any).__refreshSidebarProjects = refreshSidebarProjects;

    return () => {
      delete (window as any).__refreshSidebarProjects;
    };
  }, [refreshSidebarProjects]);

  return (
    <div className={styles.container}>
      {/* Vertical Toolbar - NOW LEFTMOST */}
      <TopToolbar
        onModelsClick={handleModelsClick}
        onFeatureTreeClick={handleFeatureTreeClick}
        onChatClick={handleNewModelButtonClick} // ← CHANGED
        onShowReferralModal={() => setShowReferralModal(true)}
        onShowFeedbackModal={() => {
          setShowPricingPage(false);
          setShowFeedbackPage(true);
          setShowSettingsPage(false); // next to setShowPricingPage(false) and setShowFeedbackPage(false)
        }}
        onShowCreditsModal={() => {
          setShowFeedbackPage(false);
          setShowPricingPage(true);
          setShowSettingsPage(false); // next to setShowPricingPage(false) and setShowFeedbackPage(false)
        }}
        onNavigateToSettings={() => {
          setShowPricingPage(false);
          setShowFeedbackPage(false);
          setShowSettingsPage(true);
        }}
        optimisticProject={optimisticProject}
        projectToHunt={projectToHunt}
        isLeftSidebarOpen={isLeftSidebarOpen}
        isRightSidebarOpen={isRightSidebarOpen}
        isModelsSidebarOpen={isModelsSidebarOpen}
        activeProjectId={activeModelId}
        activeProjectName={fileName}
        isProjectPublic={projectIsPublic}
        projectShareToken={projectShareToken}
        refreshTrigger={projectsRefreshTrigger} // 👈 PASS THE PROP HERE
        onExport={handleExport}
        projects={sidebarProjects}
        onShareStatusChange={async () => {
          if (activeModelId) {
            try {
              const projectDoc = await getDoc(
                doc(db, "projects", activeModelId),
              );
              if (projectDoc.exists()) {
                const data = projectDoc.data();
                setProjectIsPublic(data.isPublic || false);
                setProjectShareToken(data.shareToken);
              }
            } catch (error) {}
          }
        }}
        isActiveTabShared={
          openModels.find((m) => m.id === activeModelId)?.isSharedModel === true
        }
        isToolbarExpanded={isToolbarExpanded}
        onToolbarExpandChange={(expanded) => {
          console.log("🔧 TopToolbar called onToolbarExpandChange:", expanded);
          setIsToolbarExpanded(expanded);
        }}
        onProjectSelect={handleSidebarProjectSelect}
        onExampleSelect={handleExampleSelect} // ✅ ADD THIS PROP
        onProjectRename={handleTabRename}
        onProjectDelete={handleSidebarProjectDelete}
        loadingProjectId={loadingProjectId}
      />
      <div
        className={`${styles.mainContent} ${
          openModels.length === 0 ? styles.noFilesOpen : ""
        } ${isToolbarExpanded ? styles.toolbarExpanded : ""}`}
      >
        {showPricingPage && (
          <PricingPage onClose={() => setShowPricingPage(false)} />
        )}
        {showFeedbackPage && (
          <FeedbackPage onClose={() => setShowFeedbackPage(false)} />
        )}
        {showSettingsPage && (
          <SettingsPage onClose={() => setShowSettingsPage(false)} />
        )}
        {!showPricingPage && !showFeedbackPage && !showSettingsPage && (
          <>
            <div
              className={`${styles.rightSidebarContainer} ${
                isRightSidebarOpen ? styles.open : ""
              }`}
              style={{ display: isRightSidebarOpen ? "flex" : "none" }}
            >
              <RightSidebar
                ref={rightSidebarRef}
                onDockToggle={toggleRightSidebar}
                currentSelection={currentSelection}
                projectId={activeModelId}
                activeModelId={activeModelId}
                openModels={openModels}
                onTabRename={handleTabRename}
                onProjectCreated={handleProjectCreated}
                onSelectionClear={handleSelectionClear}
                onShapeCreated={handleShapeCreated}
                onSuccessfulOperation={handleSuccessfulOperation}
                onCommandStart={handleCommandStart}
                onCommandEnd={handleCommandEnd}
                onFeatureCreated={handleFeatureCreated}
                onProcessUserInput={processUserInput}
                onProcessAIResponse={processAIResponse}
                onRestoreToOperation={createProjectFromRestore}
                onNavigateOperationVersion={navigateOperationVersion}
                getOperationVersionInfo={getOperationVersionInfo}
                isInVersionMode={isInVersionMode}
                getVersionedOperations={getVersionedOperations}
                onChatMessagesChange={handleChatMessagesChange}
                currentBranch={currentBranch}
                onBranchChange={setCurrentBranch}
                onShowCreditsModal={() => setShowCreditsModal(true)}
                isModelLoading={isViewLoading || isSavingAfterCommand}
                isSharedView={isSharedView}
                isActiveTabShared={
                  openModels.find((m) => m.id === activeModelId)
                    ?.isSharedModel === true
                }
                noahGeometry={noahGeometry}
                isPendingSave={isPendingSave}
                featureTree={featureTree}
                onLoadingChange={setIsRightSidebarLoading}
                currentMeasurement={currentMeasurement}
                onButtonLoadingChange={setIsAnyButtonLoading}
                selectedUnit={displayUnit}
                onUnitChange={handleDisplayUnitChange}
                onInitialCreationError={handleInitialCreationError}
              />
            </div>

            {/* Models Sidebar - FURTHEST LEFT */}
            {isModelsSidebarOpen && (
              <div
                className={`${styles.modelsSidebarContainer} ${
                  isModelsSidebarOpen ? styles.open : ""
                }`}
                style={{ display: isModelsSidebarOpen ? "flex" : "none" }}
              >
                <ModelsSidebar
                  projects={sidebarProjects}
                  onProjectSelect={handleSidebarProjectSelect}
                  onExampleSelect={handleExampleSelect}
                  onProjectDelete={handleSidebarProjectDelete}
                  onProjectRename={handleSidebarProjectRename}
                  onClose={() => setIsModelsSidebarOpen(false)}
                  isMobile={isMobile}
                  currentProjectId={activeModelId}
                  loadingProjectId={loadingProjectId}
                  openTabIds={openModels.map((m) => m.id)}
                />
              </div>
            )}

            {/* Left Sidebar */}
            <div
              className={`${styles.leftSidebarContainer} ${
                isLeftSidebarOpen ? styles.open : ""
              }`}
              style={{ display: isLeftSidebarOpen ? "flex" : "none" }}
            >
              <LeftSidebar
                treeData={featureTree}
                onTreeItemSelect={handleTreeItemSelect}
                expandedItems={expandedItems}
                onToggleExpanded={handleToggleExpanded}
                fileName={fileName}
                onFileNameChange={handleFileNameChange}
                onItemRename={handleItemRename}
                getDisplayName={getDisplayName}
                onFileDelete={handleFileDelete}
                projectId={projectId || undefined}
                isSharedView={isSharedView}
                onShareStatusChange={() =>
                  setShareStatusChanged((prev) => prev + 1)
                }
                pendingFeatureName={pendingFeatureName}
                onDockToggle={toggleLeftSidebar}
                onMobileClose={() => {
                  setIsLeftSidebarOpen(false);
                  setIsRightSidebarOpen(false);
                }}
                currentMeasurement={currentMeasurement}
                colorValue={colorValue}
                onColorChange={handleColorChange}
                displayUnit={displayUnit}
                onDisplayUnitChange={handleDisplayUnitChange}
                isPublic={projectIsPublic ?? false}
                shareToken={projectShareToken}
                onExport={handleExport}
                selectedExportFormat={selectedExportFormat}
                onExportFormatChange={setSelectedExportFormat}
              />
            </div>

            {/* View - only shown when files are open */}
            {openModels.length > 0 && (
              <View
                isLeftSidebarOpen={isLeftSidebarOpen}
                isRightSidebarOpen={isRightSidebarOpen}
                modelTabs={openModels.map((m) => ({
                  id: m.id,
                  name: m.name,
                  isSharedModel: m.isSharedModel,
                }))}
                activeTabId={activeModelId}
                onTabClick={handleTabClick}
                onTabClose={handleTabClose}
                onTabRename={handleTabRename}
                onCloseAllTabs={handleCloseAllTabs}
                onNewModel={handleNewModel}
                onToggleLeftSidebar={handleFeatureTreeClick}
                onToggleRightSidebar={handleChatClick}
                onToggleModelsSidebar={handleModelsClick}
                noahGeometry={noahGeometry}
                onShapeSelect={handleShapeSelect}
                currentSelection={currentSelection}
                fileName={fileName}
                onTransparencyChange={handleTransparencyChange}
                onColorChange={handleColorChange}
                initialTransparency={transparencyValue}
                initialColor={colorValue}
                isLoading={isViewLoading}
                currentBranch={currentBranch}
                isSharedView={isSharedView}
                projectId={projectId || undefined}
                isPublic={projectIsPublic ?? false}
                shareToken={projectShareToken}
                onMeasurementChange={setCurrentMeasurement}
                isCreatingNewModel={isCreatingNewModel}
                isWaitingForFirstPrompt={isWaitingForFirstPrompt}
                onShareStatusChange={async () => {
                  if (projectId) {
                    try {
                      const projectDoc = await getDoc(
                        doc(db, "projects", projectId),
                      );
                      if (projectDoc.exists()) {
                        const data = projectDoc.data();
                        setProjectIsPublic(data.isPublic || false);
                        setProjectShareToken(data.shareToken);
                      }
                    } catch (error) {}
                  }
                }}
                isCommandExecuting={isRightSidebarLoading}
                isImporting={isLoading && noahGeometry.length === 0}
                isNewModel={
                  !isLoading && !fileName && noahGeometry.length === 0
                }
                isAnyButtonLoading={isAnyButtonLoading}
              />
            )}
          </>
        )}
      </div>
      {/* Mobile overlay - only shown when sidebars are open */}
      {window.innerWidth < 1280 &&
        (isLeftSidebarOpen || isRightSidebarOpen || isModelsSidebarOpen) && (
          <div
            className={styles.mobileOverlay}
            onClick={() => {
              setIsLeftSidebarOpen(false);
              setIsRightSidebarOpen(false);
              setIsModelsSidebarOpen(false);
            }}
          />
        )}
      {/* Modals */}
      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
    </div>
  );
};

export default CAD;
